import { addDays } from "../utils/addDays";
import { AddFieldDef } from "../utils/AddFieldDef";
import { appstr } from "../utils/appstr";
import { appstrp } from "../utils/appstrp";
import { comparefields } from "../utils/comparefields";
import { headerinfo } from "./headerinfo.js";
import {
  enumBoxStyleSearch,
  enumBoxStyleSet,
  enumCalcCode,
  enumCellObjectSubtypes,
  enumChartTemplateSet,
  enumChartTypes,
  enumContainerType,
  enumDataSource,
  enumDataSourceFlag,
  enumDocCreateStep,
  enumFieldType,
  enumFillMode,
  enumFilterModes,
  enumHierarchyTypes,
  enumHorzCentering,
  enumHyperLinkType,
  enumIWizDataSet,
  enumMatchSubs,
  enumOverrideType,
  enumSubChartFlag,
  enumVertCentering,
  kAdminUser,
  kDocVersion,
  kGroupingRule,
  kIndexSCID,
  kMasterSCID,
  kReservedFieldNames,
  kRWUser,
  kBoxOverlayRule,
  enumObjClassNames,
  kPositionFieldIDs,
  enumObjectTypes,
  SYSTEM_CALCULATE,
  connectorsBuiltDynamically,
  kPositionCostBandMinimum,
  kPositionCostBandMidpoint,
  kPositionCostBandMaximum,
  kPositionID,
  kParentPositionID,
  kPersonID,
  kSupervisorID,
  kDesiredHolders,
  kStaffingTarget,
  kPositionBudgetedSalary,
  kPositionCostCenter,
  kEmployeeSalary,
  kEmployeePay,
  kEmployeeCostCenter,
  kLocationID,
  SYSTEM_GENERATED_CAPABLE_FIELDS,
  kEmployeePositionID,
  boxObjClassFieldIds,
  kPositionHoldersCount,
  kPositionAllocation,
  kBudgetedHeadcount,
  kSalaryRollup,
  kCurrentPositionCost,
  kPositionBudgetRollup,
  kHeadcount,
  kAssignmentStatus,
  AssignmentStatus,
  COST_BAND_FIELDS,
  kReservedMetricsFields,
  kHolderFieldIDs,
  kPositionLocation,
  kPositionDivisionID,
  kAllocation,
  kEmployeeLocation,
  kPositionCount,
  kSubordinatePositions,
  kDirectSubordinatePositions,
  kPeerPositions,
  kAgentMarkers,
  kPhotoID
} from "../constants";
import { kMaxRecSinglePage } from "../whitelabel/brand";
import { FindTypeInfoObj } from "../utils/FindTypeInfoObj";
import { GetFieldId } from "../utils/GetFieldId";
import { isDateFieldName } from "../utils/getFieldType";
import { GetNameDefForClass } from "../utils/GetNameDefForClass";
import { GetPicSource } from "../utils/GetPicSource";
import { IsDBChart } from "../utils/IsDBChart";
import { IsFieldNameReserved } from "../utils/IsFieldNameReserved";
import { isInReporting } from "../utils/isInReporting";
import { isInViewer } from "../utils/isInViewer";
import { LoadInteractiveObjectFromGroup } from "../utils/LoadInteractiveObjectFromGroup";
import { MatchToGroup } from "../utils/MatchToGroup";
import { PositionLocked } from "../utils/PositionLocked";
import { QAlert } from "./QAlert";
import { QAutoTextMgr } from "./QAutoTextMgr";
import { QChartTemplate } from "./QChartTemplate";
import { QChartTemplateRule } from "./QChartTemplateRule";
import { QCheckPoint } from "../utils/QCheckPoint";
import { QDBTypeInfo } from "./QDBTypeInfo";
import { QDoc } from "./QDoc";
import { QFilter } from "./QFilter";
import { QFormulaRule } from "./QFormulaRule";
import { QHighlight } from "./QHighlight";
import { QIndex } from "./QIndex";
import { QLayoutMgr } from "./QLayoutMgr";
import { QObjDB } from "./QObjDB";
import { QObjDBClass } from "./QObjDBClass";
import { QObjDBFieldDef } from "./QObjDBFieldDef";
import { QOverrideMgr } from "./QOverrideMgr";
import { QPageLayoutMgr } from "./QPageLayoutMgr";
import { QPictureMgr } from "./QPictureMgr";
import { QPlaceHolder } from "./QPlaceHolder";
import { QShortcut } from "./QShortcut";
import { QShortcutMgr } from "./QShortcutMgr";
import { QSubChartMgr } from "./QSubChartMgr";
import { QSvrChartQuery } from "./QSvrChartQuery";
import { QSvrPersonQuery } from "./QSvrPersonQuery";
import { QXMLLoader } from "./QXMLLoader";
import { QXMLObject } from "./QXMLObject";
import { ReportTiming } from "../utils/ReportTiming";
import { toColumnName } from "../utils/toColumnName";
import { ValidateSortConfig } from "../utils/ValidateSortConfig";
import { Globals } from "./Globals";
import { ApplyChartTemplateToBoxes } from "../utils/ApplyChartTemplateToBoxes";
import { DoOptimizePages } from "../utils/DoOptimizePages.js";
import { appcstr } from "../utils/appcstr.js";
import { ReportToWebDriver } from "../utils/ReportToWebDriver.js";
import { isInStandardSubmode } from "../utils/isInStandardSubmode.js";
import { QFieldDefPair } from "./QFieldDefPair.js";
import { isInDirectory } from "../utils/isInDirectory.js";
import { FindMatchingParent } from "../utils/FindMatchingParent.js";
import { ContentLocked } from "../utils/ContentLocked.js";
import { parseRGB } from "../utils/parseRGB";
import { ConvertRGBToHex } from "../utils/ConvertRGBToHex";
import { SynthesizePositions } from "../utils/SynthesizePositions";
import { QSCNode } from "./QSCNode";

// Publish (truthy _publishRef without isPreview) skips DoOptimizePages —
// Optimizer.NodePoly.Clone() crashes on mid-render entities; per-page optimize
// runs via DoOptimizeHelper in SwitchToSubChart instead.
export function qShouldOptimizeAfterGrouping(publishRef) {
  if (!publishRef) return true;
  return publishRef.isPreview === true;
}

/**
 * @typedef {import('./QCanvasPanel').QCanvasPanel} QCanvasPanel
 * @typedef {import('./QObjDBObject').QObjDBObject} QObjDBObject
 * @typedef {import('./QXDocMgr').QXDocMgr} QXDocMgr
 */

export class QXDoc extends QDoc {
  constructor() {
    super();
    /**
     * @private
     * @type {QObjDB | null}
     */
    this.fObjDB = null;
    this.fDocVersion = null;
    this.fDivisionName = null;
    this.fMaxRecs = null;
    this.fAutoTextMgr = null;

    // Temporary copy used until commit
    this.fIWizDataWorkingRefresh = null;
    this.fIWizDataWorkingInteractive = null;

    this.fPictureMgr = null;
    this.fSaveScope = null;
    /**
     * @type {(null | QXDocMgr)}
     */
    this.fDocMgr = null;
    this.fPredefFieldCount = null;

    this.fSubChartMgr = null;
    this.fLayoutMgr = null;

    /**
     * @type {(null | QCanvasPanel)}
     */
    this.fCanvasRef = null;

    this.fPageLayoutMgr = null;
    this.fAccountName = null;
    this.fCurUser = null;

    this.fRowTag = "r";
    this.fValueTag = "d";
    this.fDataSourceFlag = enumDataSourceFlag.dsclear;

    this.fCompoundFilterDefID = null;
    this.fAltFilterDefID = null;
    this.fComplexSubFilterDef = null;

    this.fClearPicCache = false;
    // this.fDeferLoadPics = false;
    this.fCircCounter = null;
    this.fDownloadingExcel = false;

    this.fShortcutMgr = null;

    this.fActiveChartTemplate = null;
    this.fAuthor = "none";
    this.fHeadersList = null;
    this.fAutoFormat = null;

    this.fTreeID = null;

    this.fHierarchyType = enumHierarchyTypes.employees;
    this.fIsPreview = false;
  }

  AddNodesToMRB(layout, nodeList) {
    var containerObjID = layout.GetDBObjID();
    for (var i in nodeList) {
      var theNode = nodeList[i];
      if (layout.fAssociatedObjIDs.indexOf(theNode.GetDataObjID()) == -1)
        layout.fAssociatedObjIDs.push(theNode.GetDataObjID());
      theNode.GetLayout().SetContainerObjID(containerObjID);
      //This line is a protective measure if something upstream was missed.
      theNode.GetLayout().SetSubChartID(layout.GetSubChartID());
      layout.AddAssociatedChildID(theNode.GetDataObjID());
    }
  }

  // dbObj being passed in here will be assigned to container node
  // for position charts, dbObj is the position, groupList is a list of holders for that position
  GroupNodes(groupList, ungroupOnly, layout, scID) {
    var canvasPanel = this.fCanvasRef;

    //Take in a list of SC Nodes. Create a new dummy MRB layout and assign the nodes to it. Removes their previous container (if any)
    //Sister function to groupPeers, this time for SC nodes (off page)
    var containerObjID = null;
    var scm = this.GetSubChartMgr();
    if (!ungroupOnly) {
      if (!layout) layout = canvasPanel.GetMRBLayout(null);
      var subChartID = groupList[0]?.GetSubChartID() ?? scID;
      if (subChartID != null) layout.SetSubChartID(subChartID);
      if (!layout.GetDBObjID()) layout.SetDBObjID(canvasPanel.GetNextObjID()); // hmm...
      containerObjID = layout.GetDBObjID();
    }
    for (var aNode in groupList) {
      var theNode = groupList[aNode];
      this.DoRemoveFromMRBNode(theNode, containerObjID);
    }
    if (!ungroupOnly) {
      this.AddNodesToMRB(layout, groupList);
      var containerScNode = scm.FindSCNodeByID(containerObjID, subChartID);
      if (containerScNode != null) {
        layout.SetSCNodeRef(containerScNode);
        containerScNode.fLayout = layout;
      } else {
        containerScNode = scm.AddSCNode(layout, null, containerObjID);
      }
      return containerScNode;
    }
  }

  DoRemoveFromMRBNode(theNode, keepObjID) {
    var scm = this.GetSubChartMgr();
    var nodeLayout = theNode.GetLayout();
    var nodeSCID = theNode.GetSubChartID();
    var dataObjID = theNode.GetDataObjID();
    var curContainerID = nodeLayout.GetContainerObjID();

    // Position MRBs with no holders will have a Vacant box added on QCanvasPanel.LoadSubChart.
    const postponeNodeRemovalOn0IDs = this.IsCustomChart() && this.IsPositionDocument();

    if (curContainerID && curContainerID != keepObjID) {
      var curContainer = scm.FindSCNodeByID(curContainerID, nodeSCID);
      if (!curContainer) {
        console.log("warning - no matching container from id!");
        return;
      }
      curContainer.GetLayout().UnassociateNode(dataObjID);
      curContainer.RemoveAssociateChildrenIDs(theNode);
      if (curContainer.GetLayout().GetAssociatedObjIDs().length == 0 && !postponeNodeRemovalOn0IDs)
        scm.RemoveSCNode(curContainer.GetDataObjID(), curContainer.GetLayout().GetSubChartID());

      nodeLayout.SetContainerObjID(null);
      if (
        theNode.GetSubChartFlag() == enumSubChartFlag.drilldown &&
        theNode.GetDataObj().GetNumChildren() > 0
      ) {
        //Explicitly only handling 'bottom' nodes with this.
        var SCNodes = scm.FindSCSubNodesFromDataObj(theNode.GetDataObj());
        var topSCNode = SCNodes.filter(function (a) {
          return a.GetSubChartID() != nodeSCID;
        })[0];
        scm.ReformContainerSubchart(topSCNode, nodeSCID);
      }
    }
    nodeLayout.SetContainerObjID(null);
  }

  CombineRecords(rootObj, combineDef, nameDef, mrbDef) {
    var n = rootObj.GetNumChildren();

    if (n == 0) return;

    var groupList = [];
    var cList = rootObj.GetChildList(false);
    cList = cList.slice(0);

    for (var i = 0; i < n; i++) {
      var child = cList[i];
      var n2 = child.GetNumChildren();
      if (n2 == 0) MatchToGroup(groupList, child, combineDef);
      else this.CombineRecords(child, combineDef, nameDef, mrbDef);
    }
  }

  GetSiblingBoxes(sList) {
    var siblingArrays = {};
    for (var aBoxIdx in sList) {
      var theBox = sList[aBoxIdx];
      var theParentID = "_-1_root";
      if (!theBox.GetDataObj()) continue;
      if (theBox.GetDataObj().GetParent()) {
        var theParent = theBox.GetDataObj().GetParent();
        theParentID = theParent.GetObjectID();
      }

      theParentID = theParentID.toString();
      if (siblingArrays[theParentID]) siblingArrays[theParentID].push(theBox);
      else siblingArrays[theParentID] = [theBox];
    }
    return siblingArrays;
  }

  GetSiblingNodes(scList) {
    var scm = this.GetSubChartMgr();
    var siblingArrays = {};
    for (var aNodeIdx in scList) {
      var theNode = scList[aNodeIdx];
      if (!theNode.GetDataObj() || !theNode.GetDataObj().GetParent()) continue;
      var theParentID = theNode.GetDataObj().GetParent().GetObjectID();
      theParentID = theParentID.toString();
      var curContainerID = theNode.GetLayout().GetContainerObjID();
      if (curContainerID)
        var curContainer = scm.FindSCNodeByID(curContainerID, theNode.GetSubChartID());
      if (curContainer && curContainer.GetSubChartFlag() == enumSubChartFlag.drillup) continue;
      if (theNode.GetSubChartFlag() == enumSubChartFlag.drillup) continue;
      if (siblingArrays[theParentID]) siblingArrays[theParentID].push(theNode);
      else siblingArrays[theParentID] = [theNode];
    }
    return siblingArrays;
  }

  //The canvas panel version of this method makes some assumptions with leaning on the page layout manager to grab the relevant
  //data objects and layouts. In the automated planing context, we no longer have that assumption. This slightly reworked version
  //does not care if the boxes are on/off page- the current subchart gets reloaded after the changes are applied.
  MoveBranch(dataObj, newParentDataObj, cache = null) {
    if (dataObj.GetParent() === newParentDataObj) return;

    //cache lookups/getters to avoid recursive overhead
    if (!cache) {
      cache = {
        objDB: this.GetObjDB(),
        scm: this.GetSubChartMgr(),
        isPublish: !this.IsServerChart() && Globals._publishRef,
        nodeCache: new Map(), //cache nodes for faster lookup
        processedNodes: new Set() //avoid operating on the same node twice
      };
    }

    const objDB = cache.objDB;
    const scm = cache.scm;
    const isPublish = cache.isPublish;
    const nodeCache = cache.nodeCache;
    const processedNodes = cache.processedNodes;

    const dataObjID = dataObj.GetObjectID();
    if (processedNodes.has(dataObjID)) return;
    processedNodes.add(dataObjID);

    if (objDB.CheckforCircularReference(dataObj, newParentDataObj)) return;

    dataObj.SetParent(newParentDataObj, -1);

    if (this.IsPlanChart()) return;

    //cache by targetNode
    const cacheKey = `parent_${newParentDataObj.GetObjectID()}`;
    let targetSCNode, targetIsNoBreak;

    // ---- Find the Target Node. We want nodes that are the topbox, and if not, the node we dragged the box to itself.
    if (nodeCache.has(cacheKey)) {
      targetSCNode = nodeCache.get(cacheKey);
    } else {
      const scSubNodes = scm
        .FindSCSubNodesFromDataObj(newParentDataObj)
        .filter(node => node.GetSubChartFlag() !== enumSubChartFlag.drilldown);

      targetSCNode = scSubNodes[0];
      targetIsNoBreak = targetSCNode
        ? targetSCNode.GetSubChartFlag() == enumSubChartFlag.nobreak
        : false;
      nodeCache.set(cacheKey, targetSCNode);
    }

    if (!targetSCNode && !isPublish) {
      this.DeleteBranch(dataObj, dataObj.GetParent());
      return;
    }

    const targetSCID = targetSCNode ? targetSCNode.GetSubChartID() : null;

    const dropSubChart = scm.FindSubChart(targetSCID); // the subchart where our dragged box will end.
    if (!dropSubChart) return;

    const sourceCacheKey = `source_${dataObjID}`;
    let myTopSCNode, originIsNoBreak;

    // ---- Find the subcharts below the dragged box. If there're none, we'll use the origin subchart.
    if (nodeCache.has(sourceCacheKey)) {
      myTopSCNode = nodeCache.get(sourceCacheKey);
    } else {
      const scSubNodes = scm
        .FindSCSubNodesFromDataObj(dataObj)
        .filter(node => node.GetSubChartFlag() !== enumSubChartFlag.drillup);

      myTopSCNode = scSubNodes[0];
      originIsNoBreak = myTopSCNode
        ? myTopSCNode.GetSubChartFlag() == enumSubChartFlag.nobreak
        : false;
      nodeCache.set(sourceCacheKey, myTopSCNode);
    }

    if (!myTopSCNode && !isPublish) {
      this.DeleteBranch(dataObj, dataObj.GetParent());
      return;
    }

    const cursubchartID = myTopSCNode.GetSubChartID();
    const draggedObjectChildrenSubChartID = scm.FindSubChartSubID(dataObjID, cursubchartID); // the subchart where the moved object is a topbox.

    this.GroupNodes([myTopSCNode], true, null);

    if (targetIsNoBreak) {
      // There's not a subchart just below the target. We just move the box without creating the subchart.

      if (targetSCID != cursubchartID) {
        // This can happen if we move from another chart to the current one (usually you drag from the current chart).
        // One example is rolling up subordinates after deletion.
        myTopSCNode.MoveSCNodeToSubChart(targetSCID);
      }

      const plm = this.GetPageLayoutMgr();
      plm.SetParent(dataObj, newParentDataObj, -1);
    } else {
      if (draggedObjectChildrenSubChartID > -1) {
        const startSubChart = scm.FindSubChart(draggedObjectChildrenSubChartID);
        if (startSubChart) {
          //I have my own subchart, so I need to move to my new home.
          //This could come about from an add or a move. Either way, the formatting likely needs an update. Hook into autoformat.
          const curParent = scm.FindSubChart(cursubchartID);
          if (curParent) curParent.RemoveChild(startSubChart);
          dropSubChart.AppendChildSubChart(startSubChart);
          scm.IncChangeCount();
        }

        myTopSCNode.MoveSCNodeToSubChart(targetSCID);
      } else if (originIsNoBreak) {
        // Below the box there are more boxes, not a subchart. We need a more complex function.
        this.GetCanvasRef().MoveBranchToSubChart(myTopSCNode, cursubchartID, targetSCID);
      }
    }

    dropSubChart.forceTemplate = true;
    dropSubChart.SetNeedOptimize(true);

    const childList = dataObj.GetChildList(false);
    if (!childList?.length) return;

    //TODO: detect what needs to happen with the children- rollup, orphan, move with, etc.
    for (let i = 0; i < childList.length; i++) {
      const theChild = childList[i];
      this.MoveBranch(theChild, dataObj, cache);
    }
  }

  MoveToStaging(dbObj) {
    const superFieldDef = dbObj.GetRecordParentIDFieldDef();
    const superKey = superFieldDef.GetFieldName();

    const change = {
      data: {
        [superKey]: "-1",
        Orphan: 1
      }
    };

    // Remove the lingering subchart to avoid Export Issues (and other potential problems)
    const scm = this.GetSubChartMgr();
    const subchart = scm.FindSubChartByTopObj(dbObj);
    if (subchart != null) scm.RemoveSubChart(subchart.GetSubChartID());

    this.SetBoxOverride(dbObj, enumOverrideType.move, change);
  }

  // OCN5-6486: re-home a deleted parent's children so they don't float.
  // Position docs revert child add-overrides; other docs stage each child.
  ResolveSubordinatesDeletedParent(record) {
    const children = [...(record.GetChildListNoFilter?.(false) ?? [])];
    if (this.IsPositionDocument()) {
      const overrideClass = this.GetOverrideClass();
      const allOverrides = overrideClass?.GetObjectList() ?? [];

      const addOverridesByRefId = new Map();
      allOverrides.forEach(ov => {
        if (ov.SmartGetFieldValue("Type") === enumOverrideType.add) {
          const refId = ov.SmartGetFieldValue("RefID");
          if (!addOverridesByRefId.has(refId)) addOverridesByRefId.set(refId, ov);
        }
      });
      children.forEach(child => {
        const childRecordId = child.GetObjectRecordID?.();
        const childAddOverride = childRecordId ? addOverridesByRefId.get(childRecordId) : null;
        if (childAddOverride) {
          overrideClass.RemoveDataOverride(child, childAddOverride);
        }
      });
    } else {
      children.forEach(child => this.MoveToStaging(child));
    }
  }

  /**
   *
   * @param {QObjDBObject} dataObj
   * @param {(QObjDBObject | null)} newPositionObj
   * @param {boolean} isNewHolder
   * @returns {void}
   */
  MoveHolder(dataObj, curPositionObj, newPositionObj) {
    var objDB = this.GetObjDB();
    curPositionObj?.Journal();
    newPositionObj?.Journal();
    dataObj?.Journal();

    if (curPositionObj) {
      const curParentHolderList = curPositionObj.GetHolderList() ?? [];
      const nextCurParentHolderList = curParentHolderList.filter(a => a != dataObj);
      curPositionObj.SetHolderList(nextCurParentHolderList);
    }

    dataObj.SetParentPosition(newPositionObj);

    if (newPositionObj) {
      dataObj.SmartSetFieldValue(kAssignmentStatus, AssignmentStatus.Assigned);
    }

    if (newPositionObj) {
      const newParentHolderList = newPositionObj.GetHolderList() ?? [];
      const holderAlreadyPresent = newParentHolderList.find(a => a == dataObj);

      if (!holderAlreadyPresent) {
        newParentHolderList.push(dataObj);
        newPositionObj.SetHolderList(newParentHolderList);
      }
    }

    if (!this.IsCustomChart()) return;

    // Move Holder Nodes from their Positions to the New Position.
    // If we have less old nodes than new, the HandlePositionsGroup method will create them.
    // If we have more old nodes than new, we remove the extra nodes.
    const scm = this.GetSubChartMgr();
    scm.JournalSCNodeList();
    const oldHolderNodes = scm.FindSCSubNodesFromDataObj(dataObj);
    const newPosNodes = newPositionObj ? scm.FindSCSubNodesFromDataObj(newPositionObj) : [];
    const affectedSC = new Set();

    oldHolderNodes.forEach((oldHolderNode, i) => {
      const oldNodeSubChartID = oldHolderNode.GetSubChartID();
      const newPosNode = newPosNodes[i];

      if (curPositionObj) {
        const oldHolderPosLayout = scm.FindSCNode(curPositionObj, oldNodeSubChartID)?.GetLayout();

        oldHolderPosLayout?.SetAssociatedObjIDs(
          oldHolderPosLayout.GetAssociatedObjIDs().filter(id => id !== dataObj.GetObjectID())
        );
      }

      if (newPosNode) {
        oldHolderNode.GetLayout().SetSubChartID(newPosNode.GetSubChartID());
      } else {
        scm.RemoveSCNode(dataObj.GetObjectID(), oldNodeSubChartID);
      }

      affectedSC.add(oldNodeSubChartID);
    });

    newPosNodes.forEach(newNode => {
      affectedSC.add(newNode.GetSubChartID());
    });

    affectedSC.forEach(scID => {
      this.HandlePositionsGroup(scID);
    });
  }

  DisconnectRoot(dataObj, parentDataObj) {
    dataObj.SetParent(null, parentDataObj);

    var scm = this.GetSubChartMgr();

    var removeList = [];
    dataObj.GetAllChildren(removeList);

    scm.RemoveChildrenSubCharts(removeList);

    //Layout Management - find destination subchart

    //Find current subchart
    var scSubNodes = scm.FindSCSubNodesFromDataObj(dataObj);
    var drillDownNodes = scSubNodes.filter(function (a) {
      return a.GetSubChartFlag() != enumSubChartFlag.drillup;
    });
    var drillUpNodes = scSubNodes.filter(function (a) {
      return a.GetSubChartFlag() == enumSubChartFlag.drillup;
    });

    var myTopSCNode = drillDownNodes[0];
    if (myTopSCNode != null) {
      var cursubchartID = myTopSCNode.GetSubChartID();

      scm.RemoveSCNode(dataObj.GetObjectID(), cursubchartID);

      if (removeList.length == 0) return;

      let subsSCID = cursubchartID;
      if (drillUpNodes.length >= 1) {
        const drillUpNode = drillUpNodes[0];
        subsSCID = drillUpNode.GetSubChartID();
        scm.RemoveSCNode(dataObj.GetObjectID(), subsSCID);
      }

      for (const child of removeList) {
        scm.RemoveSCNode(child.GetObjectID(), subsSCID);
      }
    }
  }

  DeleteBranch(dataObj, parentDataObj) {
    //Treat as move to parking
    if (dataObj.GetParent() == null) return;

    this.DisconnectRoot(dataObj, parentDataObj);

    //Layout Management - find destination subchart

    //Find current subchart
    var scm = this.GetSubChartMgr();
    var scSubNodes = scm.FindSCSubNodesFromDataObj(dataObj);
    var scSubNodes = scSubNodes.filter(function (a) {
      return a.GetSubChartFlag() != enumSubChartFlag.drillup;
    });
    var myTopSCNode = scSubNodes[0];
    if (myTopSCNode != null) {
      for (var i = 0; i < dataObj.GetNumChildren(); i++) {
        var theChild = dataObj.GetIndexChild(i);
        this.DeleteBranch(theChild, dataObj);
      }
    }
  }

  SetBoxOverride(dbObj, type, data = {}) {
    if (dbObj == null) return;
    this.SetDirty(true);
    var overrideClass = this.GetOverrideClass();
    overrideClass.SetBoxOverride(dbObj, type, data);
    this.DoAfterOverride({ dbObj, type });
    // Consume the pending filter refresh flagged by SetBoxOverride internals.
    // ApplyOverrides handles its own refresh; this covers single-edit calls.
    var wsMgr = this.fDocMgr?.fWSMgr;
    if (wsMgr?.fFilterRefreshPending) {
      if (overrideClass.RefreshFiltersIfCustomChart()) {
        wsMgr.fFilterRefreshPending = false;
      }
    }
  }

  GetManualAddedObjInfo() {
    return {
      source: {
        type: "manual",
        name: appstr("Manually added"),
        details: appstrp("Created by", " ") + Globals._wsMgr.GetUserName()
      },
      modified: []
    };
  }

  DoAfterOverride({ type, dbObj }) {
    switch (type) {
      case enumOverrideType.add:
        this.DoAfterAdd({ dbObj });
        break;
      case enumOverrideType.change:
        this.DoAfterChange({ dbObj });
        break;
      case enumOverrideType.delete:
        this.DoAfterDelete({ dbObj });
        break;
      case enumOverrideType.move:
        // move needs to be called later in stack in 'MoveBranch' function
        break;
    }
  }

  // Behavior after box ADD goes here
  DoAfterAdd({ dbObj }) {
    var canvas = this.GetCanvasRef();
    canvas.FocusObject({ dbObject: dbObj });
  }

  // Behavior after box MOVE goes here
  DoAfterMove({ dbObj, navigateToBox = true, doFocus = true }) {
    var canvas = this.GetCanvasRef();
    if (doFocus) {
      canvas.FocusObject({ dbObject: dbObj, navigateToBox: navigateToBox });
    }
  }

  // Behavior after box CHANGE goes here
  DoAfterChange({ dbObj }) {
    var canvas = this.GetCanvasRef();
    canvas.FocusObject({ dbObject: dbObj });
  }

  // Behavior after box DELETE goes here
  DoAfterDelete({ dbObj }) {
    // Keep holder lists in sync when a holder is removed from a position chart.
    var parentPos = dbObj?.GetParentPosition?.();
    if (parentPos) {
      var holderList = parentPos.GetHolderList() ?? [];
      parentPos.SetHolderList(holderList.filter(h => h !== dbObj));
    }
  }

  //=================================================================================================
  GetActiveFilters() {
    var objDB = this.fObjDB;
    var aClass = objDB.GetClassByName("Box");
    var fmgr = aClass.GetFilterMgr();
    const ctxId = fmgr.GetContextId();
    return fmgr.GetFilterBlocks(ctxId).filter(block => {
      return block.GetRules().length > 0;
    });
  }

  GetActiveHighlights() {
    var objDB = this.fObjDB;
    var aClass = objDB.GetClassByName("Box");
    var hmgr = aClass.GetHighlightMgr();
    return hmgr.CloneHighlightList().filter(function (a) {
      return a.GetHighlightApplied();
    });
  }

  ApplyChartFilters(allowSCSwitch) {
    var objDB = this.fObjDB;
    const objClasses = ["Box"];
    if (this.IsPositionDocument()) {
      objClasses.push("Position");
    }

    let didFilter = false;
    let filteredObjs, objMgr;

    if (this.IsPositionDocument()) {
      const aClass = objDB.GetClassByName("Position");
      objMgr = aClass.GetObjMgr();

      filteredObjs = objMgr.TagPositionFilteredObjs();
    } else {
      for (const className of objClasses) {
        var aClass = objDB.GetClassByName(className);
        objMgr = aClass.GetObjMgr();

        //Remove and re-add filtered objects from the DB
        filteredObjs = objMgr.TagFilteredObjs();
      }
    }

    const didMgrFilter = objMgr.RemoveFilteredObjects(filteredObjs);
    didFilter = didFilter || didMgrFilter;

    //Fix heirarchy/node list issues after applying filter.
    if (didFilter && allowSCSwitch) {
      var scm = this.GetSubChartMgr();
      this.fCanvasRef.SwitchToSubChartByObj(scm.GetCurrentSubChart());
    }
  }

  ApplyChartHighlights() {
    const docMgr = this.GetDocMgr();
    const wsMgr = docMgr.fWSMgr;
    const hMgr = this.GetHighlightMgr();
    if (hMgr) wsMgr.DoDynamicChartHighlights(this);
    wsMgr.DoApplyAgentView(this);
  }

  CopyLayoutToContainer(curLayout, containerLayout, fieldDef, copyVisuals = true) {
    if (copyVisuals) {
      containerLayout.SetBoxBorderColor(curLayout.GetBoxBorderColor());
      containerLayout.SetBoxShapeType(curLayout.GetBoxShapeType());
      containerLayout.SetBoxFillMode(curLayout.GetBoxFillMode());
      containerLayout.SetBoxFillColor(curLayout.GetBoxFillColor());
      containerLayout.SetBoxFillColorGradient(curLayout.GetBoxFillColorGradient());
      containerLayout.SetBorderThickness(curLayout.GetBorderThickness());
      containerLayout.SetBorderStyle(curLayout.GetBorderStyle());
    }

    if (fieldDef && copyVisuals) {
      // For system box styles (copyVisuals=true): inject the grouping field into the first
      // field cell and copy visual formatting from the employee box. For user-defined box
      // styles (copyVisuals=false): preserve the template's field assignments as designed.
      var textFormatCopy = curLayout.GetFirstFieldCell();
      var fieldCell = containerLayout.GetFirstFieldCell() || containerLayout.GetCellDesc(1, 1);
      if (fieldCell) {
        fieldCell.SetField(fieldDef);
        if (textFormatCopy) {
          fieldCell.SetFontName(textFormatCopy.GetFontName());
          fieldCell.SetFontColor(textFormatCopy.GetFontColor());
          fieldCell.SetCellFillMode(textFormatCopy.GetCellFillMode());
          fieldCell.SetCellFillColor(textFormatCopy.GetCellFillColor());
        }
      }
    }

    containerLayout.CalculateLayout();
  }

  CopyCondensedLayout(curLayout, condensedLayout) {
    condensedLayout.SetBoxFillMode(enumFillMode.noFill);
    var textFieldCopy = null;
    var textFormatCopy = null;
    var cellList = curLayout.fCellList;

    for (var aCellIdx in cellList) {
      var aCell = cellList[aCellIdx];
      var cellField = aCell.GetField();
      if (cellField == null) continue;

      if (cellField.GetFieldID() === "Name") {
        textFieldCopy = cellField;
        textFormatCopy = aCell;
        break;
      }

      if (!textFormatCopy || aCell.GetTextFormat().GetFontSize() > textFormatCopy.GetFontSize()) {
        textFieldCopy = cellField;
        textFormatCopy = aCell;
      }
    }

    if (textFieldCopy == null) return;
    var curFieldCell = condensedLayout.GetCellDesc(1, 1);
    curFieldCell.SetField(textFieldCopy);

    if (textFormatCopy) curFieldCell.ReplaceFormat(textFormatCopy);
    condensedLayout.CalculateLayout();
  }

  UngroupBoxesAndReapplyLayout(boxList) {
    var scm = this.GetSubChartMgr();
    var curSC = scm.GetCurrentSubChart();
    var docMgr = this.GetDocMgr();
    var wsMgr = docMgr.fWSMgr;
    var boxStyleMgr = wsMgr.GetBoxStylesMgr();
    var canvasPanel = this.fCanvasRef;
    var standardBoxes = boxList.filter(function (a) {
      return a.GetContainerType() === enumContainerType.nonmrb && !a.CanBeGrouped();
    });

    const proxyTemplate = this.GetProxyTemplate();

    canvasPanel.GroupPeers(standardBoxes, true);
    ApplyChartTemplateToBoxes(
      this,
      proxyTemplate,
      boxList,
      curSC.GetSubChartID(),
      wsMgr,
      boxStyleMgr
    );
  }

  ApplyGroupingToBoxes(
    boxList,
    theRule,
    groupFieldDef,
    MRBmatchLayout,
    applyLayoutToChildren,
    isMgrGroup = false,
    firstChildLayoutOverride = null,
    dynamicNumCols = false
  ) {
    var docMgr = this.GetDocMgr();
    var wsMgr = docMgr.fWSMgr;
    var boxStyleMgr = wsMgr.GetBoxStylesMgr();
    var canvasPanel = this.fCanvasRef;
    // Override lets the co-manager pass supply the pre-condensed employee layout,
    // so the container copies real border/fill instead of the nameOnly style.
    var firstChildLayout =
      firstChildLayoutOverride ?? boxStyleMgr.CloneLayout(boxList[0].GetLayout(), this);

    if (applyLayoutToChildren) {
      var childBoxStyleID = isMgrGroup
        ? theRule.GetManagerChildrenBoxStyle()
        : theRule.GetChildrenBoxStyle();
      var matchLayout = boxStyleMgr.GetLayoutByID(
        childBoxStyleID,
        true,
        enumBoxStyleSearch.allSets
      );
      var cloneLayout = boxStyleMgr.CloneLayout(matchLayout, this);
      // CopyCondensedLayout is only intended for the nameOnly system style (Condensed mode, ID 118).
      // Other system styles (e.g. Manager-Large) should keep their own fill colors.
      const kNameOnlyStyleID = 118;
      if (childBoxStyleID == kNameOnlyStyleID)
        this.CopyCondensedLayout(firstChildLayout, cloneLayout);
      wsMgr.ApplyLayoutToBoxList(cloneLayout, boxList, theRule, false);
    }
    var MRBLayout = boxStyleMgr.CloneLayout(MRBmatchLayout, this);
    // Only co-manager mode overrides numCols; other styles keep the designer's chosen column count.
    if (dynamicNumCols) MRBLayout.SetContainerNumCols(boxList.length);
    var isSystemContainerStyle =
      boxStyleMgr.GetBoxStyleSetFromID(MRBmatchLayout.GetBoxStyleID(), true) ===
      enumBoxStyleSet.systemboxstyles;
    // firstChildLayoutOverride means the container is a clone of the employee's group
    // style, not a user-picked container style, so visuals always copy regardless of
    // isSystemContainerStyle.
    const copyVisuals = isSystemContainerStyle || firstChildLayoutOverride != null;
    this.CopyLayoutToContainer(firstChildLayout, MRBLayout, groupFieldDef, copyVisuals);
    this.FinishLoadLayout(MRBLayout);
    canvasPanel.GroupPeers(boxList, false, MRBLayout);
  }

  GroupSiblings(
    siblingArrays,
    theRule,
    groupingRuleField,
    MRBmatchLayout,
    isMgrGroup = false,
    firstChildLayoutOverride = null,
    dynamicNumCols = false
  ) {
    var applyLayoutToChildren = isMgrGroup
      ? theRule.GetManagerChildrenBoxStyleEnabled()
      : theRule.GetChildrenBoxStyleEnabled();
    var groupingThreshold = theRule.GetGroupingThreshold();

    for (var aParentID in siblingArrays) {
      var theSiblings = siblingArrays[aParentID];
      var theBox = theSiblings[0];
      var combineDef = theBox.GetDataObj().GetFieldDefByName(groupingRuleField);
      if (combineDef == null) continue;
      var groupList = [];
      for (var aSiblingIdx in theSiblings) {
        var theSibling = theSiblings[aSiblingIdx];
        MatchToGroup(groupList, theSibling, combineDef);
      }
      for (var aGroup in groupList) {
        var theBoxes = groupList[aGroup].fGroup;
        if (theBoxes.length >= groupingThreshold) {
          this.ApplyGroupingToBoxes(
            theBoxes,
            theRule,
            combineDef,
            MRBmatchLayout,
            applyLayoutToChildren,
            isMgrGroup,
            firstChildLayoutOverride,
            dynamicNumCols
          );
        }
      }
    }
  }

  // Resolve "Default to last used or only created" (fGroupingBoxStyle === null) to a real ID.
  // Preference order: last successfully applied style (persisted in localStorage) → first
  // available user/shared standardmrb style.
  _resolveDefaultMRBStyleID(boxStyleMgr) {
    const lastUsed = localStorage.getItem("lastMRBStyleID");
    if (lastUsed != null) {
      var lastLayout = boxStyleMgr.GetLayoutByID(lastUsed, false, enumBoxStyleSearch.allSets);
      if (lastLayout != null && lastLayout.GetContainerType() === enumContainerType.standardmrb) {
        return lastUsed;
      }
    }
    // Fall back to the first available custom group style (user styles first, then shared).
    const priorSet = boxStyleMgr.CurrentBoxStyleSet();
    let firstStyleID = null;
    for (const set of [enumBoxStyleSet.userboxstyles, enumBoxStyleSet.sharedboxstyles]) {
      boxStyleMgr.SwitchBoxStyles(set);
      const n = boxStyleMgr.GetNumLayouts();
      for (let i = 0; i < n; i++) {
        const layout = boxStyleMgr.GetIndexLayout(i);
        if (
          layout.GetContainerType() === enumContainerType.standardmrb &&
          layout.GetHierarchyType() === enumHierarchyTypes.grouping
        ) {
          firstStyleID = layout.GetBoxStyleID();
          break;
        }
      }
      if (firstStyleID != null) break;
    }
    boxStyleMgr.SwitchBoxStyles(priorSet);
    return firstStyleID;
  }

  ApplyChartGrouping(groupingRule, unGroupOnly) {
    if (this.GetHierarchyType() === enumHierarchyTypes.positions) return;

    const ctx = this._prepareGroupingContext(groupingRule);
    if (!ctx) return;

    this.UngroupBoxesAndReapplyLayout(ctx.boxList);

    if (!unGroupOnly) {
      if (!ctx.boxList.length) return;
      const layouts = this._resolveGroupingLayouts(ctx);
      if (!layouts) return;
      const isManagerBox = this._buildManagerDetector(ctx.boxList);
      this._applyFirstPassGrouping(ctx, layouts, isManagerBox);
      if (ctx.applyToMgrs) this._applyManagerPassGrouping(ctx, layouts, isManagerBox);
      this._applyCondensedSingletons(ctx, isManagerBox);
      this._reapplyEmployeeStyles(ctx, isManagerBox);
    }

    this._recordBranchGroupingFlag(ctx, unGroupOnly);
    this._finalizeGroupingLayout(ctx.wsMgr);
  }

  _prepareGroupingContext(groupingRule) {
    const docMgr = this.GetDocMgr();
    const wsMgr = docMgr.fWSMgr;
    const scm = this.GetSubChartMgr();
    const curSC = scm.GetCurrentSubChart();

    const invalidSubchartForGrouping =
      curSC == null ||
      curSC.GetSubChartID() === -1 ||
      curSC.GetSubChartParentID() === -1 ||
      curSC.GetSubChartID() === kMasterSCID;
    if (invalidSubchartForGrouping) return null;

    const theRule = new QChartTemplateRule();
    theRule.LoadFromJSON(groupingRule);

    const groupingRuleField = theRule.GetGroupingField();
    if (groupingRuleField == null) return null;

    // allowDuringPublish=true so grouping is applied during PDF/PPT export.
    const boxList = wsMgr.GetTargetTrees(false, this, true, true) || [];

    // The root node has no peers at its level, so it must never be a grouping candidate.
    const topDBObj = curSC.GetTopDBObj();
    const matchingBoxes = theRule
      .GetMatchingBoxes(boxList, this, kGroupingRule)
      .filter(b => !b.CanBeGrouped() && b.GetDataObj() !== topDBObj);

    return {
      wsMgr,
      boxStyleMgr: wsMgr.GetBoxStylesMgr(),
      theRule,
      groupingRuleField,
      boxList,
      matchingBoxes,
      topDBObj,
      applyToMgrs: theRule.GetApplyToManagers(true) === true
    };
  }

  _resolveGroupingLayouts(ctx) {
    const { theRule, boxStyleMgr } = ctx;

    let boxStyleID = theRule.GetGroupingBoxStyle();
    // null means "Default to last used or only created" — resolve at apply time.
    if (boxStyleID == null) boxStyleID = this._resolveDefaultMRBStyleID(boxStyleMgr);
    const MRBmatchLayout = boxStyleMgr.GetLayoutByID(boxStyleID, false, enumBoxStyleSearch.allSets);
    const invalidContainer =
      MRBmatchLayout == null || MRBmatchLayout.GetContainerType() === enumContainerType.nonmrb;
    if (invalidContainer) {
      console.log("invalidMRBset");
      return null;
    }
    localStorage.setItem("lastMRBStyleID", boxStyleID);

    return { MRBmatchLayout, origEmployeeLayout: null };
  }

  _buildManagerDetector(boxList) {
    // Three-signal manager detection: fSubChartDrill (server/PDF), GetNumChildren
    // (source doc), parentIDSet (mock docs where fChildren back-refs are dropped).
    const parentIDSet = new Set();
    for (const b of boxList) {
      const parent = b.GetDataObj()?.GetParent?.();
      const parentID = parent?.GetObjectRecordID?.();
      if (parentID != null) parentIDSet.add(parentID);
    }
    return b => {
      if (b.fSubChartDrill != null) return true;
      const dbObj = b.GetDataObj();
      if ((dbObj?.GetNumChildren?.() ?? 0) > 0) return true;
      const recordID = dbObj?.GetObjectRecordID?.();
      return recordID != null && parentIDSet.has(recordID);
    };
  }

  _applyFirstPassGrouping(ctx, layouts, isManagerBox) {
    const { theRule, groupingRuleField, matchingBoxes, boxStyleMgr, applyToMgrs } = ctx;
    // Capture original employee layout before first pass transforms employees to
    // nameOnly in condensed mode. Prefer non-manager so co-manager copies employee style.
    const firstEmpBox = applyToMgrs
      ? (matchingBoxes.find(b => !isManagerBox(b)) ?? matchingBoxes[0])
      : matchingBoxes[0];
    layouts.origEmployeeLayout = firstEmpBox
      ? boxStyleMgr.CloneLayout(firstEmpBox.GetLayout(), this)
      : null;

    const firstPassBoxes = matchingBoxes.filter(b => !isManagerBox(b));
    const siblingArrays = this.GetSiblingBoxes(firstPassBoxes);
    this.GroupSiblings(siblingArrays, theRule, groupingRuleField, layouts.MRBmatchLayout, false);
  }

  _applyManagerPassGrouping(ctx, layouts, isManagerBox) {
    const { theRule, groupingRuleField, matchingBoxes, boxStyleMgr, wsMgr } = ctx;
    const mgrStyle = theRule.GetManagerGroupingStyle(); // "comgr" | "custom"
    let mgrContainerLayout = null;
    let mgrFirstChildLayoutOverride = null;

    if (mgrStyle === "custom") {
      const mgrContainerStyleID =
        theRule.GetManagerGroupingBoxStyle() ?? this._resolveDefaultMRBStyleID(boxStyleMgr);
      mgrContainerLayout = boxStyleMgr.GetLayoutByID(
        mgrContainerStyleID,
        false,
        enumBoxStyleSearch.allSets
      );
    } else {
      mgrContainerLayout = boxStyleMgr.CloneLayout(layouts.MRBmatchLayout, this);
      mgrFirstChildLayoutOverride = layouts.origEmployeeLayout;
    }

    if (!mgrContainerLayout || mgrContainerLayout.GetContainerType() === enumContainerType.nonmrb)
      return;

    const mgrBoxes = matchingBoxes.filter(isManagerBox);
    if (mgrBoxes.length === 0) return;

    const mgrSiblingArrays = this.GetSiblingBoxes(mgrBoxes);
    this.GroupSiblings(
      mgrSiblingArrays,
      theRule,
      groupingRuleField,
      mgrContainerLayout,
      true,
      mgrFirstChildLayoutOverride,
      mgrStyle !== "custom"
    );
    wsMgr.RecalcChartLayout();
  }

  _applyCondensedSingletons(ctx, isManagerBox) {
    const { theRule, boxList, matchingBoxes, boxStyleMgr, topDBObj, wsMgr } = ctx;
    const kNameOnlyStyleID = 118;
    const childrenStyleID = theRule.GetChildrenBoxStyleEnabled()
      ? theRule.GetChildrenBoxStyle()
      : null;
    const anyGroupFormed =
      childrenStyleID == kNameOnlyStyleID && matchingBoxes.some(b => b.GetContainerNode() != null);
    if (!anyGroupFormed) return;

    const matchingBoxSet = new Set(matchingBoxes);
    const singletons = boxList.filter(
      b =>
        b.GetDataObj() != null &&
        !b.GetLayoutLock() &&
        !b.CanBeGrouped() &&
        b.GetDataObj().GetParent() != null &&
        b.GetDataObj() !== topDBObj &&
        b.GetContainerNode() == null &&
        !matchingBoxSet.has(b) &&
        !isManagerBox(b)
    );
    if (singletons.length === 0) return;

    const nameOnlyLayout = boxStyleMgr.GetLayoutByID(
      kNameOnlyStyleID,
      true,
      enumBoxStyleSearch.allSets
    );
    if (!nameOnlyLayout) return;

    const sourceBox = matchingBoxes.find(b => b.GetContainerNode() != null);
    const sourceLayout = sourceBox ? sourceBox.GetLayout() : null;
    for (const box of singletons) {
      const cloneLayout = boxStyleMgr.CloneLayout(nameOnlyLayout, this);
      this.CopyCondensedLayout(sourceLayout ?? box.GetLayout(), cloneLayout);
      wsMgr.ApplyLayoutToBoxList(cloneLayout, [box], theRule, false);
    }
  }

  _reapplyEmployeeStyles(ctx, isManagerBox) {
    const { theRule, matchingBoxes, boxStyleMgr, wsMgr, applyToMgrs } = ctx;

    // Re-applying here corrects any view-rule box style override on grouped boxes.
    if (theRule.GetChildrenBoxStyleEnabled()) {
      const empLayout = boxStyleMgr.GetLayoutByID(
        theRule.GetChildrenBoxStyle(),
        true,
        enumBoxStyleSearch.allSets
      );
      if (empLayout) {
        const nonMgrBoxes = (
          applyToMgrs ? matchingBoxes.filter(b => !isManagerBox(b)) : matchingBoxes
        ).filter(b => b.GetContainerNode() != null);
        if (nonMgrBoxes.length > 0) {
          wsMgr.ApplyLayoutToBoxList(
            boxStyleMgr.CloneLayout(empLayout, this),
            nonMgrBoxes,
            theRule,
            false
          );
        }
      }
    }

    if (applyToMgrs && theRule.GetManagerChildrenBoxStyleEnabled()) {
      const mgrEmpLayout = boxStyleMgr.GetLayoutByID(
        theRule.GetManagerChildrenBoxStyle(),
        true,
        enumBoxStyleSearch.allSets
      );
      if (mgrEmpLayout) {
        const mgrBoxes = matchingBoxes.filter(b => isManagerBox(b) && b.GetContainerNode() != null);
        if (mgrBoxes.length > 0) {
          wsMgr.ApplyLayoutToBoxList(
            boxStyleMgr.CloneLayout(mgrEmpLayout, this),
            mgrBoxes,
            theRule,
            false
          );
        }
      }
    }
  }

  _recordBranchGroupingFlag(ctx, unGroupOnly) {
    if (!this.fPageLayoutMgr?.fBranchMgr) return;
    this.fPageLayoutMgr.fBranchMgr.fUsesMgrGrouping = !unGroupOnly && ctx.applyToMgrs;
  }

  _finalizeGroupingLayout(wsMgr) {
    wsMgr.RecalcChartLayout();
    if (qShouldOptimizeAfterGrouping(Globals._publishRef)) DoOptimizePages(wsMgr);
    this.UpdateDocContext();
    wsMgr.PrepRenderWS();
    wsMgr.RenderWS();
  }

  ApplyChartGroupingToDoc() {
    var activeGroup = this.GetActiveGroup();
    var activeGroupID = activeGroup ? activeGroup.activeGroupID : null;
    var user = activeGroup ? activeGroup.user : null;
    var docMgr = this.GetDocMgr();
    var wsMgr = docMgr.fWSMgr;
    var userInfo = wsMgr.GetUserInfo();
    var lastActiveGroup = userInfo.GetLastActiveGroup();
    var ungroupOnly = false;
    var activeGroup = null;

    if (activeGroupID != null) {
      // A group is currently active. Always re-apply it — the pre-switch ungroup removes
      // containers before every subchart switch, so they must be rebuilt on every arrival
      // regardless of whether lastActiveGroup says "already applied".
      if (user === wsMgr.GetUserName()) {
        activeGroup = userInfo.GetChartGroupByID(parseInt(activeGroupID));
      }
    } else if (lastActiveGroup != null) {
      activeGroup = userInfo.GetChartGroupByID(parseInt(lastActiveGroup));
      ungroupOnly = true;
    }

    if (!activeGroup) return;

    this.ApplyChartGrouping(activeGroup, ungroupOnly);
  }

  GetActiveGroup() {
    const userGrouping = this.GetWSMgr()?.GetUserInfo()?.GetChartRules()?.activeGrouping;
    if (userGrouping) return userGrouping;

    // Local storage fallback
    const activeGroup = JSON.parse(localStorage.getItem("activeGroup"));
    return activeGroup ? activeGroup : null;
  }

  IsGroupingApplied() {
    if (this.IsPositionDocument()) return false;
    const wsmgr = this.GetDocMgr().fWSMgr;
    const currUser = wsmgr.GetUserName();
    const activeGroup = this.GetActiveGroup();
    const activeGroupID = activeGroup ? activeGroup.activeGroupID : null;
    const user = activeGroup ? activeGroup.user : null;
    return activeGroupID != null && currUser === user;
  }

  //=================================================================================================
  AddDynShortcuts(metadata) {
    var shortcuts = metadata["shortcuts"];
    if (!shortcuts) return;
    var scutmgr = this.GetShortcutMgr();
    var active = shortcuts["shortcutConfig"] && shortcuts["shortcutConfig"]["enabled"];
    scutmgr.SetActive(active);
    for (var shortcut in shortcuts) {
      var loadedShortcut = shortcuts[shortcut];
      var newshortCut = new QShortcut();
      newshortCut.fShortcutName = loadedShortcut["value"];
      newshortCut.fShortcutField = loadedShortcut["field"];
      newshortCut.fShortcutObjID = loadedShortcut["ID"];
      newshortCut.fShortcutTreeID = loadedShortcut["treeID"];
      newshortCut.DoInitShortcut();
      scutmgr.fShortcutList.push(newshortCut);
    }
  }

  AddDynFilters(aClass, jsonObj, wsMgr) {
    //TODO: Scope against object DB?
    var filterData = jsonObj["filters"];
    var highlightData = jsonObj["highlights"];

    var previouslyActiveFilters = [];
    var previousActiveHighlights = [];

    var refreshInfo = wsMgr.fDataRefreshInfo;
    var activeFilters = Globals._svrChartMgr.fActiveFilters;
    var activeHighlights = Globals._svrChartMgr.fActiveHighlights;

    if (filterData) {
      var curFilterNames = filterData.map(function (item) {
        return item.filterName;
      });
      if (refreshInfo) {
        // workbench refresh
        //TODO: This should happen at a separate stage (copy over prior state) and not inline here.
        var priorDocMgr = refreshInfo.GetPriorDocMgr();
        var priorFMgr = priorDocMgr.GetCurrentDoc().GetFilterMgr();
        var curFilters = priorFMgr.GetAllowedFilterList().filter(function (item) {
          return !item.fFilterOff && curFilterNames.indexOf(item.fFilterName) > -1;
        });
        previouslyActiveFilters = curFilters.map(function (item) {
          return item.fFilterName;
        });
      }

      var n = filterData.length;
      for (var i = 0; i < n; i++) {
        var aFilter = new QFilter();
        var filterInfo = filterData[i];
        var filterName = filterInfo.filterName;

        var filterOff = activeFilters ? !activeFilters[filterName] : !filterInfo.fFilterOnByDefault;

        aFilter.InitFilter(filterName, filterInfo.filterDef, filterOff);
        aFilter.fHidden = !!filterInfo.fHidden;

        aClass.AddFilter(aFilter);
      }
    }

    if (highlightData) {
      var curHighlightNames = highlightData.map(function (item) {
        return item.name;
      });
      if (refreshInfo) {
        var priorDocMgr = refreshInfo.GetPriorDocMgr();
        var priorHMgr = priorDocMgr.GetCurrentDoc().GetHighlightMgr();
        var curHighlights = priorHMgr.GetAllowedHighlightList().filter(function (item) {
          return item.fActive && curHighlightNames.indexOf(item.fName) > -1;
        });
        previousActiveHighlights = curHighlights.map(function (item) {
          return item.fName;
        });
      }
      for (var aHighlightIdx in highlightData) {
        var newHighlightObj = new QHighlight();
        var highlightInfo = highlightData[aHighlightIdx];
        var highlightName = highlightInfo.name;
        var highlightOn = activeHighlights
          ? !!activeHighlights[highlightName]
          : highlightInfo.fHighlightOnByDefault;

        newHighlightObj.InitHighlight(
          highlightName,
          highlightInfo.rule,
          highlightOn,
          highlightInfo.color
        );
        newHighlightObj.fHidden = !!highlightInfo.fHidden;

        aClass.AddHighlight(newHighlightObj);
      }
    }

    var fMgr = this.GetFilterMgr();
    fMgr.LoadLocalFilters();

    var hMgr = this.GetHighlightMgr();
    var cachedHighlightInfo = hMgr.GetFullGroupList();
    if (cachedHighlightInfo) {
      var myHighlightInfo = cachedHighlightInfo.filter(
        highlight => highlight.GetGroupIdentifier() === hMgr.GetFilterGroupIdentifier()
      );
      hMgr.SetInteractiveHighlights(myHighlightInfo);
    }
  }

  HandleInternalFields(aClass, metadata) {
    var builtInFields = ["Span Of Control", "Subordinates", kHeadcount, "Level", "Peers"]; // all belong to position
    if (this.IsServerChart() || this.IsBackground()) {
      //Why bury object info in here?
      builtInFields.push("scFlag");
      builtInFields.push("treeID");
    }

    builtInFields.push("_objInfo");

    if (this.IsPositionDocument() && aClass.GetClassName() === enumObjClassNames.position) {
      builtInFields = [
        ...builtInFields,
        kBudgetedHeadcount,
        kSalaryRollup,
        kCurrentPositionCost,
        kPositionBudgetRollup,
        kPositionHoldersCount,
        kPositionAllocation,
        kDesiredHolders,
        kStaffingTarget,
        kPositionCount,
        kSubordinatePositions,
        kDirectSubordinatePositions,
        kPeerPositions
      ];
    }

    if (aClass.ActiveAuxMapping(metadata.fields) === true) builtInFields.push("isAux");

    var noTranslation = ["scFlag", "isAux", "treeID", "_objInfo"]; // position
    for (var aFieldIdx in builtInFields) {
      var theName = builtInFields[aFieldIdx];
      let theLocalizedName = Globals._svrChartMgr.Localize(aClass.GetClassName(), theName);
      var fieldID = theName.replaceAll(" ", "");

      if (noTranslation.indexOf(theName) < 0 && theLocalizedName === theName) {
        theLocalizedName = appstr(fieldID);
      }
      switch (theName) {
        case "Span Of Control":
        case "Subordinates":
        case kHeadcount:
        case "Peers":
        case kPositionCount:
        case kSubordinatePositions:
        case kDirectSubordinatePositions:
        case kPeerPositions: {
          var aField = aClass.GetFieldDefByID(fieldID);
          if (!aField) continue;
          aField.SetCalcCode(null);
          aField.SetFormulaRule(null);
          aField.SetReadOnly(true);
          aField.SetFieldType(enumFieldType.number);
          aField.SetFieldLabel(theLocalizedName);
          break;
        }
        case kBudgetedHeadcount:
        case kSalaryRollup:
        case kCurrentPositionCost:
        case kPositionBudgetRollup:
        case "Level":
        case kPositionHoldersCount:
        case kPositionAllocation: {
          var aField = aClass.GetFieldDefByID(fieldID);
          if (!aField) continue;
          aField.SetCalcCode(null);
          aField.SetReadOnly(true);
          aField.SetFormulaRule(null);
          aField.SetFieldType(enumFieldType.number);
          aField.SetFieldLabel(theLocalizedName);
          if (theName === kPositionAllocation) {
            aField.SetNumDecPlaces(2);
          }
          break;
        }
        case "treeID":
        case "scFlag":
        case "isAux": {
          if (aClass.GetFieldDefByID(fieldID)) break;
          var aField = new QObjDBFieldDef();
          aField.SetReadOnly(true);
          aField.InitField(fieldID, theName, aClass);
          aField.SetInternalOnly(theName != "isAux");
          aClass.AddField(aField);
          break;
        }
        case "_objInfo": {
          if (aClass.GetFieldDefByID(fieldID)) break;
          aField = new QObjDBFieldDef();
          aField.SetReadOnly(true);
          aField.InitField(fieldID, theName, aClass);
          aField.SetFieldType(enumFieldType.object);
          aField.SetFieldSubtype(enumCellObjectSubtypes.objinfo);
          aField.SetInternalOnly(true);
          aClass.AddField(aField);
          break;
        }
      }
    }

    const kCalcFields = [];
    if (this.IsPositionDocument()) {
      if (aClass.GetClassName() === enumObjClassNames.box) {
        kCalcFields.push("_Headcount", "_Subordinates", "_Span Of Control", "_Peers");
      } else {
        kCalcFields.push(
          "_Level",
          "_Position Count",
          "_Subordinate Positions",
          "_Direct Subordinate Positions",
          "_Peer Positions"
        );
      }
    } else {
      kCalcFields.push("_Level", "_Headcount", "_Subordinates", "_Span Of Control", "_Peers");
    }
    if (!this.IsServerChart()) {
      for (var aFieldIdx in kCalcFields) {
        var theName = kCalcFields[aFieldIdx];
        if (aClass.GetFieldDefByID(theName)) continue;
        var aField = new QObjDBFieldDef();
        var preppedName = theName.slice(1);
        let theLocalizedName = Globals._svrChartMgr.Localize(aClass.GetClassName(), preppedName);
        aField.InitField(theName, theName, aClass);
        switch (theName) {
          case "_Level":
            aField.SetCalcCode(enumCalcCode.level);
            break;
          case "_Headcount":
            aField.SetCalcCode(enumCalcCode.headcount);
            break;
          case "_Peers":
            aField.SetCalcCode(enumCalcCode.peers);
            break;
          case "_Subordinates":
            aField.SetCalcCode(enumCalcCode.subordinates);
            break;
          case "_Span Of Control":
            aField.SetCalcCode(enumCalcCode.spanofcontrol);
            break;
          case "_Position Count":
            aField.SetCalcCode(enumCalcCode.positioncount);
            break;
          case "_Subordinate Positions":
            aField.SetCalcCode(enumCalcCode.subordinatepositions);
            break;
          case "_Direct Subordinate Positions":
            aField.SetCalcCode(enumCalcCode.directsubordinatepositions);
            break;
          case "_Peer Positions":
            aField.SetCalcCode(enumCalcCode.peerpositions);
            break;
        }
        aField.SetReadOnly(true);
        aField.SetFieldType(enumFieldType.number);
        var calcFieldFormula = new QFormulaRule();
        calcFieldFormula.InitFormulaRule(aField.GetCalcCode());
        aField.SetFormulaRule(calcFieldFormula);

        const labelPrefix = this.IsCustomChart() ? "_" : "";
        aField.SetFieldLabel(labelPrefix + theLocalizedName);
        aClass.AddField(aField);
      }
    }
  }

  // CCs only: live calc fields (_Headcount etc.) are not serializable and absent after LoadFromJSON.
  SeedPreviewCalcFields() {
    const o = this.GetObjDB();

    const classNames = [enumObjClassNames.box];
    if (this.IsPositionDocument()) classNames.push(enumObjClassNames.position);

    classNames.forEach(className => {
      const aClass = o.GetClassByName(className);
      if (!aClass) return;

      // All calc fields for a class are seeded in one pass. Avoid recalc if one is already present.
      const sentinel = className === enumObjClassNames.position ? "_Level" : "_Headcount";
      if (aClass.GetFieldDefByID(sentinel)) return;

      this.HandleInternalFields(aClass, { fields: null });
    });
  }

  LocalizeBuiltInPositionFields(aClass, metadata) {
    const className = aClass.GetClassName();
    const fieldMapping = metadata["fields"];
    if (!fieldMapping) return;

    const localizeFields = fields => {
      for (const fieldId of fields) {
        const fieldValue = fieldMapping[fieldId];
        const isMapped = fieldValue != null && fieldValue !== "";

        // We can system generate Cost Band fields from holder values
        const isPositionFieldFromEmployee =
          this.IsPositionDocument() &&
          COST_BAND_FIELDS.includes(fieldId) &&
          metadata?.positionChart?.costBandFields?.[fieldId]?.type === enumObjectTypes.holder;
        const isSystemCalculated = fieldValue === SYSTEM_CALCULATE || isPositionFieldFromEmployee;

        // Skip localization only if the field is mapped to something other than SYSTEM_CALCULATE
        if (isMapped && !isSystemCalculated) continue;

        const aField = aClass.GetFieldDefByID(fieldId);
        if (!aField) continue;

        let theLocalizedName = Globals._svrChartMgr.Localize(className, fieldId);
        if (theLocalizedName === fieldId) {
          theLocalizedName = appstr(fieldId);
        }

        aField.SetFieldLabel(theLocalizedName);
      }
    };

    const fieldsByClass = {
      [enumObjClassNames.position]: [
        kDesiredHolders,
        kPositionBudgetedSalary,
        kPositionCostCenter,
        kPositionCostBandMinimum,
        kPositionCostBandMidpoint,
        kPositionCostBandMaximum,
        kPositionHoldersCount
      ],
      [enumObjClassNames.box]: [
        kLocationID,
        kEmployeeSalary,
        kEmployeePay,
        kEmployeeCostCenter,
        kEmployeeLocation
      ]
    };

    const fieldsToProcess = fieldsByClass[className];
    if (fieldsToProcess) {
      localizeFields(fieldsToProcess);
    }
  }

  GetFieldIDIfNotReserved(fieldName, removeSpaces = true) {
    const fieldID = removeSpaces ? fieldName.replaceAll(" ", "") : fieldName;
    if (kReservedFieldNames.indexOf(fieldID.toLowerCase()) !== -1) {
      return null;
    }
    return fieldID;
  }

  CreateFieldDef(fieldName, aClass, staticFormulaFields, removeSpaces = true) {
    const fieldID = this.GetFieldIDIfNotReserved(fieldName, removeSpaces);
    if (!fieldID) {
      return null;
    }
    const theLocalizedName = Globals._svrChartMgr.Localize(aClass.GetClassName(), fieldName);
    const fieldDef = new QObjDBFieldDef();
    // field name
    fieldDef.InitField(fieldID, fieldName, aClass);
    fieldDef.SetFieldLabel(theLocalizedName);
    if (staticFormulaFields.indexOf(fieldName) > -1) {
      fieldDef.SetReadOnly(true);
      fieldDef.SetCalcCode(enumCalcCode.static);
    }
    aClass.ForceUniqueID(fieldDef);
    aClass.AddField(fieldDef);

    return fieldDef;
  }

  AddDynFields(objDb, fieldList, metadata) {
    //Convert the fields from the metadata into FE document fields.
    const boxClass = objDb.GetClassByName("Box");
    const positionClass = objDb.GetClassByName("Position");

    //NOTE - this should NOT be called independent of InitializeDocFields
    //Supporting components exist outside of this.
    var fieldMapping = metadata["fields"];

    // TODO: review restrictQuery. If we are passing in the fieldList here, we should be passing in the restrictQuery fields.
    // will restrictQuery apply to both holder and position fields?
    //if(data['doRestrictQuery'] === true)
    //fieldList = data['restrictQuery'];
    Globals._svrChartMgr.SetLocaleInfo(metadata["localizationConfig"]);

    const isPositions = this.IsPositionDocument();

    if (fieldList == null) fieldList = [];

    fieldList = fieldList.filter(function (a) {
      return a != null;
    });
    var staticFormulaFields = [];
    if ("formulas" in metadata)
      staticFormulaFields = metadata["formulas"].map(formula => formula.name);

    var positionFields;
    var holderFields;
    if (this.GetHierarchyType() === enumHierarchyTypes.positions) {
      positionFields = metadata["positionFields"];
      holderFields = metadata["holderFields"];
    }
    if (metadata["formulaDependencies"] != null)
      fieldList = fieldList.concat(metadata["formulaDependencies"]);

    if (isPositions && SynthesizePositions(metadata)) {
      if (!fieldList.includes("PositionID")) {
        fieldList = fieldList.concat(["PositionID"]);
      }
      if (!fieldList.includes("Parent PositionID")) {
        fieldList = fieldList.concat(["Parent PositionID"]);
      }
    }

    for (var aFieldIdx in fieldList) {
      const theName = fieldList[aFieldIdx];
      let boxFieldDef = null;

      if (isPositions) {
        const isHolderField = holderFields.includes(theName);
        if (isHolderField) {
          boxFieldDef = this.CreateFieldDef(theName, boxClass, staticFormulaFields);
        }

        const isPositionField = positionFields.includes(theName);
        if (isPositionField) {
          this.CreateFieldDef(theName, positionClass, staticFormulaFields);
        }
      } else {
        boxFieldDef = this.CreateFieldDef(theName, boxClass, staticFormulaFields);
      }

      if (theName == fieldMapping["AuxSuperID"]) {
        boxFieldDef?.SetFieldID("AuxSuperID");
      }
    }

    if (!this.IsPlanChart()) return;

    const metricsFields = [...staticFormulaFields, ...kReservedMetricsFields];
    if (metricsFields && metricsFields.length > 0) {
      for (const metricField of metricsFields) {
        let objClass = boxClass;
        if (isPositions && positionFields?.includes(metricField)) objClass = positionClass;
        this.addFormulaFieldDef("_metrics_" + metricField, objClass);
      }

      // For position charts, employee-type static formulas get their _metrics_ field defs
      // added to boxClass above. Also register a standalone _metrics_ def in positionClass so
      // that fRootDBObj (the position hierarchy root) can map the server JSON value for these
      // fields via ProcessObject. The server stores _metrics_employeeFormula on position records.
      // Use formula.targetType from metadata as the authoritative source rather than holderFields,
      // because user-defined formula names may not be present in the holderFields array.
      // For position charts, employee-type static formulas get their _metrics_ field defs
      // added to boxClass above. Also register a plain def and a _metrics_ def in positionClass
      // so that fRootDBObj (the position hierarchy root) can resolve these fields via the
      // standard GetFieldValue planning redirect — the same path used for all other metrics.
      // plainDef.SetPlanningFieldDef(metricsDef) creates the redirect so that:
      //   GetFieldValue(plainDef, false)       → _metrics_ planning value
      //   GetFieldValue(plainDef, false, true) → stable pre-plan baseline
      if (isPositions) {
        const formulasByName = new Map((metadata["formulas"] ?? []).map(f => [f.name, f]));
        for (const metricField of metricsFields) {
          const formula = formulasByName.get(metricField);
          if (formula && formula.targetType !== "position") {
            const plainDef = this.addStandaloneMetricsFieldDef(metricField, positionClass);
            const metricsDef = this.addStandaloneMetricsFieldDef(
              "_metrics_" + metricField,
              positionClass
            );
            if (plainDef && metricsDef) {
              plainDef.SetPlanningFieldDef(metricsDef);
            }
          }
        }
      }
    }
  }

  addFormulaFieldDef(fieldName, objClass) {
    var fieldID = GetFieldId(fieldName);
    var fieldDef = objClass.GetFieldDefByName(fieldName);
    if (!fieldDef) {
      var localizedName = Globals._svrChartMgr.Localize(objClass.GetClassName(), fieldName);
      var newField = new QObjDBFieldDef();
      newField.InitField(fieldID, fieldName, objClass);
      newField.SetFieldLabel(localizedName);
      var sourceFieldDef = objClass.GetFieldDefByID(fieldID.replace("_metrics_", ""));
      if (!sourceFieldDef) return;
      if (sourceFieldDef != null) {
        sourceFieldDef.SetPlanningFieldDef(newField);
      }

      newField.SetReadOnly(true);
      newField.SetCalcCode(enumCalcCode.static);

      objClass.ForceUniqueID(newField);
      objClass.AddField(newField);
      return newField;
    }
  }

  // Registers a standalone read-only static field def in objClass without linking it to a
  // source field via SetPlanningFieldDef. Used to register _metrics_ field defs in the position
  // class for employee-type formulas so that ProcessObject can map the server JSON value into
  // fRootDBObj on position charts.
  addStandaloneMetricsFieldDef(fieldName, objClass) {
    if (objClass.GetFieldDefByName(fieldName)) return;
    var fieldID = GetFieldId(fieldName);
    var localizedName = Globals._svrChartMgr.Localize("profile", fieldName);
    var newField = new QObjDBFieldDef();
    newField.InitField(fieldID, fieldName, objClass);
    newField.SetFieldLabel(localizedName);
    newField.SetReadOnly(true);
    newField.SetCalcCode(enumCalcCode.static);
    objClass.ForceUniqueID(newField);
    objClass.AddField(newField);
    return newField;
  }

  AddScriptFormulas(scriptFormulas, aClass) {
    if (scriptFormulas != null) {
      for (var aScriptFormulaIdx in scriptFormulas) {
        var thisFormula = scriptFormulas[aScriptFormulaIdx];
        if (!thisFormula["formulaName"]) continue;

        var fieldName = thisFormula["formulaName"];
        if (thisFormula["formulaID"]) var fieldID = thisFormula["formulaID"].replaceAll(" ", "");
        else fieldID = thisFormula["formulaName"].replaceAll(" ", "");
        if (aClass.GetFieldDefByID(fieldID) != null) {
          console.log("Conflict - fieldID already exists " + fieldID);
          continue; // conflict here. fieldID already exists
        }

        var scriptField = new QObjDBFieldDef();
        scriptField.SetCalcCode(enumCalcCode.customcalc);
        scriptField.InitField(fieldID, fieldName, aClass);

        var theLocalizedName = Globals._svrChartMgr.Localize(aClass.GetClassName(), fieldName);
        scriptField.SetFieldLabel(theLocalizedName);

        var calcCacheformula = new QFormulaRule();

        calcCacheformula.InitFormulaRuleFromDB(thisFormula);
        scriptField.SetFormulaRule(calcCacheformula);

        aClass.AddField(scriptField);
      }
    }
  }

  // Aggregated Fields are not included in chart headers.
  // Need to add them manually.
  BuildAggregatedFieldDefs(
    aggregatedFields,
    aClass // holder class most likely
  ) {
    if (aggregatedFields == null) return;

    for (var idx in aggregatedFields) {
      var aTable = aggregatedFields[idx];
      var tableName = aTable["name"];
      var fieldID = tableName.replaceAll(" ", "");

      var tableDef = new QObjDBFieldDef();
      tableDef.SetCalcCode(enumCalcCode.aggregatedField);
      tableDef.InitField(fieldID, tableName, aClass);
      tableDef.SetReadOnly(true);
      tableDef.SetTableRef(aTable);
      aClass.AddField(tableDef);
    }
  }

  FormatDynFields(aClass, metadata) {
    var linkTypes = metadata["links"];

    var fieldFormats = metadata["fieldFormats"];

    var orgInfo = this.GetDocMgr().fWSMgr.GetOrgInfo();
    var curDateFmt = orgInfo.GetDateFormat();

    // localization.profile stores per-language custom display labels set via Fields & Formulas.
    // This is decoupled from localizationConfig (data field name mapping for multi-language
    // data sources)
    var curLangCode = Globals._stringMgr.fCurLangStr;

    for (var aFieldIdx in aClass.fFieldList) {
      var aField = aClass.fFieldList[aFieldIdx];
      var theName = aField.GetFieldName();

      var theLocalizedName = Globals._svrChartMgr.Localize(aClass.GetClassName(), theName);
      var customLabel = metadata["localization"]?.profile?.[theName]?.[curLangCode] || null;

      // _metrics_-prefixed field defs are not in fieldFormats (which uses plain names).
      // Fall back to the plain name so they inherit the same type and decimal formatting.
      var lookupName = theName;
      if (fieldFormats && !fieldFormats[lookupName] && theName.startsWith("_metrics_")) {
        lookupName = theName.slice("_metrics_".length);
      }

      if (fieldFormats && fieldFormats[lookupName]) {
        var theFormat = fieldFormats[lookupName];
        if (theFormat["type"]) aField.SetFieldType(enumFieldType[theFormat["type"].toLowerCase()]);
        if (theFormat["numDecimals"] != null)
          aField.SetNumDecPlaces(parseInt(theFormat["numDecimals"]));
        aField.SetShowThousandsSep(theFormat["thousandsep"] !== false);
        if (theFormat["breakChar"]) aField.SetBreakChars(theFormat["breakChar"]);
        if (theFormat["prefix"]) aField.SetPrefix(theFormat["prefix"]);
        if (theFormat["postfix"]) aField.SetPostfix(theFormat["postfix"]);
        if (theFormat["percentage"]) aField.SetShowPercent(theFormat["percentage"]);
        if (theFormat["convertlocal"]) aField.SetShowLocal(theFormat["convertlocal"]);
        if (aField.GetFieldType() === enumFieldType.date) aField.SetDateFormat(curDateFmt);
        // pickListOptions will be an object nested within a field format.
        // the main controller for picklist behavior here is the toggle state of isPicklist
        // a field can hold picklist info without behaving as a picklist field
        if (theFormat["pickListOptions"]) {
          var pickFormat = theFormat["pickListOptions"];
          if (pickFormat["isPicklist"]) aField.SetFieldSubtype(enumCellObjectSubtypes.picklist);
          aField.SetPickType(pickFormat["picklistType"]);
          aField.SetPickValues(pickFormat["pickValues"]);
        }

        if (theFormat["listOptions"]) {
          var listFormat = theFormat["listOptions"];
          if (listFormat["enabled"]) aField.SetFieldSubtype(enumCellObjectSubtypes.list);
          aField.SetListDelimiter(listFormat["delimiter"]);
        }

        if (customLabel) aField.SetFieldLabel(customLabel);
      } else {
        //Try to autodetect field type.
        if (customLabel) aField.SetFieldLabel(customLabel);
        else if (theLocalizedName !== theName) aField.SetFieldLabel(theLocalizedName);
        if (theName.toLowerCase().indexOf("photo") > -1) aField.SetFieldType(enumFieldType.image);
        else if (isDateFieldName(theName)) {
          aField.SetFieldType(enumFieldType.date);
          aField.SetDateFormat(curDateFmt);
        }
      }
      if (linkTypes != null) {
        //TODO: This is problematic- we are using an object structure for this so we can't have multiple phones, skypes. Need to switch to fieldformats.
        if (linkTypes["Phone"] == theName) {
          aField.SetHyperlinkType(enumHyperLinkType.phone);
        } else if (linkTypes["Skype"] == theName) {
          aField.SetHyperlinkType(enumHyperLinkType.skype);
          aField.SetBreakChars("@");
        } else if (linkTypes["Hyperlink"] == theName) {
          aField.SetHyperlinkType(enumHyperLinkType.hyperlink);
          //aField.SetBreakChars('');
        } else if (linkTypes["File"] == theName) {
          aField.SetHyperlinkType(enumHyperLinkType.file);
          //aField.SetBreakChars('');
        } else if (linkTypes["Email"] == theName) {
          aField.SetHyperlinkType(enumHyperLinkType.email);
          aField.SetBreakChars("@");
        }
        //TODO: add a 'person' link?
      }
      // TODO: a bit lazy here.
      if (aField.GetCalcCode() === enumCalcCode.aggregatedField) {
        aField.SetFieldType(enumFieldType.object);
        aField.SetFieldSubtype(enumCellObjectSubtypes.enumeratedtable);
      }
    }
  }

  PopDBFromJSON(response, wsMgr) {
    //Need to isolate to JUST DB vs Metadata
    var data = null;
    var info = null;
    var positions = null;
    try {
      data = response["chartData"];
      info = response["info"];
      positions = response["positions"];
      if (!data || !info) throw "nodata";
      //if (this.IsServerChart())
      //Only if we're given the subcharts from the BE
      //this.FindTargetSubChartID(data, info);
    } catch (e) {
      return appcstr("dynloaderr1"); // Bad JSON
    }

    var results = data;
    var numRecords = results.length;

    if (numRecords == 0) return appcstr("dynloaderr2"); // No records

    //this.InitializeDocFields(response);  // gateway for initializing fieldDefs based on chart metadata contained in response

    var o = this.GetObjDB();
    var boxClass = o.GetClassByName("Box");

    boxClass.LoadDynObjects(results);
    // wire ref to container
    if (this.GetHierarchyType() === enumHierarchyTypes.positions) {
      var positionClass = o.GetClassByName("Position");
      positionClass.LoadDynPositions(positions, boxClass);
    }

    return "success";
  }

  //=================================================================
  // Sync chart fields (Background Editor)
  //
  // Takes same response as input that is used from mongo query build chart from json pipeline (load dyn fields)
  SyncChartFieldsToDoc(metadata) {
    var docMgr = this.GetDocMgr();
    var wsMgr = docMgr.fWSMgr;

    // remove all doc fields and resets doc fields to default fields baseline
    this.ResetDocDefaultFields();

    // adds doc fields from chart metadata (fields, formulas, etc)
    this.InitializeDocFields(metadata);

    // updates page nodes with new field def obj references (removes if they no longer exist)
    this.UpdateFieldReferences();

    // resets layout, canvas recalc, doc dirty, clear undo redo
    wsMgr.DocFieldsChanged();
  }

  ResetDocDefaultFields() {
    var keepFields = [
      "ID",
      "Locked",
      "Persist",
      "Orphan",
      "Top Of SubChart",
      "Level",
      "SubChart Level",
      "ReportsToName",
      "SuperIDLookup",
      "Allocation Ratio",
      // Server-computed count fields — not in chart schema headers so must be preserved here
      kHeadcount,
      "Subordinates",
      "Span Of Control",
      "Peers"
    ];
    var o = this.GetObjDB();
    var aClass = o.GetClassByName("Box");
    var m = aClass.GetFieldCount();

    var fieldDefList = aClass.fFieldList;
    var removeList = fieldDefList.map(function (item) {
      return item;
    });
    for (var j = 0; j < m; j++) {
      var targetField = removeList[j];
      var checkID = targetField.GetFieldID();
      var checkName = targetField.GetFieldName();
      if (keepFields.includes(checkID) || keepFields.includes(checkName)) continue;
      this.RemoveField(removeList[j]);
    }
  }

  // Called directly by loaddynfields in build chart from json and also used by sync doc fields pipeline (Background Editor)
  InitializeDocFields(metadata) {
    var fieldList = metadata["headers"];

    if (!fieldList?.length) return;

    var o = this.GetObjDB();
    var aClass = o.GetClassByName(enumObjClassNames.box);

    this.SetupHierarchyType(metadata);
    this.SetHeaderList(fieldList);
    this.AddDynFields(o, fieldList, metadata);

    if (IsDBChart()) {
      var classNames = [enumObjClassNames.box];
      if (this.IsPositionDocument()) classNames.push(enumObjClassNames.position);
      for (const i in classNames) {
        var objClass = o.GetClassByName(classNames[i]);
        this.HandleInternalFields(objClass, metadata);
      }
    }

    this.AddScriptFormulas(metadata["scriptFormulas"], aClass);
    this.FormatDynFields(aClass, metadata);
    this.SetupNineBox(aClass);
    this.SetupART(aClass, metadata);
    this.BuildAggregatedFieldDefs(metadata["aggregatedFields"], aClass);
    this.AssignFieldIDs(metadata["fields"]);

    // Handle position-specific fields
    if (this.IsPositionDocument()) {
      var positionClass = o.GetClassByName(enumObjClassNames.position);
      if (positionClass) {
        this.LocalizeBuiltInPositionFields(positionClass, metadata);
        this.SetupAllocationRatio(positionClass);
        this.FormatDynFields(positionClass, metadata);
      }
      var boxClass = o.GetClassByName(enumObjClassNames.box);
      if (boxClass) {
        this.LocalizeBuiltInPositionFields(boxClass, metadata);
        this.FormatDynFields(boxClass, metadata);
      }
    }
  }

  SetupHierarchyType(metadata) {
    if (metadata?.positionChart?.enabled) {
      this.SetHierarchyType(enumHierarchyTypes.positions);

      const wsMgr = this.GetDocMgr()?.fWSMgr;
      const objDb = this.GetObjDB();

      const boxClass = objDb.GetClassByName("Box");
      const boxObjMgr = boxClass.GetObjMgr();

      const positionClass = new QObjDBClass();
      positionClass.InitClass("Position", false, wsMgr, false, this.fObjDB);

      const positionObjMgr = positionClass.GetObjMgr();
      positionObjMgr.fFilterMgr = boxObjMgr.fFilterMgr;
      this.fObjDB.AddClass(positionClass);
    } else {
      this.SetHierarchyType(enumHierarchyTypes.employees);
    }
  }

  HighlightDataToRule(fieldName, fieldValue, hexColor) {
    const rulePair = {
      fieldName: fieldName,
      fieldID: this.GetFieldID(fieldName),
      operator: "equals",
      value: fieldValue
    };

    const newRule = new QChartTemplateRule();
    newRule.InitChartTemplateRule();
    newRule.LoadFromJSON({
      type: "Basic Rule",
      logicalop: "",
      pairs: [rulePair]
    });
    newRule.AddRuleAction(kBoxOverlayRule);
    newRule.fActionSet[kBoxOverlayRule] = {
      fTriFlagEnabled: true,
      fTriColor1: hexColor,
      fTriColor3: null
    };

    return newRule;
  }

  AddHighlightBoxFlags(highlightData) {
    const chartRules = this.GetSelfChartRules();
    highlightData.forEach(highlight => {
      const { dataField: fieldName, dataValue: fieldValue, colorCode } = highlight;
      if (!fieldName || !fieldValue || !colorCode) {
        console.log("Highlight missing values: ", highlight); //EY Troubleshooting
        return;
      }
      const [r, g, b] = parseRGB(colorCode);
      const hexColor = ConvertRGBToHex(parseInt(r), parseInt(g), parseInt(b));
      const newRule = this.HighlightDataToRule(fieldName, fieldValue, hexColor);
      chartRules.AddRule(newRule);
    });
  }

  AddSupplementalOptions(importData) {
    var supplementalMetadata = importData["supplementalOptions"];
    if (!supplementalMetadata) return false;

    // update fields
    this.ResetDocDefaultFields();
    this.InitializeDocFields(supplementalMetadata);

    // update profile
    var profileMgr = this.GetProfileMgr();
    var profile = supplementalMetadata["profile"];
    profileMgr.LoadProfile(this, profile);

    var docMgr = this.fDocMgr;
    var iWizData = this.GetIWizData();

    //Disregard whatever topbox existed in the base chart metadata
    iWizData.SetSpecifyTopBox(false);
    iWizData.SetTopBoxIDs(null);

    // Hack to force template recalc
    let activeView = iWizData.GetChartTemplateID();
    var theTemplate = docMgr.fWSMgr
      .GetChartTemplatesMgr()
      .FindTemplateByIDAllSets(activeView, false);
    if (theTemplate?.success) {
      if (supplementalMetadata?.highlights)
        this.AddHighlightBoxFlags(supplementalMetadata.highlights);
      this.SetActiveTemplate(theTemplate["template"]);
    }
    return true;
  }

  LoadDynChart(importData, wsMgr) {
    //TODO: This function does both initial metadata/document setup + database load. Need to trim it down to just the database
    this.AddSupplementalOptions(importData);

    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");

    this.fPredefFieldCount = aClass.GetFieldCount();

    ReportTiming("Starting Database Load");
    // TODO add supplemental headers here
    var result = this.PopDBFromJSON(importData, wsMgr);
    ReportTiming("Database Loaded");

    var refreshInfo = wsMgr.fDataRefreshInfo;
    let priorDoc = null;
    if (refreshInfo) {
      priorDoc = refreshInfo.GetPriorDocMgr().GetCurrentDoc();
    }
    this.CopyAndApplyOverrides(priorDoc);

    //TODO: This next part should be its own step!!

    //Does this need to exist? Why the explicit cleanup here? The memory should be released automatically.

    if (result != "success") this.UnwindImport();
    else {
      let singleSubchart = false; //The chart builder configuration will determine page breaks

      let hierState = true;

      let filterObj = null;

      let iwizData = this.GetIWizData(enumIWizDataSet.refresh);
      if (iwizData.GetFilterData()) {
        filterObj = iwizData.GetFilterRule();
      }
      let topBoxIDs = iwizData.GetForceTopBoxID() ?? iwizData.GetTopBoxIDs();

      let mapping = iwizData.fMapping;

      result = this.WireHierarchy(mapping, filterObj, hierState, singleSubchart, topBoxIDs);

      // Only remap to a filter error for results that mean the filter reduced the
      // dataset to nothing. Other WireHierarchy failures (account-limit, circular-
      // reference, missing-field, too-many-records, etc.) surface with their own
      // messages and must not be overridden.
      const EMPTY_HIERARCHY_ERRORS = ["noMatchingRecords"];
      if (filterObj != null && EMPTY_HIERARCHY_ERRORS.includes(result)) {
        result = this.IsCustomChart() ? "chartScopeFilterError" : "importFilterError";
      }

      //TODO: Move this all over to the load metadata stage...
      /*
        var docMgr = this.GetDocMgr();

        //If we keep this, we have to ask the chart metadata at this time what these should be.
        if (importData['data']['placeHolders'])
        {
            var placeHolderParams = importData['data']['placeHolders'];
            if (placeHolderParams['doPlaceHolder']){
                var pHolderFunction = placeHolderParams['placeHolderFunction'];
                var pHolderSupFunction = placeHolderParams['placeHolderSupFunction'];
                if (pHolderFunction != null)
                this.HandlePlaceHolderChart(pHolderFunction, pHolderSupFunction);

            }
        }

         */
      /*
        //TODO: This all should move.
        this.GetProfileMgr().AddKeyFields(this, importData['data']['profile']);
        var curDoc = docMgr.GetCurrentDoc();
        var docPrefs = curDoc.GetDocPrefs();
        var pageOpt = docPrefs.GetPageOpt(false);
        var builderPageSize = importData['data']['options']['page'];
        if (builderPageSize == null)
        {
            var pageSizeOption =  localStorage.pageSize;
            if (pageSizeOption)
                builderPageSize = enumCorePageSize[pageSizeOption.toLowerCase()];
        }

        switch (builderPageSize)
        {
            case 1:
                pageSizeOption = "Legal";
                break;
            case 2:
                pageSizeOption = "Tabloid";
                break;
            case 3:
                pageSizeOption = 'A0';
                break;
            case 4:
                pageSizeOption = 'A1';
                break;
            case 5:
                pageSizeOption = 'A2';
                break;
            case 6:
                pageSizeOption = 'A3';
                break;
            case 7:
                pageSizeOption = 'A4';
                break;
            default:
                pageSizeOption = "Letter";
                break;
        }

        var defPageSize = enumCorePageSize[pageSizeOption.toLowerCase()];
        var resetPageSize = defPageSize == null;

        if (!resetPageSize)
        {
            var aspectIndex = 6; // Letter to 1.6
            switch (defPageSize)
            {
                case enumCorePageSize.legal: // Legal goes to 2.0
                    aspectIndex += 1;
                    break;
                case enumCorePageSize.tabloid: // Tabloid goes to 2.5
                    aspectIndex += 2;
                    break;
                case enumCorePageSize.a0:  // Euro to 0.8
                case enumCorePageSize.a1:
                case enumCorePageSize.a2:
                case enumCorePageSize.a3:
                case enumCorePageSize.a4:
                    aspectIndex -=3;
                    break;

            }
            pageOpt.SetAspectRatio(aspectIndex);
            var pm = curDoc.GetPageMgr();
            pm.SetMinPageSizeFromCore(defPageSize);
        }

         */
    }

    ReportTiming("LoadDynChart Done");
    return result;
  }

  StaticChartQueryResult(success, chartData) {
    if (success) {
      var wsMgr = this.GetDocMgr().fWSMgr;
      const result = this.LoadDynChart(chartData, wsMgr);
      if (result !== "success") {
        var alert = new QAlert();
        const alertMsg =
          result === "importFilterError"
            ? appstr("importfiltercharterr")
            : result === "chartScopeFilterError"
              ? appstr("chartScopeFilterCharterr")
              : result;
        alert.InitAlert(appstr("unablebuildhier"), alertMsg, wsMgr, wsMgr, true);
        wsMgr.fDataRefreshInfo = null;
        wsMgr.fChartLoadPending = false;
        wsMgr.EndProgress();

        if (wsMgr.fPriorDocMgr) {
          wsMgr.RestoreDocMgr(wsMgr.fPriorDocMgr);
        }
        return false;
      }

      this.fSubChartMgr.fSubChartList = [];
      wsMgr.FinishImport(this);
      return true;
    }
    return false;
  }

  WireSubChartList(subChartData) {
    //Metadata should exist (for whatever you need) at this point
    var scm = this.GetSubChartMgr();
    var metadata = this.GetIWizData();
    return scm.WireSubChartList(metadata, subChartData);
  }

  /**
   * @param {Nullish<string>} topBoxId
   * @param {Nullish<boolean>} isAux
   * @param {Nullish<boolean>} storeInteraction
   * @param {Nullish<boolean>} ignoreFilters
   *
   * @returns {Promise<any>}
   */
  DoChartDataQuery(topBoxId = null, isAux = null, storeInteraction = false, ignoreFilters = false) {
    //TODO: weird overlap with LoadServerChartData
    //Grab a single subchart
    var wsMgr = this.GetDocMgr().fWSMgr;
    var requestedLevels = this.GetRequestedLevels();

    //TODO: Stamp treeID on the document
    var attachSubcharts = this.IsServerChart() && this.GetSubChartMgr().GetSCNodeCount() === 0;
    var querySettings = {
      isAux: isAux,
      treeID: this.fTreeID,
      chainOfCommand: this.GetRequestedChainLevels(),
      isPlan: this.GetDocMgr().IsPlanChart(),
      singlePage: this.IsServerChart(),
      attachSubcharts: attachSubcharts,
      storeInteraction: storeInteraction
    };

    if (this.GetDocMgr().GetIWizDataInteractive().GetDataSource() === enumDataSource.pointintime) {
      querySettings["doctype"] = "Archive";
    }

    if (this.GetDocMgr().IsPlanChart()) {
      var planMgr = wsMgr.GetPlanMgr();
      querySettings["isPlan"] = true;
      querySettings["planID"] = planMgr.fPlanInfo.ID;
      if (!Globals._publishRef) {
        querySettings["attachSubcharts"] = true;
      }
    }

    var filterMgr = this.GetFilterMgr();
    if (
      !ignoreFilters &&
      filterMgr.HasActiveFilter() &&
      filterMgr.GetFilterMode() === enumFilterModes.remove
    ) {
      querySettings["filter"] = filterMgr.Marshall();
    }

    var accountName = wsMgr.GetAccountName();
    var user = wsMgr.GetUserName();

    if (Globals._uiConfig.GetIsSelfLaunch()) {
      topBoxId = "__self__";
      Globals._uiConfig.SetIsSelfLaunch(false);
    }

    if (topBoxId == null) {
      var chartMetadata = this.GetDocMgr().fChartMetadata;
      var isBuilderChart =
        chartMetadata?.GetDataSource() === enumDataSource.chartbuilder ||
        chartMetadata?.GetDataSource() === enumDataSource.pointintime;
      var topBoxIdList = chartMetadata?.GetTopBoxIDs() ?? null;
      if (isBuilderChart) {
        if (topBoxIdList) topBoxId = topBoxIdList[0];
      }
      if (topBoxId == null) topBoxId = Globals._accountInfo.GetTopBoxID();
      if (topBoxId == null) {
        if (topBoxIdList) topBoxId = topBoxIdList[0];
      }
    }

    querySettings["breadCrumbs"] = Globals._uiConfig.GetBreadCrumbsEnabled();
    querySettings = JSON.stringify(querySettings);

    var chartID = this.GetChartID();
    var levels = requestedLevels;

    var svrChartQuery = new QSvrChartQuery();
    const svrChartQueryPromise = svrChartQuery.GetPromise();
    svrChartQuery.InitServerChartQuery(this);
    svrChartQuery.SetParams(accountName, user, topBoxId, chartID, levels, querySettings);

    Globals._rfcMgr.QueueRFC(svrChartQuery);

    return svrChartQueryPromise;
  }

  SvrChartQueryResult(success, response) {
    var info = response["info"];
    var docMgr = this.GetDocMgr();
    var wsMgr = docMgr.fWSMgr;
    if (success) {
      var curTreeID = this.fTreeID;
      var newTreeID = info["treeID"];
      if (curTreeID != null && newTreeID != curTreeID) {
        //TODO: In the future, we may want to lean into the multi-chart functionality, but it
        //also look like
        var objDB = this.GetObjDB();
        var scm = this.GetSubChartMgr();
        objDB.PurgeData();
        scm.PurgeData();
      }

      this.fTreeID = newTreeID;
      var chartData = response["chartData"];
      var baseData = response["baseData"];
      var positions = response["positions"];

      var basePositions = response["basePositions"];
      if (response["hierarchyData"] != null && response["hierarchyData"].length > 0) {
        if (docMgr.IsPlanChart() && Globals._publishRef) {
          // do nothing
        } else {
          var iWiz = this.GetIWizData(enumIWizDataSet.refresh);
          var scm = this.GetSubChartMgr();
          scm.WireSubChartList(iWiz, response["hierarchyData"]);
          scm.PurgeLoadCache();
          var o = this.GetObjDB();
          var aClass = o.GetClassByName("Box");
          aClass.GetObjMgr().PurgeData();
          var posClass = o.GetClassByName("Position");
          if (posClass) posClass.GetObjMgr().PurgeData();
        }
      }

      var topbox = info["topbox"];
      var subchart = this.LoadChartDataPage(topbox, chartData, positions, baseData, basePositions);
      if (docMgr.IsPlanChart()) {
        var planMgr = wsMgr.GetPlanMgr();
        var planMetaData = response["planMetaData"];
        //planMgr.HandleNavPrompt(response, info, wsMgr)
        planMgr.UpdatePlanState(planMetaData);
        subchart.fPlanChecksum = planMetaData["hashes"]["overridemd5"];
      }
      // fLoadedLevels has to be stamped before SubChartSwitch, because
      // SubChartDataLoaded reads it to decide on the cached path. That means the level
      // change is committed before the render that may still refuse it, so keep the
      // previous values around as the rollback point. QCanvasPanel.PRefusePage
      // restores them; a successful SwitchToSubChart clears them.
      subchart.fPrevLoadedLevels = subchart.fLoadedLevels;
      subchart.fPrevChainLoaded = subchart.fChainLoaded;
      subchart.fLoadedLevels = info["levels"];
      subchart.fChainLoaded = info["chainLevels"];

      if (response["disconnectedBranches"]) {
        this.GetDocMgr().AddDisconnectedBranches(response["disconnectedBranches"]);
      }

      if (
        subchart.fLoadedLevels != this.GetRequestedLevels() ||
        subchart.fChainLoaded != this.GetRequestedChainLevels()
      ) {
        console.log("PROBLEM - infinite loop recursion fix");
        return;
      }

      this.SubChartSwitch(subchart);

      wsMgr.ApplyBackground();

      wsMgr.BroadcastTopic("chartDocumentLoaded", this);
      wsMgr.SignalDocChanged();

      // Trigger publish URL action (?action=publish_PDF^Book) for live server charts.
      // Static chart loads hit this via finishstddownload/FinishImport, but live
      // server charts finalize here without invoking HandleDocAction. Scope is
      // intentionally narrow — only publish actions fire the signal; refresh/
      // savedoc/signoff retain prior behavior to avoid surprise side effects.
      // Peek fURLAction[0] so SignalDocumentLoadComplete can still shift it.
      var pendingAction = Globals._accountInfo?.fURLAction?.[0];
      if (typeof pendingAction === "string" && pendingAction.startsWith("publish_")) {
        wsMgr.SignalDocumentLoadComplete();
      }
    } else wsMgr.QueryFailAlert(response);

    wsMgr.fDataRefreshInfo = null;
    wsMgr.PrepRenderWS();
    wsMgr.RenderWS();
  }

  GetDuplicateRecords(records, recordIDKey, recordIDIndex) {
    return records
      .filter(function (a) {
        return recordIDIndex.FindObject(a[recordIDKey]) != null;
      })
      .map(function (a) {
        return { ID: a[recordIDKey], SCFlag: a["scFlag"], objType: a["objType"] ?? null };
      });
  }

  LoadChartDataPage(topbox, records, positions, baseData, basePositions) {
    //formerly loaddynsubchart.
    var doc = this;
    var wsMgr = this.GetDocMgr().fWSMgr;
    var o = doc.GetObjDB();
    var boxClass = o.GetClassByName("Box");
    var scm = this.GetSubChartMgr();
    var bsm = wsMgr.GetBoxStylesMgr();
    var layoutMgr = this.GetLayoutMgr();

    if (this.IsPlanChart()) {
      // || this.IsManualChart()
      let planMgr = wsMgr.GetPlanMgr();
      planMgr.HandleLoadRecordResponse(true, baseData, basePositions);
    }

    var recordIdDef = scm.GetRecordIDField(boxClass);
    var recordIDIndex = new QIndex();
    recordIDIndex.InitIndex(boxClass, recordIdDef, true, false);
    var isPosChart = doc.IsPositionDocument();

    var recordIdKey = recordIdDef.GetFieldName();

    var duplicates = this.GetDuplicateRecords(records, recordIdKey, recordIDIndex);

    records = records.filter(function (a) {
      return recordIDIndex.FindObject(a[recordIdKey]) == null;
    });

    var objList = boxClass.LoadDynObjects(records);

    let posObjList = [];
    let posDuplicates = [];
    let positionIDIndex = null;

    if (isPosChart) {
      var positionClass = o.GetClassByName("Position");
      var posIDDef = positionClass.GetRecordIDFieldDef();
      var posRecordIDKey = posIDDef.GetFieldName();
      positionIDIndex = new QIndex();
      positionIDIndex.InitIndex(positionClass, posIDDef, true, false);
      posDuplicates = this.GetDuplicateRecords(positions, posIDDef.GetFieldName(), positionIDIndex);
      positions = positions.filter(function (a) {
        return positionIDIndex.FindObject(a[posRecordIDKey]) == null;
      });
      posObjList = positionClass.LoadDynPositions(positions, boxClass);
      positionIDIndex.InitIndex(positionClass, posIDDef, true, false);
      positionClass.WireObjects(posObjList, positionIDIndex);
    }

    //Will we need this, or is the BE handling the mutation?
    //var overrideClass = o.GetClassByName("Override");
    //objList = overrideClass.GetAssociatedObjects(objList, 2);

    //Wire heirarchy
    //TODO: NEED to switch to using subchart IDs for this!
    var subchart = doc.SearchSubChartIDMatch(topbox);

    // If topbox doesn't match, try other cohead IDs
    if (subchart == null) {
      var docmgr = this.GetDocMgr();
      var iwiz = docmgr.GetIWizDataInteractive();
      var coheadIDs = (iwiz && iwiz.IsCoHeadChart() && iwiz.GetTopBoxIDs()) || [];

      for (var i = 0; i < coheadIDs.length && subchart == null; i++) {
        if (coheadIDs[i] !== topbox) {
          subchart = doc.SearchSubChartIDMatch(coheadIDs[i]);
        }
      }

      subchart = subchart || scm.GetRootSubChart();
    }

    //TODO: The section above is the wrong workflow!

    //Assign Subchart top ref properly
    recordIDIndex.InitIndex(boxClass, recordIdDef, true, false);
    scm.ResolveTopBox(subchart.GetTopDBObjRef(), subchart);

    if (!isPosChart) boxClass.WireObjects(objList, recordIDIndex);

    //Generate default layout to assign to new boxes.
    var baselayout = wsMgr.GetDefaultLayout();
    baselayout.SetDocRef(this);
    var bsmgr = wsMgr.GetBoxStylesMgr();
    var baseMRBLayout = bsmgr.GetLayoutByID(400, false, enumBoxStyleSearch.allSets);
    baseMRBLayout.ClearAssociatedDBObjs();
    var scid = subchart.GetSubChartID();

    var docmgr = this.GetDocMgr();
    var iwiz = docmgr.GetIWizDataInteractive();
    //Handle Coheads/comanagers of this page
    var topIDs = [];
    //TODO: This only supports coheads at this time.
    if (iwiz.IsCoHeadChart()) topIDs = iwiz.GetTopBoxIDs();

    scm.RemoveSCNodes(scid, boxClass, subchart, o);

    var coheadNodes = scm.GeneratePageSCNodes(
      [...objList, ...posObjList],
      bsm,
      baselayout,
      doc,
      scid,
      subchart,
      topIDs,
      baseMRBLayout
    );

    scm.CorrectDupLayouts(
      duplicates,
      recordIDIndex,
      bsm,
      doc,
      subchart,
      topIDs,
      coheadNodes,
      baselayout
    );

    if (isPosChart) {
      scm.CorrectDupLayouts(
        posDuplicates,
        positionIDIndex,
        bsm,
        doc,
        subchart,
        topIDs,
        coheadNodes,
        baseMRBLayout
      );
    }

    //Make sure topboxes are grouped if they are coheads/comanagers. No mechanism for doing this to comanagers exists yet, but soon(tm).
    if (coheadNodes.length > 0) {
      var docMgr = this.GetDocMgr();
      var wsMgr = docMgr.fWSMgr;
      var bsmgr = wsMgr.GetBoxStylesMgr();
      //Holy hardcoded batman! Doesn't seem like a better way to do this at this time.
      var coHeadLayout = bsmgr.GetLayoutByID("302", false, enumBoxStyleSearch.allSets);
      coHeadLayout.ClearAssociatedDBObjs();
      coHeadLayout = bsmgr.CloneLayout(coHeadLayout, doc);
      var theNewNode = doc.GroupNodes(coheadNodes, false, coHeadLayout);
    }
    // sc had been loaded at this point, time to group holders.
    if (doc.GetHierarchyType() === enumHierarchyTypes.positions) {
      doc.HandlePositionsGroup(scid);
    }

    var parentList = scm.GetParentListForChart();
    var subchartRules = doc.GetDocMgr().GetIWizDataInteractive();
    var sortConfig = subchartRules.GetSortCriteria();
    if (sortConfig != null) {
      sortConfig = ValidateSortConfig(sortConfig, doc);
      doc.PerformSort(sortConfig, parentList);
      var docPrefs = doc.GetDocPrefs();
      var pageOpt = docPrefs.GetPageOpt(false);
      pageOpt.SetAllowSort(false);
    }
    subchart.SetNeedOptimize(true);

    Globals._svrChartMgr.SetTargetSCID(subchart.GetSubChartID());

    doc.ApplyChartFilters(false, true);

    layoutMgr.ApplyOverrides();
    return subchart;
  }

  async SubChartSwitch(subChart) {
    //Request for switch subchart comes in. If we have the data cached, we switch. Otherwise, we need to call for page load
    var subchartDataLoaded = this.SubChartDataLoaded(subChart);
    if (!subchartDataLoaded) {
      var subRef = subChart.GetTopDBObjRef();
      const response = await this.DoChartDataQuery(subRef); // isAux? dbObj not loaded. no reference to isAux val.
      const result = JSON.parse(response);
      return result["success"] === true;
    } else {
      return this.fCanvasRef.SwitchToSubChart(subChart.GetSubChartID());
    }
  }

  SubChartDataLoaded(subChart) {
    if (this.IsPlanChart() && this.IsLiveChart()) {
      var planMgr = this.fDocMgr.fWSMgr.GetPlanMgr();
      if (subChart.fPlanChecksum !== planMgr.GetOverrideHash()) return false;
    }
    return !(
      this.IsServerChart() &&
      (subChart.fLoadedLevels !== this.GetRequestedLevels() ||
        subChart.fChainLoaded !== this.GetRequestedChainLevels())
    );
  }

  SubChartDrill(theBox) {
    //Input: A box
    //Output: the subchart in question to drill (pass to subchart switch)
    //Shouldn't this just be rescoped to the document or canvas level?
    var subChart = null;
    var scm = this.GetSubChartMgr();
    if (this.IsServerChart()) {
      //TODO: Has to be an easier way to get this
      //TODO: Significant overalp with chartbox.drillsubchartid
      var objDB = this.GetObjDB();
      var boxClass = objDB.GetClassByName("Box");
      var recordIDDef = boxClass.GetRecordIDFieldDef(true);
      var scFlag = theBox.GetSubChartFlag();
      var isAux = 0;
      var objID;

      if (scFlag == enumSubChartFlag.drillup) {
        var sc = scm.GetCurrentSubChart();
        var pSc = sc.GetSubChartParent();
        if (!pSc) return;
        objID = pSc.GetTopDBObjRef();
      } else {
        var boxLayout = theBox.GetLayout();
        // drilldown on a container
        if (
          boxLayout.GetContainerType() !== enumContainerType.nonmrb &&
          boxLayout.GetHierarchyType() !== enumHierarchyTypes.positions
        ) {
          // get dataObjs associated with this container
          var containerMembers = boxLayout.GetContainerDataObjs();
          // for positions charts, relevant hierarchy info is attached to the first holder of a position
          // qmongo.subChartQuery/mongoQuery recordCopy[fieldDict['PositionID']] += str(idx) was creating a invalid PositionID that
          // was *sometimes the first containerMember, causing query to fail. Sorting by positionID will prevent this.
          if (containerMembers.length > 1)
            containerMembers = containerMembers.sort((a, b) =>
              a.GetFieldValue(recordIDDef).localeCompare(b.GetFieldValue(recordIDDef))
            );
          var dbObj = containerMembers[0];
        } else dbObj = theBox.GetDataObj();

        objID = dbObj.GetObjectRecordID();
      }
      subChart = this.SearchSubChartIDMatch(objID);
    } else {
      var subChartID = theBox.DrillSubChartID();
      subChart = scm.FindSubChart(subChartID);
    }
    if (subChart) {
      this.SubChartSwitch(subChart);
      // Announce the new top record id so the React URL sync can mirror it
      // into `?topOfChart=`. The drill arrow does not flow through
      // `DoNavToBox`, so this is where the broadcast has to live for the
      // QXDoc-level drill path.
      var topRef = subChart.GetTopDBObjRef && subChart.GetTopDBObjRef();
      if (topRef != null) {
        var w = this.fDocMgr && this.fDocMgr.fWSMgr;
        if (w && w.BroadcastTopic) {
          w.BroadcastTopic("navToBoxApplied", { targetID: topRef });
        }
      }
    }
  }

  //=================================================================================================
  SearchSubChartIDMatch(topBoxID) {
    var scm = this.GetSubChartMgr();
    for (var i = 0; i < scm.GetNumSubCharts(); i++) {
      var scInfo = scm.fSubChartList[i];
      var objRef = scInfo.GetTopDBObjRef();
      if (objRef == topBoxID) {
        return scInfo;
      }
    }
    return null;
  }

  LookupFieldValue(r1, targetID, baseName, lookupFieldName) {
    var j = r1;
    //var iDFieldName = j["fields"].PersonID;
    // var superIDFieldName = j["fields"].SupervisorID;

    var chartData = j["chartdata"];
    var n = chartData.length;
    for (var i = 0; i < n; i++) {
      var row = chartData[i];
      var curID = row[baseName];
      if (curID == targetID) return row[lookupFieldName];
    }

    return null;
  }

  FindTargetSubChartID(chartMetadata, info) {
    var subchartInfo = chartMetadata["subcharts"];

    var rootSCInfo = subchartInfo[0];
    var rootTopBoxID = rootSCInfo["TopBoxRef"];
    //Two different aspects in question. The first is the root top box from the organization (which we extract here, although
    //we do it incorectly. The second is the one supplied from the BE. These are different if the first page you load
    //is not the top of the chart, which IS possible.
    var targetTopBoxID = info["topbox"];
    //var topBoxID = Globals._accountInfo.GetTopBoxID(); At this stage, any override to this was placed on the req to the BE.
    //Trust the BE response at this juncture.

    if (targetTopBoxID == null) {
      //?????
      targetTopBoxID = rootTopBoxID;
    }

    var svrChartSCID = null;
    for (var i in subchartInfo) {
      var scInfo = subchartInfo[i];
      var objRef = scInfo["TopBoxRef"];
      if (objRef == targetTopBoxID) {
        svrChartSCID = scInfo["scID"];
      }
    }
    if (svrChartSCID == null) console.log(":(");

    //Globals._accountInfo.SetTopBoxID(targetTopBoxID);       //Ugly, but needed too many places elsewhere. Leaving as-is for now.
    //Globals._svrChartMgr.SetTargetSCID(svrChartSCID);
    return svrChartSCID;
  }

  IsServerChart() {
    return this.fDocMgr.IsServerChart();
  }

  IsLiveChart() {
    return this.fDocMgr.IsLiveChart();
  }

  IsPlanChart() {
    return this.fDocMgr.IsPlanChart();
  }

  HasAgentField() {
    var boxClass = this.GetObjDB()?.GetClassByName("Box");
    return boxClass?.GetFieldDefByName("isAgent") != null;
  }

  IsOcApiChart() {
    const importConfig = this.GetIWizData()?.GetImport();
    if (!importConfig) return false;
    return [
      importConfig.source,
      importConfig.positions?.source,
      importConfig.holders?.source
    ].includes("ocAPI");
  }

  GetPhotoFieldDef() {
    return this.GetObjDB()?.GetClassByName("Box")?.GetFieldDefByID(kPhotoID);
  }

  ShowsPhotosButHasNone(boxList, photoDef) {
    const photoBoxes = boxList.filter(box => box.GetLayout()?.HasPhotoCell());
    if (photoBoxes.length === 0) return false;

    return !photoBoxes.some(
      box => String(box.GetDataObj()?.GetFieldValue(photoDef) ?? "").trim() !== ""
    );
  }

  FindAgentRecordID() {
    return new Promise(resolve => {
      var wsMgr = this.GetDocMgr().fWSMgr;
      var idField = this.GetObjDB()?.GetClassByName("Box")?.GetRecordIDFieldDef()?.GetFieldName();

      if (!idField) {
        resolve(null);
        return;
      }

      var query = { isAgent: { $inNormalized: [...kAgentMarkers] } };

      var svrPersonQuery = new QSvrPersonQuery();
      svrPersonQuery.InitServerPersonQuery({
        SvrQueryResult: (success, r) => {
          var results = success ? (r?.results ?? []) : [];
          var match = results.find(rec => rec?.[idField] != null);
          resolve(match ? match[idField] : null);
        }
      });
      svrPersonQuery.AddParameter("options", JSON.stringify({}));
      svrPersonQuery.SetParams(
        wsMgr.GetAccountName(),
        wsMgr.GetUserName(),
        query,
        null,
        this.GetChartID(),
        this.IsPositionDocument(),
        false
      );
      Globals._rfcMgr.QueueRFC(svrPersonQuery);
    });
  }

  IsCustomChart() {
    return !this.IsServerChart() && !this.IsPlanChart();
  }

  IsPreviewChart() {
    return this.fIsPreview === true;
  }

  IsBackground() {
    var docMgr = this.GetDocMgr();
    var source = docMgr.GetIWizDataInteractive().GetDataSource();
    return source == enumDataSource.background;
  }

  GetSelfChartRules() {
    let selfView = this.fSelfView;
    if (!selfView) {
      selfView = new QChartTemplate();
      selfView.SetTemplateName("This chart");
      selfView.SetTemplateLibrary(enumChartTemplateSet.chartselfview);
      selfView.InitTemplate();
      this.fSelfView = selfView;
    }

    return this.fSelfView;
  }

  GetProxyTemplate() {
    const activeTemplate = this.GetActiveTemplate();
    if (!activeTemplate) return null;

    // Clone the template before modifying the rule list.
    const proxyTemplate = activeTemplate.Clone();
    const selfRules = this.GetSelfChartRules();

    if (selfRules?.GetNumRules()) {
      proxyTemplate.fRuleList = [...proxyTemplate.fRuleList, ...selfRules.fRuleList];
    }

    // Deliberately not cloning/adding to cloning method, as root layout is intended to be a transient data structure.
    proxyTemplate.fRootLayout = activeTemplate.fRootLayout;

    return proxyTemplate;
  }

  InitXDoc(newDoc, classOnly, hierarchyType, docMgr, objDB, addDefaultProfile, wsMgr) {
    this.fObjDB = objDB;
    if (objDB == null) {
      this.fObjDB = new QObjDB();
      this.fObjDB.InitDB();
    }

    this.fObjDB.SetDocRef(this);
    this.fDocMgr = docMgr;

    if (newDoc) this.TempPopDB(classOnly);

    this.fHierarchyType = hierarchyType;

    this.fSubChartMgr = new QSubChartMgr();
    this.fSubChartMgr.InitSubChartMgr(this); //objDB

    this.fLayoutMgr = new QLayoutMgr();
    this.fLayoutMgr.InitLayoutMgr(this); //objDB

    this.fPictureMgr = new QPictureMgr();
    this.fPictureMgr.InitPictureMgr(wsMgr, this);

    this.fPageLayoutMgr = new QPageLayoutMgr();
    this.fPageLayoutMgr.InitPageLayoutMgr(this);
    this.fSubChartMgr.SetPageLayoutMgr(this.fPageLayoutMgr);

    if (addDefaultProfile) {
      var wasDirty = this.IsDocDirty();
      this.SetUpDefaultProfile("success");
      this.SetDirty(wasDirty);
    }

    this.fDocVersion = kDocVersion;
    this.SetXMLElementName("qxdoc");

    this.fAutoTextMgr = new QAutoTextMgr();
    this.fAutoTextMgr.InitAutoTextMgr(this);

    this.fShortcutMgr = new QShortcutMgr();
    this.fShortcutMgr.InitShortcutMgr(this);
  }

  GetHierarchyType() {
    return this.fHierarchyType;
  }

  SetHierarchyType(hierarchyType) {
    this.fHierarchyType = hierarchyType;
  }

  IsPositionDocument() {
    return this.fHierarchyType === enumHierarchyTypes.positions;
  }

  GetHierarchyClass() {
    const objDb = this.GetObjDB();
    const hierarchyType = this.GetHierarchyType();

    if (hierarchyType === enumHierarchyTypes.positions) {
      return objDb.GetClassByName(enumObjClassNames.position);
    }

    return objDb.GetClassByName(enumObjClassNames.box);
  }

  HandlePositionsGroup(scID) {
    var scm = this.GetSubChartMgr();
    if (scID == null) {
      scID = scm.GetActiveSubChart().GetSubChartID();
    }

    // grab reference to 'Employee PositionID' fieldDef
    const objDB = this.GetObjDB();
    const positionClass = objDB.GetClassByName(enumObjClassNames.position);
    const positionIDDef = positionClass.GetFieldDefByID("PositionID");

    // Opt 7: Single-pass node collection — one GetNodesForSubChart call + partition
    var allNodes = scm.GetNodesForSubChart(scID, false);
    var positionNodes = [];
    var holderNodes = [];
    for (var k = 0; k < allNodes.length; k++) {
      if (allNodes[k].IsPositionSCNode()) positionNodes.push(allNodes[k]);
      else if (allNodes[k].IsHolderSCNode()) holderNodes.push(allNodes[k]);
    }

    // Opt 9: Pre-build position object Map for O(1) lookups
    var positionObjMap = positionClass.BuildObjectMap(positionIDDef);

    this._processPositionGroup(
      scm,
      scID,
      positionNodes,
      holderNodes,
      positionObjMap,
      positionIDDef
    );
  }

  HandleAllPositionGroups() {
    var scm = this.GetSubChartMgr();
    const objDB = this.GetObjDB();
    const positionClass = objDB.GetClassByName(enumObjClassNames.position);
    const positionIDDef = positionClass.GetFieldDefByID("PositionID");

    // ONE pass over all SC nodes — partition by subchart
    var nodesBySubChart = new Map();
    var n = scm.GetSCNodeCount(false);
    for (var i = 0; i < n; i++) {
      var scn = scm.GetIndexSCNode(i, false);
      var scID = scn.GetSubChartID();
      if (scID === kMasterSCID) continue;
      var isPosition = scn.IsPositionSCNode();
      var isHolder = scn.IsHolderSCNode();
      if (!isPosition && !isHolder) continue;
      var bucket = nodesBySubChart.get(scID);
      if (!bucket) {
        bucket = { positionNodes: [], holderNodes: [] };
        nodesBySubChart.set(scID, bucket);
      }
      if (isPosition) bucket.positionNodes.push(scn);
      else bucket.holderNodes.push(scn);
    }

    // ONE BuildObjectMap for all subcharts
    var positionObjMap = positionClass.BuildObjectMap(positionIDDef);

    // Process each subchart
    nodesBySubChart.forEach(function (bucket, scID) {
      this._processPositionGroup(
        scm,
        scID,
        bucket.positionNodes,
        bucket.holderNodes,
        positionObjMap,
        positionIDDef
      );
    }, this);
  }

  _processPositionGroup(scm, scID, positionNodes, holderNodes, positionObjMap, positionIDDef) {
    // Delete unassigned holder nodes; build Map from remaining holders (Opt 8)
    var holderNodeMap = new Map();
    for (var h = 0; h < holderNodes.length; h++) {
      var hn = holderNodes[h];
      if (hn.GetDataObj().SmartGetFieldValue(kAssignmentStatus) === AssignmentStatus.Unassigned) {
        scm.RemoveSCNode(hn.GetDataObjID(), scID);
      } else {
        holderNodeMap.set(hn.GetDataObjID(), hn);
      }
    }

    //group position id to holders nodes and position layout for grouping
    var positionGroups = {};
    for (var i = 0; i < positionNodes.length; i++) {
      var dbObj = positionNodes[i].GetDataObj();
      if (dbObj == null) {
        console.log("Missing db obj ref.");
        continue;
      }
      var positionID = dbObj.GetFieldValue(positionIDDef);
      var holders = dbObj.GetHolderList() ?? [];
      if (positionGroups[positionID] == null)
        positionGroups[positionID] = { holders: [], layout: null };

      positionGroups[positionID].layout = positionNodes[i].GetLayout();

      for (var j = 0; j < holders.length; j++) {
        var holder = holders[j];
        // Opt 8: O(1) Map lookup instead of O(N) FindSCNode
        var holderNode = holderNodeMap.get(holder.GetObjectID()) || null;
        if (!holderNode) {
          if (holder.SmartGetFieldValue(kAssignmentStatus) === AssignmentStatus.Unassigned)
            continue;
          holderNode = this.EnsureHolderNode(holder, scID);
        }

        positionGroups[positionID].holders.push(holderNode);
      }
    }

    // group the corresponding holders for each unique PositionID in the current subchart
    for (var posID in positionGroups) {
      var holderNodes = positionGroups[posID].holders;
      var layout = positionGroups[posID].layout;
      var aContainer = this.GroupNodes(holderNodes, false, layout, scID);
      var targetLayout = aContainer.GetLayout();
      // Opt 9: O(1) Map lookup instead of O(N) FindObject linear scan
      var positionObj = positionObjMap.get(posID) || null;
      if (positionObj) {
        aContainer.fDataObj = positionObj;
        targetLayout.fDataObjID = positionObj.GetObjectID();
        targetLayout.fObjClassName = enumObjClassNames.position;
      }
    }
  }

  EnsureHolderNode(holder, scID) {
    const scm = this.GetSubChartMgr();
    const plm = this.GetPageLayoutMgr();
    if (!scm) return;

    // Check if the holder node already exists
    const node = scm.FindSCNode(holder, scID);
    if (!node) {
      // If not, create a new holder node
      const cp = this.fCanvasRef;
      const wsMgr = this.fDocMgr.fWSMgr;

      var bsmgr = wsMgr.GetBoxStylesMgr();
      cp.ClearSelection();

      var defaultLayout = wsMgr.GetDefaultLayout();
      defaultLayout = bsmgr.CloneLayout(defaultLayout, this);

      // Create a new employee object
      cp.SetDefaults(holder, defaultLayout);

      // Add the box
      const box = cp.AddChartBox(
        0,
        0,
        null,
        holder,
        null,
        false,
        false,
        false,
        false,
        holder.GetObjectID(),
        false
      );
      box.GetLayout().SetSubChartID(scID);
      const scNode = scm.FindSCNode(holder, scID);

      return scNode;
    } else {
      return node;
    }
  }

  GetRefreshDate() {
    return this.fAutoTextMgr.GetRefreshDate();
  }

  GetRefreshTime() {
    return this.fAutoTextMgr.GetRefreshTime();
  }

  GetRefreshTimeStamp(dateFormat) {
    return this.fAutoTextMgr.GetTimeStamp(dateFormat);
  }

  GetRefreshDateValue() {
    return this.fAutoTextMgr.GetRefreshDateValue();
  }

  GetRefreshDateRaw() {
    return this.fAutoTextMgr.GetRefreshDateRaw();
  }

  GetDownloadingExcel() {
    return this.fDownloadingExcel;
  }

  SetDownloadingExcel(downloading) {
    this.fDownloadingExcel = downloading;
  }

  GetPicLoadCount() {
    return this.fPictureMgr.GetPicLoadCount();
  }

  SetCanvasRef(canvasRef) {
    this.fCanvasRef = canvasRef;
  }

  GetCanvasRef() {
    return this.fCanvasRef;
  }

  ClearFormulaCache() {
    if (this.fObjDB) this.fObjDB.ClearFormulaCache();
  }

  ReplaceObjDB(newDB) {
    this.fObjDB = newDB;
    this.fObjDB.SetDocRef(this);
  }

  GetDocMgr() {
    var docMgr = this.fDocMgr; // fWSMgr.GetDocMgr();
    return docMgr;
  }

  GetWSMgr() {
    const docMgr = this.GetDocMgr();
    if (!docMgr) return null;

    return docMgr.GetWSMgr();
  }

  ShowSubChartNum() {
    var docMgr = this.GetDocMgr();
    var dPrefs = docMgr.GetDocPrefs();

    return dPrefs.GetShowSubChartNum();
  }

  SetShowSubChartNumBM(forceShowSCNum) {
    var docMgr = this.GetDocMgr();
    var dPrefs = docMgr.GetDocPrefs();
    dPrefs.SetShowSubChartNumBM(forceShowSCNum);
  }

  GetShowSubChartNumBM() {
    var docMgr = this.GetDocMgr();
    var dPrefs = docMgr.GetDocPrefs();
    return dPrefs.GetShowSubChartNumBM();
  }

  SetShowSubChartNum(showSCNum, setDirty) {
    var docMgr = this.GetDocMgr();
    var dPrefs = docMgr.GetDocPrefs();

    return dPrefs.SetShowSubChartNum(showSCNum, setDirty);
  }

  OptimizeAssistants() {
    var docMgr = this.GetDocMgr();
    return docMgr.OptimizeAssistants();
  }

  GetStartAssistLeft() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetStartAssistLeft();
  }

  AllowOptimizeSort() {
    var docMgr = this.GetDocMgr();
    return docMgr.AllowOptimizeSort();
  }

  GetPageMgr() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetPageMgr();
  }

  GetPageLayoutMgr() {
    return this.fPageLayoutMgr;
  }

  GetSubChartMgr() {
    return this.fSubChartMgr;
  }

  GetLayoutMgr() {
    return this.fLayoutMgr;
  }

  FinishLoadLayout(aLayout) {
    var lm = this.GetLayoutMgr();
    var o = this.GetObjDB();
    lm.FinishLoadLayout(aLayout, o);
  }

  //=================================================================================================
  // Undo/Redo
  StartJournal() {
    this.fObjDB.StartJournal();
    var scm = this.GetSubChartMgr();
    scm.StartJournal();

    var pm = this.GetPageMgr();
    pm.StartJournal();

    var bgm = this.GetBGMgr();
    bgm.StartJournal();
  }

  GetBGMgr() {
    var wsMgr = this.fDocMgr.fWSMgr;
    return wsMgr?.fBackgroundMgr;
  }

  ClearJournal() {
    this.fObjDB.ClearJournal();
    var scm = this.GetSubChartMgr();
    scm.ClearJournal();
    var pm = this.GetPageMgr();
    pm.ClearJournal();
    var bgm = this.GetBGMgr();
    bgm.ClearJournal();
  }

  ApplyCheckPoint(theCheckPoint) {
    var updateCode = null;
    var dbJournal = theCheckPoint.fDBJournal;
    if (dbJournal) this.fObjDB.RestoreJournal(dbJournal);
    var scmJournal = theCheckPoint.fSCMJournal;
    if (scmJournal) {
      var scm = this.GetSubChartMgr();
      updateCode = scm.RestoreJournal(scmJournal);
    }

    var pm = this.GetPageMgr();
    var pmJournal = theCheckPoint.fPMJournal;
    if (pmJournal) {
      pm.RestoreJournal(pmJournal);
    }

    var dbOverrideJournal = theCheckPoint.fDBOverrideJournal;
    if (dbOverrideJournal) {
      this.fObjDB.RestoreJournal(dbOverrideJournal, false);
      var overridesClass = this.fObjDB.GetClassByName("Override");
      overridesClass.ApplyOverrides();
    }

    var bgJournal = theCheckPoint.fBGJournal;
    if (bgJournal) {
      var bgm = this.GetBGMgr();
      updateCode = bgm.RestoreJournal(bgJournal);
    }

    this.UpdateDocContext();
    this.ApplyChartFilters(false);

    return updateCode;
  }

  GetCheckPoint() {
    var cp = null;
    var j = this.fObjDB.GetJournal();
    if (j) {
      cp = new QCheckPoint();
      cp.fDBJournal = j;
    }
    var overrideJ = this.fObjDB.GetOverridesJournal();
    if (overrideJ) {
      if (cp == null) cp = new QCheckPoint();
      cp.fDBOverrideJournal = overrideJ;
    }
    var scm = this.GetSubChartMgr();
    var sj = scm.GetJournal();
    if (sj) {
      if (cp == null) cp = new QCheckPoint();
      cp.fSCMJournal = sj;
    }

    var pm = this.GetPageMgr();
    var pj = pm.GetJournal();
    if (pj) {
      if (cp == null) cp = new QCheckPoint();
      cp.fPMJournal = pj;
    }

    var bgm = this.GetBGMgr();
    var bgJ = bgm.GetJournal();
    if (bgJ) {
      if (cp == null) cp = new QCheckPoint();
      cp.fBGJournal = bgJ;
    }

    return cp;
  }

  //=================================================================================================
  // DocPrefs
  GetDocPrefs() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetDocPrefs();
  }

  PictureLoading(directory, filename, callBack) {
    return this.fPictureMgr.PictureLoading(directory, filename, callBack);
  }

  //=================================================================================================
  // Pictures
  LoadPicture(directory, filename, callBack, destPanel, srcElement, staticImage) {
    var forceReload = this.fPictureMgr.ForceReload(directory, filename);
    var clearCache = this.fClearPicCache || forceReload;

    this.fPictureMgr.LoadPicture(
      directory,
      filename,
      callBack,
      destPanel,
      srcElement,
      staticImage,
      clearCache
    );
  }

  FindPicture(directory, filename) {
    var aPic = this.fPictureMgr.FindPicture(directory, filename);
    return aPic;
  }

  RemovePicture(theDirectory, theFile) {
    this.fPictureMgr.RemovePicture(theDirectory, theFile);
  }

  ReportPictureFault() {
    this.fPictureMgr.ReportPictureFault();
  }

  ClearPictureFault() {
    this.fPictureMgr.ClearPictureFault();
  }

  GetPictureFault() {
    return this.fPictureMgr.GetPictureFault();
  }

  GetShortcutMgr() {
    return this.fShortcutMgr;
  }

  //=================================================================================================
  // Output Methods
  OutputSubElements(stream) {
    var versionNum = this.GetVersion();
    this.OutputSimpleElement("version", versionNum, stream);

    var author = this.fAuthor ? this.fAuthor : this.fDocMgr.fWSMgr.GetUserName();
    this.OutputSimpleElement("author", author, stream);

    var activeTemplate = this.GetActiveTemplate();
    if (activeTemplate)
      this.OutputSimpleElement("activetemplate", activeTemplate.GetTemplateID(), stream);

    var divisionName = this.GetDivisionName();
    if (divisionName) this.OutputSimpleElement("division", divisionName, stream);

    var hierarchyType = this.GetHierarchyType();
    if (hierarchyType != null) this.OutputSimpleElement("hierarchyType", hierarchyType, stream);

    var headerList = this.fDocMgr.fHeadersList;
    if (headerList) this.OutputElementList("headerList", headerList, stream);
  }

  //=================================================================================================
  // Output Methods
  OutputToJSON(json) {
    var newdoc = {};

    let iwiz = this.GetIWizData();
    var backgroundSave = this.GetSaveAsMPTemplate();
    var isStaticChart = !this.IsServerChart() && !iwiz.fIsLiveChart;
    newdoc["version"] = this.fDocVersion;

    if (!backgroundSave) {
      var activeView = this.GetActiveTemplate();
      if (activeView) newdoc["activeView"] = activeView.GetTemplateID();

      let selfChartRules = this.fSelfView;
      if (selfChartRules && selfChartRules.GetNumRules()) {
        newdoc["selfView"] = selfChartRules.SaveToJSON();
        selfChartRules.SetDirty(false);
      }

      var divisionName = this.GetDivisionName();
      if (divisionName) newdoc["division"] = divisionName;

      var hierarchyType = this.GetHierarchyType();
      if (hierarchyType != null) newdoc["hierarchyType"] = hierarchyType;
    }

    var headerList = this.fHeadersList;
    if (headerList) newdoc["headerList"] = headerList;

    var saveObjDB = isStaticChart && !backgroundSave;
    var saveScope = this.GetSaveScope();

    var scm = this.GetSubChartMgr();

    // Output the database
    scm.SetSaveScope(saveScope);
    newdoc["objDB"] = this.fObjDB.OutputToJSON();

    var setFilter = false;

    var classesToSave = ["Override"];
    if (saveObjDB) {
      scm.FilterObjects(true);
      setFilter = true;
      classesToSave.push("Box");
      if (this.IsPositionDocument()) classesToSave.push("Position");
    }
    newdoc["objData"] = this.fObjDB.OutputObjectsJSON(classesToSave);

    var outputFormatOverrides = true;
    if (outputFormatOverrides) {
      var layoutMgr = this.GetLayoutMgr();
      newdoc["layoutMgr"] = layoutMgr.OutputToJSON();
    }
    // Output the SubChartMgr
    newdoc["subchartMgr"] = scm.OutputToJSON();

    if (setFilter) scm.FilterObjects(false);

    if (!backgroundSave) {
      newdoc["shortcuts"] = this.fShortcutMgr.OutputToJSON();
      if (this.fDirectoryInfo) newdoc["directoryInfo"] = this.fDirectoryInfo;
      if (this.fSearchKeys) newdoc["searchKeys"] = this.fSearchKeys;
    }

    var plm = this.GetPageLayoutMgr();
    newdoc["pageLayoutMgr"] = plm.OutputToJSON();

    json.push(newdoc);
  }

  GetSaveScope() {
    return this.fSaveScope;
  }

  SetSaveScope(saveScope) {
    this.fSaveScope = saveScope;
  }

  GetHeaderList() {
    return this.fHeadersList;
  }

  SetHeaderList(headers) {
    this.fHeadersList = headers;
  }

  /**
   * @returns {QObjDBFieldDef[]} List of field defs to be used as columns for planning tabular view.
   */
  GetPlanningTabularColumns() {
    const objDb = this.GetObjDB();
    const positionFieldDefs = this.GetFieldList2(
      true,
      false,
      false,
      false,
      false,
      true,
      objDb.GetClassByName(enumObjClassNames.position)
    );
    const nameFieldDef = this.GetBoxClass()?.GetFieldDefByID(boxObjClassFieldIds.NAME);
    return positionFieldDefs.concat(nameFieldDef ? [nameFieldDef] : []);
  }

  GetDirectoryColumns() {
    if (this.fDirectoryInfo?.fields == null) {
      return this.GetFieldList2(true, false, true, false).map(item => item.GetFieldName());
    }

    return this.fDirectoryInfo.fields;
  }

  HasSearchKeys() {
    if (this.fSearchKeys == null) {
      return true;
    }

    return this.fSearchKeys.length > 0;
  }

  GetSearchKeys() {
    if (this.fSearchKeys == null) {
      return this.GetFieldList2(true, false, false, false, true).map(item => ({
        keyDef: item.GetFieldName()
      }));
    }

    return this.fSearchKeys;
  }

  SetSearchKeys(newKeyList) {
    this.fSearchKeys = newKeyList;
  }

  GetExcelKeys() {
    return this.fExcelKeys;
  }

  SetExcelKeys(newKeyList) {
    this.fExcelKeys = newKeyList;
  }

  GetSaveAsMPTemplate() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetSaveAsMPTemplate();
  }

  OutputChildElements(stream) {
    var stdSave = !this.GetSaveAsMPTemplate();
    var saveScope = this.GetSaveScope();

    var scm = this.GetSubChartMgr();

    // Output the database
    scm.SetSaveScope(saveScope);
    this.fObjDB.OutputToXML(stream);
    var setFilter = false;
    if (stdSave) {
      scm.FilterObjects(true);
      setFilter = true;
      this.fObjDB.OutputObjects(stream);
    }

    // Output the LayoutMgr

    var outputPreso = !this.IsRuleDrivenChart();
    if (outputPreso) {
      // Output the SubChartMgr
      scm.OutputToXML(stream);
      var layoutMgr = this.GetLayoutMgr();
      layoutMgr.OutputToXML(stream);
    }

    if (setFilter) scm.FilterObjects(false);

    this.fShortcutMgr.OutputToXML(stream);
    var plm = this.GetPageLayoutMgr();
    plm.OutputToXML(stream);

    if (this.fDirectoryInfo) {
      var writer = new QXMLObject();
      writer.OutputSimpleElement("dirInfo", JSON.stringify(this.fDirectoryInfo), stream);
    }

    if (this.fSearchKeys) {
      var writer = new QXMLObject();
      writer.OutputSimpleElement("searchKeys", JSON.stringify(this.fSearchKeys), stream);
    }
  }

  LoadSubCharts(excludeList) {
    // ExcludeList is a list of all boxes that have been removed due to access filtering
    var objDB = this.GetObjDB(); //fObjDB;
    var scm = this.GetSubChartMgr();
    var topReplaced = false;
    var newRoot = null;
    if (excludeList) {
      var aClass = this.IsPositionDocument
        ? objDB.GetClassByName(enumObjClassNames.position)
        : objDB.GetClassByName(enumObjClassNames.box);
      newRoot = this.GetLargestTree(aClass, false, false, null);
      topReplaced = true;
    }

    scm.WireSubCharts(objDB, newRoot);
    var lm = this.GetLayoutMgr();
    lm.LoadLayouts(excludeList);

    // Top box got filtered out

    if (topReplaced) {
      var rootSCID = scm.GetRootSubChartID();
      var newRootSCNode = scm.FindSCNode(newRoot, rootSCID);
      var theLayout = newRootSCNode.GetLayout();
      theLayout.SetSubChartFlag(enumSubChartFlag.nobreak);
      theLayout.SetChartHorzCentering(enumHorzCentering.centermiddle);
      theLayout.SetChartVertCentering(enumVertCentering.centertop);
    }

    // Remove subcharts that contain only a topbox
    if (excludeList) {
      scm.RemoveAllSubChartBreaks(true);
      scm.ForceOptimizeRootSC();
    }
  }

  LoadBaseChartMetadata() {
    const docMgr = this.GetDocMgr();
    const wsMgr = docMgr.fWSMgr;

    const chartID = this.GetChartID();

    const callbackObj = {
      ChartMetadataResult: this.BaseChartMetadataResult.bind(this)
    };

    wsMgr.GetChartMetadata(callbackObj, chartID);
  }

  BaseChartMetadataResult(success, metadata) {
    if (success) {
      const iwiz = this.GetIWizData();

      if (iwiz.GetIsLiveChart()) {
        this.LoadLiveChartMetadata(metadata);
      }

      this.LoadCommonChartMetadata(metadata);
    }
  }

  LoadCommonChartMetadata(metadata) {
    var profileMgr = this.GetProfileMgr();
    var profile = metadata["profile"];
    profileMgr.LoadProfile(this, profile);

    if (metadata["keys"]) {
      this.fSearchKeys = metadata["keys"];
    }

    var excelOptions = metadata["excel"];
    if (excelOptions && excelOptions["fields"]) {
      this.fExcelKeys = excelOptions["fields"];
    }

    var directory = metadata["directory"];
    if (directory) {
      this.fDirectoryInfo = directory;
    }

    var views = metadata["views"];
    if (views != null) {
      var viewMgr = this.GetViewMgr();
      viewMgr.ResetViewMgr();
      for (var index in views) {
        viewMgr.AddView(views[index], false);
      }
    }

    const maxLimit = metadata["maxQueryLimit"];
    if (maxLimit) {
      Globals._accountInfo.SetMaxRecords(maxLimit);
    }

    // Apply fieldFormats to field defs. For live charts this is a no-op re-application
    // (already done in InitializeDocFields). For file-based charts (e.g. custom charts
    // loaded from storage), this is the only place fieldFormats are applied — they are
    // not stored in the saved file and must be synced from the server metadata here.
    const o = this.GetObjDB();
    if (o) {
      const aClass = o.GetClassByName("Box");
      if (aClass) this.FormatDynFields(aClass, metadata);
      if (this.IsPositionDocument()) {
        const positionClass = o.GetClassByName("Position");
        if (positionClass) this.FormatDynFields(positionClass, metadata);
      }
    }
  }

  LoadLiveChartMetadata(metadata) {
    const docMgr = this.GetDocMgr();

    docMgr.LoadModDateAndTime(metadata);
    this.ResetDocDefaultFields();
    this.InitializeDocFields(metadata);
    const o = this.GetObjDB();
    const aClass = o.GetClassByName("Box");
    this.AddDynFilters(aClass, metadata, docMgr.fWSMgr);
  }

  isValidChartMetaData(metadata) {
    return (
      metadata &&
      metadata.fields &&
      (metadata.headers || connectorsBuiltDynamically.includes(metadata?.import?.source))
    );
  }

  ChartMetadataResult(success, chartData, response) {
    success = success && this.isValidChartMetaData(chartData);
    const w = this.fDocMgr.fWSMgr;
    //TODO: The previous version of this handling
    if (success) {
      if (w.fBuilderInfo?._pendoChartType) {
        w.GetUsageTracker().ReportEvent("chart_mode.custom_chart.created", {
          chart_type: w.fBuilderInfo._pendoChartType
        });
        delete w.fBuilderInfo._pendoChartType;
      }
      var msg = Globals._uiConfig.InBackgroundEditorMode() ? "Setting Up Background" : "dynmsg2";
      w.AdvanceBuilderChart(enumDocCreateStep.loaddocmetadata, chartData, null, msg, this);
    } else {
      w.fChartLoadPending = false;
      if (!chartData) {
        w.HandleFailedSvrQuery(response);
      } else {
        w.SignalDocChanged();
        w.FinishDynChartLogin();
        if (connectorsBuiltDynamically.includes(chartData?.import?.source)) {
          const alert = new QAlert();
          alert.InitAlert(appstr("Alert"), appstr("eymappingerror"), null, w);
        } else {
          w.ViewerNeedsRefreshAlert(null, chartData.ID);
        }
      }
    }
    w.PrepRenderWS();
    w.RenderWS();
  }

  //=================================================================================================
  // Load Methods
  LoadFromJSON(json) {
    this.fDocVersion = json["version"];
    var activeTemplateID = json["activeView"];
    if (activeTemplateID && this.fDocMgr.fWSMgr.GetChartTemplatesMgr()) {
      var theActiveTemplate = this.fDocMgr.fWSMgr
        .GetChartTemplatesMgr()
        .FindTemplateByIDAllSets(activeTemplateID, false);
      if (theActiveTemplate["success"]) this.SetActiveTemplate(theActiveTemplate["template"]);
    }

    let selfView = json["selfView"];
    if (selfView) {
      this.fSelfView = new QChartTemplate();
      this.fSelfView.LoadFromJSON(json["selfView"]);
    }

    var divisionName = json["divisionName"];
    if (divisionName) this.fDivisionName = divisionName;

    var headerList = json["headerList"];
    if (headerList) this.fHeadersList = headerList;

    var hierarchyType = json["hierarchyType"];
    if (hierarchyType) this.fHierarchyType = hierarchyType;

    // Load DB Structure
    if (json["objDB"]) this.fObjDB.LoadFromJSON(json["objDB"]);

    // Load objects
    var objectData = json["objData"];
    if (objectData) {
      this.fObjDB.LoadObjectsJSON(objectData);
      var boxClass = this.fObjDB.GetClassByName("Box");
      this.SetupNineBox(boxClass);
    }

    var docMgr = this.GetDocMgr();

    var scm = this.GetSubChartMgr();
    var subChartMgrData = json["subchartMgr"];
    if (subChartMgrData != null) scm.LoadFromJSON(subChartMgrData);
    //   else
    //        scm.CreateRootSubChart();

    var lm = this.GetLayoutMgr();
    var lmData = json["layoutMgr"];
    if (lmData != null) lm.LoadFromJSON(lmData);

    var shortcutMgr = this.GetShortcutMgr();
    var shortcutData = json["shortcuts"];
    if (shortcutData != null) shortcutMgr.LoadFromJSON(shortcutData);

    var dirInfo = json["directoryInfo"];
    if (dirInfo) {
      this.fDirectoryInfo = dirInfo;
    }

    var searchKeys = json["searchKeys"];
    if (searchKeys) {
      this.fSearchKeys = searchKeys;
    }

    this.MigrateV2FieldReferences();

    // Live calc fields (_Headcount etc.) are non-serializable — re-seed them after every JSON load.
    if (this.IsCustomChart()) {
      this.SeedPreviewCalcFields();
    }
  }

  LoadSubElements(objTree) {
    this.fDocVersion = objTree.GetValueForChild("version", true);

    this.fAuthor = objTree.GetValueForChild("author", false);

    var activeTemplateID = objTree.GetValueForChild("activetemplate", false);
    if (activeTemplateID && this.fDocMgr.fWSMgr.GetChartTemplatesMgr()) {
      var theActiveTemplate = this.fDocMgr.fWSMgr
        .GetChartTemplatesMgr()
        .FindTemplateByIDAllSets(activeTemplateID, false);
      if (theActiveTemplate["success"]) this.SetActiveTemplate(theActiveTemplate["template"]);
    }

    var divisionName = objTree.GetValueForChild("division", false);
    if (divisionName) this.fDivisionName = divisionName;

    var headerList = objTree.GetValueListForChild("headerList", false);
    if (headerList) this.fDocMgr.fHeadersList = headerList;

    var hierarchyType = objTree.GetValueForChild("hierarchyType", true);
    if (hierarchyType) this.fHierarchyType = hierarchyType;
  }

  LoadChildElements(
    objTree // Loading from .oc5 file
  ) {
    // Load DB Structure
    var theDBStructure = objTree.GetChildByName(this.fObjDB.GetXMLElementName());
    if (theDBStructure) this.fObjDB.LoadFromXML(theDBStructure);

    // Load objects
    var objectData = objTree.GetChildByName("qObjectData");
    if (objectData) {
      this.fObjDB.LoadObjects(objectData);
      var aClass = this.fObjDB.GetClassByName("Box");
      this.SetupNineBox(aClass);
    }

    var docMgr = this.GetDocMgr();
    // Load layout objects
    var pageMgrData = objTree.GetChildByName("pagemgr");
    if (pageMgrData) docMgr.LoadOldPageMgr(pageMgrData, this);

    var docPrefs = objTree.GetChildByName("docprefs");
    if (docPrefs) {
      docMgr.LoadOldDocPrefs(docPrefs);
    }

    var viewMgr = objTree.GetChildByName("viewmgr");
    if (viewMgr) {
      docMgr.LoadOldViewPrefs(viewMgr);
    }

    var profileMgrObjTree = objTree.GetChildByName("profilemgr");
    if (profileMgrObjTree) docMgr.LoadOldProfile(profileMgrObjTree);
    else if (!docMgr.IsBackground(null)) {
    }

    // Load Refresh Params
    var refreshData = objTree.GetChildByName("refreshparams");

    // Load Wizard Settings
    var wizparams = objTree.GetChildByName("wizparams");

    if (refreshData || wizparams) docMgr.LoadWizData(refreshData, wizparams);

    var scm = this.GetSubChartMgr();
    var subChartMgrData = objTree.GetChildByName("subchartmgr");
    if (subChartMgrData != null) scm.LoadFromXML(subChartMgrData);
    //   else
    //        scm.CreateRootSubChart();

    var lm = this.GetLayoutMgr();
    var lmData = objTree.GetChildByName("layoutmgr");
    if (lmData != null) lm.LoadFromXML(lmData);
    else {
      var legacyData = docMgr.GetLegacyBoxLayoutList(true);
      lm.LoadBoxLayouts(legacyData);
    }

    var shortcutMgr = this.GetShortcutMgr();
    var shortcutData = objTree.GetChildByName("shortcutmgr");
    if (shortcutData != null) shortcutMgr.LoadFromXML(shortcutData);

    var dirInfo = objTree.GetChildByName("dirInfo");
    if (dirInfo) {
      var dirJSON = objTree.GetValueForChild("dirInfo", false);
      this.fDirectoryInfo = JSON.parse(dirJSON);
    }

    var searchKeys = objTree.GetChildByName("searchKeys");
    if (searchKeys) {
      var searchJSON = objTree.GetValueForChild("searchKeys", false);
      this.fSearchKeys = JSON.parse(searchJSON);
    }

    this.MigrateV2FieldReferences();
  }

  /**
   * Placeholder for future V2→V3 migration of serialized field references.
   * V2 docs stay V2 (case-insensitive) on load for zero regressions.
   * New charts are created as V3 (case-sensitive) via kDocVersion.
   * Full migration requires tracing all serialized field refs
   * (box layouts, rule pairs, field value libs, profile refs, formula rules)
   * and resolving lowercased IDs to actual-cased IDs before bumping version.
   */
  MigrateV2FieldReferences() {
    // V2 docs remain V2 — case-insensitive indexing preserved
    // New docs are created as V3 via kDocVersion in InitDoc
  }

  GetVersion() {
    return this.fDocVersion;
  }

  GetDivisionName() {
    return this.fDivisionName;
  }

  SetDivisionName(divisionName) {
    this.fDivisionName = divisionName;
  }

  GetObjDB() {
    return this.fObjDB;
  }

  RemoveFieldReferences(fieldRefs) {
    var scm = this.GetSubChartMgr();
    scm.RemoveFieldReferences(fieldRefs, false);
  }

  UpdateFieldReferences() {
    var o = this.GetObjDB();
    var aClass = o.GetClassByName("Box");
    var fieldRefs = aClass.fFieldList;
    var scm = this.GetSubChartMgr();
    scm.RemoveFieldReferences(fieldRefs, true);
  }

  GetCurBoxCount() {
    var theC = this.fObjDB.GetClassByName("Box");
    if (theC == null) return -1;

    var boxCount = theC.GetPersistObjectCount();
    var filteredCount = theC.GetFilteredObjectCount();
    var auxCount = theC.GetAuxCount();

    return boxCount - filteredCount - auxCount;
  }

  IsInMainSubChart(dbObj) {
    var scm = this.GetSubChartMgr();
    return scm.IsInMainSubChart(dbObj);
  }

  GetFieldID(fieldName) {
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var n = boxClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var f = boxClass.GetIndexFieldDef(i);
      if (f.GetFieldDisplayName() == fieldName) return f.GetFieldID();
    }

    return null;
  }

  /**
   *
   * @param {string} objType
   * @param {string[]=} includeFieldIds
   * @returns {object}
   */
  GetFieldMap(objType, includeFieldIds) {
    const objDB = this.GetObjDB();
    let objClass = objDB.GetClassByName(enumObjClassNames.box);

    let coreFieldIds = ["PersonID", "SupervisorID", "Name", "Title"];

    if (this.IsPositionDocument()) {
      if (objType === enumObjectTypes.holder) {
        coreFieldIds = ["EmployeeID", "Employee PositionID", "Name", "Title"];
      } else if (objType === enumObjectTypes.position) {
        objClass = objDB.GetClassByName(enumObjClassNames.position);
        coreFieldIds = ["PositionID", "Parent PositionID", "Position Title"];
      }
    }

    if (includeFieldIds) {
      coreFieldIds = coreFieldIds.concat(includeFieldIds);
    }

    const fieldMap = {};
    for (var i in coreFieldIds) {
      const fieldID = coreFieldIds[i];
      const fieldDef = objClass.GetFieldDefByID(fieldID);
      if (fieldDef == null) continue;
      fieldMap[fieldID] = fieldDef.GetFieldName();
    }

    return fieldMap;
  }

  /**
   *
   * @param {object} jsonRec
   * @param {string} objType
   * @returns {object}
   */
  NormalizeDisplayRecord(jsonRec, objType) {
    var mapping = this.GetFieldMap(objType);
    var normalizedRec = {};
    Object.entries(jsonRec).forEach(([field, value]) => {
      var fieldName = mapping[field];
      if (fieldName) normalizedRec[fieldName] = value;
    });
    return normalizedRec;
  }

  FindDataObjByPersonID(personID) {
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var personIDDef = this.GetRecordIDField();
    return boxClass.FindObject(personIDDef, personID, true);
  }

  FindDataObjByRecordID(recordID, objType = enumObjectTypes.box) {
    var objDB = this.GetObjDB();
    var objClass = objDB.GetClassByName(objType);
    var recordIDDef = objClass.GetRecordIDFieldDef();

    return objClass.FindObject(recordIDDef, recordID, true);
  }

  GetEmployeeLabelByPersonID(personID, doFields) {
    var empLabel = appstr("refinvalid");
    var dataObj = this.FindDataObjByPersonID(personID);
    if (dataObj) {
      empLabel = dataObj.FormatEmployeeLabel(doFields);
    }
    return empLabel;
  }

  /**
   * Returns a list of fields headers based on criteria.
   * @param {boolean} includeCalcs - Determines if fields with non-null calcCodes should be filtered out.
   * @param {boolean} includeReadOnly - If false, prevents readOnly fields from being added to the returned field list.
   * @param {boolean} includePhotos - If false, prevents photo fields from being added to the returned field list.
   * @param {boolean} includeLocked - Only applies to the "Locked" field. If true, adds it to the returned field list.
   * @param {Array|null} [excludeArray=null] - If provided, excludes these field names from the returned list.
   * @param {QObjDBClass|null} [objClass=null] - If provided, uses this class to get the fields, otherwise defaults to the "Box" class.
   * @returns {Array} - A list of fields based on the given criteria.
   */
  GetTargetHeaders(
    includeCalcs,
    includeReadOnly,
    includePhotos,
    includeLocked,
    excludeArray,
    objClass = null
  ) {
    //TODO: 'isAux', 'scFlag' are a unique case. Added during attachChartMetadata.
    // There likely exists a workaround to prevent them from being appended.
    var exclude = ["isAux", "scFlag"];
    if (excludeArray != null) exclude = exclude.concat(excludeArray);
    var headers = [];
    var fieldList = this.GetFieldList2(
      includeCalcs,
      includeReadOnly,
      includePhotos,
      includeLocked,
      false,
      false,
      objClass
    );
    for (var fieldIdx in fieldList) {
      var aField = fieldList[fieldIdx];
      headers.push(aField.GetFieldName());
    }
    headers = headers.sort();
    return headers.filter(function (aHeader) {
      return exclude.indexOf(aHeader) === -1;
    });
  }

  /**
   * Returns a list of fields based on the given criteria.
   * @param {boolean} includeCalcs - Determines if fields with non-null calcCodes should be filtered out.
   * @param {boolean} includeReadOnly - If false, prevents readOnly fields from being added to the returned field list.
   * @param {boolean} includePhotos - If false, prevents photo fields from being added to the returned field list.
   * @param {boolean} includeLocked - Only applies to the "Locked" field. If true, adds it to the returned field list.
   * @param {boolean} [includeInternal=false] - If false, prevents internal fields (e.g. scFlag, treeID, _objInfo) from being added to the returned field list (default=false).
   * @param {boolean} [includeAggregated=true] - If true, includes Aggregated fields in the returned field list, otherwise excludes them (default=true).
   * @param {QObjDBClass|null} [objClass=null] - If provided, uses this class to get the fields, otherwise defaults to the "Box" class.
   * @returns {Array} - A list of fields based on the given criteria.
   */
  GetFieldList2(
    includeCalcs,
    includeReadOnly,
    includePhotos,
    includeLocked,
    includeInternal = false,
    includeAggregated = true,
    objClass = null
  ) {
    var objDB = this.GetObjDB();
    var aClass = objClass ?? objDB.GetClassByName("Box");

    var l = [];
    var n = aClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var f = aClass.GetIndexFieldDef(i);

      var isPhoto = f.GetFieldType() == enumFieldType.image;
      if (!includePhotos && isPhoto) continue;

      var isLocked = f.IsLockedField() || f.IsPersistField();
      if (!includeLocked && isLocked) continue;

      var isCalc = f.GetCalcCode() != null;
      var fieldReadOnly = f.FieldReadOnly();
      var internalOnly = f.GetInternalOnly();

      if (!includeCalcs && isCalc) continue;
      if (
        !includeReadOnly &&
        fieldReadOnly &&
        !(includeCalcs && f.GetCalcCode() == enumCalcCode.static)
      )
        continue;
      else if (
        (internalOnly && !includeInternal) ||
        (!includeAggregated && f.GetCalcCode() == enumCalcCode.aggregatedField)
      )
        continue;
      else if (f.GetFieldSubtype() === enumCellObjectSubtypes.objinfo) continue;

      l.push(f);
    }

    l.sort(comparefields);

    return l;
  }

  GetFieldDefsBySubtype(subType) {
    var fieldDefs = this.GetFieldList(true, true);
    var pickFields = [];
    for (var i = 0; i < fieldDefs.length; i++) {
      var aField = fieldDefs[i];
      if (aField.GetFieldSubtype() === subType) pickFields.push(aField);
    }
    return pickFields;
  }

  GetFieldList(includeCalcs, includeReadOnly) {
    return this.GetFieldList2(includeCalcs, includeReadOnly, true, false);
  }

  RemoveField(theField) {
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");

    boxClass.RemoveField(theField);
  }

  TempPopDB(classOnly) {
    var aClass = new QObjDBClass();
    aClass.InitClass("Box", false, this.fDocMgr.fWSMgr, false, this.fObjDB);
    this.fObjDB.AddClass(aClass);

    var overrideClass = new QOverrideMgr();
    overrideClass.InitClass("Override", false, this.fDocMgr.fWSMgr, true, this.fObjDB);
    this.fObjDB.AddClass(overrideClass);

    if (classOnly)
      // do not want to hit below block for positions.......
      return;

    AddFieldDef(aClass, "Name", "Name", appstr("Name"), false);
    AddFieldDef(aClass, "Title", "Title", appstr("Title"), false);
    AddFieldDef(aClass, "Location", "Location", appstr("Location"), false);
    AddFieldDef(aClass, "Department", "Department", appstr("Department"), false);
    AddFieldDef(aClass, "WorkPhone", "Work Phone", appstr("WorkPhone"), false);
    var photoField = AddFieldDef(aClass, "Photo", "Photo", appstr("Photo"), false);
    photoField.SetFieldType(enumFieldType.image);
  }

  ClearBranchFilter() {
    var iWizData = this.GetIWizData(enumIWizDataSet.refresh);
    if (iWizData) {
      iWizData.SetFilterData(false);
    }
  }

  SetDataSource(dataSource) {
    var iWizData = this.GetIWizData();
    if (iWizData) {
      iWizData.SetDataSource(dataSource);
    }
  }

  SetIsServerChart(isServerChart) {
    var iWizData = this.GetIWizData();
    if (iWizData) {
      iWizData.SetIsServerChart(isServerChart);
    }
  }

  SetFilterToBranch() {
    var iWizData = this.GetIWizData(enumIWizDataSet.refresh);
    if (iWizData) {
      iWizData.SetFilterData(true);

      //     var o = this.fObjDB;
      //    var aClass = o.GetClassByName("Box");
      //     var curDef = aClass.GetFieldDefByID("PersonID");

      var curDef = this.GetRecordIDField();
      if (curDef) iWizData.SetFilterField(curDef.GetFieldName());
      iWizData.SetFilterComp("equals");
      var scm = this.GetSubChartMgr();
      var personID = scm.GetCurrentTopObjPersonID();
      iWizData.SetFilterValue(personID);
      iWizData.SetFilterSubsOption(enumMatchSubs.allsubs);
    }
  }

  LoadImportData(importData, defaultProfile) {
    var loader = new QXMLLoader();
    var objTree = loader.LoadXML(importData);
    var result = this.PopDBFromXML(objTree);
    if (defaultProfile) this.SetUpDefaultProfile(result);
    return result;
  }

  SetDataSourceFlag(dsFlag) {
    this.fDataSourceFlag = dsFlag;
  }

  GetDataSourceFlag() {
    return this.fDataSourceFlag;
  }

  ConfigureXMLImportForSource() {
    var ds = this.GetDataSourceFlag();
    this.fRowTag = "r";
    this.fValueTag = "d";

    switch (ds) {
      case enumDataSourceFlag.ultipro:
        this.fRowTag = "row";
        this.fValueTag = "value";
        break;
    }
  }

  TrimDocument() {
    // Strip down objects so that doc still exists but doesn't take up so much memory
    // this.fPageMgr.TrimDocument();
  }

  ReleaseMemory() {
    // this.fProfileMgr = null;
    this.fPictureMgr = null;
    //  this.fDocPrefs = null;
    this.fAutoTextMgr = null;
    this.fObjDB = null;

    //   this.fPageMgr.ClosePageMgr();
    //   this.fPageMgr = null;
  }

  //=================================================================================================
  // For changes that are not undoable - set the doc to dirty
  IsDocDirty() {
    var docMgr = this.GetDocMgr();
    return docMgr.IsDocDirty();
  }

  SetDirty(isDirty) {
    var docMgr = this.GetDocMgr();
    docMgr.SetDirty(isDirty);
    this.UpdateDocContext();
  }

  //=================================================================================================
  // Load import data into the ODB
  CleanFieldName(fieldName) {
    var theName = fieldName;
    var lcName = theName.toLowerCase();
    switch (lcName) {
      case "isassistant":
        theName = "isAssistant";
        break;
      case "title":
        theName = "Title";
        break;
      case "name":
        theName = "Name";
        break;
      case "supervisorid":
        theName = "SupervisorID";
        break;
      case "personid":
        theName = "PersonID";
        break;
      case "photo":
        theName = "Photo";
        break;
      case "action":
        theName = "Action";
        break;
      case "isaux":
        theName = "isAux";
        break;
    }

    return theName;
  }

  SetupNineBox(aClass) {
    //TODO: handle this mapping properly
    var perfName = appstr("Performance");
    var perfField = aClass.GetFieldDefByName(perfName);
    if (perfField == null) perfField = aClass.GetFieldDefByName("ReviewRatingDesc");
    if (perfField == null) return;

    var potName = appstr("Potential");
    var potField = aClass.GetFieldDefByName(potName);
    if (potField == null) potField = aClass.GetFieldDefByName("LatestTalentAssessment");
    if (potField == null) return;

    this.ForceFieldID(perfField, "Performance");
    this.ForceFieldID(potField, "Potential");
    aClass.AddNineBox();
  }

  //====================================================================
  //== doc / override hooks
  /**
   * @returns {(QOverrideMgr | null)}
   */
  GetOverrideClass() {
    var objDB = this.GetObjDB();
    return objDB.GetClassByName(enumObjClassNames.override);
  }

  GetBoxClass() {
    var objDB = this.GetObjDB();
    return objDB.GetClassByName(enumObjClassNames.box);
  }

  GetPositionClass() {
    var objDB = this.GetObjDB();
    return objDB.GetClassByName(enumObjClassNames.position);
  }

  JournalBaseRecords() {
    var boxClass = this.GetBoxClass();
    boxClass.JournalObjList();
  }

  JournalOverrides() {
    var overrideClass = this.GetOverrideClass();
    overrideClass.JournalObjList();
  }

  JournalDataRecords() {
    this.JournalBaseRecords();
    this.JournalOverrides();
  }

  CopyAndApplyOverrides(priorDoc) {
    var newOverrideClass = this.GetOverrideClass();
    if (priorDoc) newOverrideClass.CopyOverrides(priorDoc);
    newOverrideClass.ApplyOverrides();
  }

  //=====================================================================
  SetupART(aClass, metadata) {
    if (aClass.ActiveAuxMapping(metadata.fields) === true) aClass.AddART();
  }

  SetupAllocationRatio(aClass) {
    if (aClass.HasAllocationRatioFields()) {
      aClass.AddAllocationRatio();
    }
  }

  ProcessUltiHeader(objTree) {
    var header = new headerinfo();
    var childList = null;
    var errMsg = "";

    var headerData = null;

    var root = objTree.GetChildByName("dataset");
    if (root != null) headerData = root.GetChildByName("metadata");

    if (headerData == null) errMsg = appstr("loaderr1");
    else {
      var childList = headerData.GetChildOfTypeList("item");
      var nFields = childList.length;
      if (nFields == 0) errMsg = appstr("loaderr2");
      else {
        for (var i = 0; i < nFields; i++) {
          var c = childList[i];
          var fieldParam = c.GetParamByName("name");
          var v = null;
          if (fieldParam) {
            v = fieldParam.GetParamValue();
            var l = v.length;
            if (l > 3) v = v.substr(1, v.length - 2);
          }
          if (v != null) c.AddValue(v);
        }
      }
    }

    header.fFieldList = childList;
    header.fErrorMsg = errMsg;

    return header;
  }

  ProcessHeader(objTree) {
    var header = null;
    var ds = this.GetDataSourceFlag();
    if (ds == enumDataSourceFlag.ultipro) header = this.ProcessUltiHeader(objTree);
    else {
      header = new headerinfo();
      var docMgr = this.GetDocMgr();
      var modDate = objTree.GetValueForChild("moddate", false);
      if (modDate != null) docMgr.SetRefreshDate(modDate);

      var childList = null;
      var errMsg = "";

      var headerData = objTree.GetChildByName("header");
      if (headerData == null) errMsg = appstr("loaderr1");
      else {
        childList = headerData.GetChildOfTypeList("field");
        var nFields = childList.length;
        if (nFields == 0) errMsg = appstr("loaderr2");
      }

      header.fFieldList = childList;
      header.fErrorMsg = errMsg;
    }

    return header;
  }

  AddAuxObjs(
    auxes,
    value,
    aClass,
    idx,
    auxLabel,
    isAuxDef,
    supervisorIDDef,
    personIDDef // fieldDef, fieldDef2,
  ) {
    var auxValue = value.toString().trim();
    if (auxValue.length > 0) {
      var parts = [auxValue];
      if (auxValue.indexOf(",") >= 0) parts = auxValue.split(",");
      else if (auxValue.indexOf("|") >= 0) parts = auxValue.split("|");

      var objCount = aClass.GetObjectCount();
      var objCopy = aClass.GetIndexObject(idx);
      if (parts.length > 1) auxLabel = null;
      for (var i = 0; i < parts.length; i++) {
        var baseID = objCopy.GetFieldValue(personIDDef).toString();
        var curID = baseID;
        var label = auxLabel !== null ? auxLabel : i;
        if (curID.indexOf("-aux") < 0) curID += "-aux" + label;

        var skip = false;
        for (var j = 0; j < objCount; j++) {
          var anObj = aClass.GetIndexObject(j);
          var objPersonID = anObj.GetFieldValue(personIDDef).toString();
          var splitID = objPersonID.split("-aux");
          if (objPersonID === curID) {
            // This aux already exists
            skip = true;
            break;
          } else if (anObj.GetFieldValue(isAuxDef) == "1") {
            // Have found an aux object
            if (
              splitID[0] == baseID &&
              parts[i] == anObj.GetFieldValue(supervisorIDDef).toString()
            ) {
              // If they are an aux sub, and their base PersonID == my id, and their supervisor is the same as my supervisor, they will be a duplicate.
              skip = true;
              break;
            }
          }
        }
        if (
          objCopy.GetFieldValue(supervisorIDDef) == null ||
          objCopy.GetFieldValue(supervisorIDDef).toString() == parts[i] ||
          parts[i] == objCopy.GetFieldValue(personIDDef).toString()
        )
          // Same Supervisor as AuxSuper || My ID == AuxSuper
          skip = true;
        if (skip) continue;
        var newObj = aClass.AddNewObject();
        var defList = this.GetFieldList2(false, false, true, false);
        for (var defIdx in defList)
          newObj.SetFieldValue(defList[defIdx], objCopy.GetFieldValue(defList[defIdx], false));
        newObj.SetFieldValue(isAuxDef, "1");
        newObj.SetFieldValue(personIDDef, curID);
        newObj.SetFieldValue(supervisorIDDef, parts[i]);
        newObj["auxindex"] = i;
        auxes.push(newObj);
      }
    }
  }

  IsAuxField(fieldName) {
    var field_lower = fieldName.toLowerCase();

    if (field_lower == "auxsuperid") return true;

    var fn1 = "dotted line manager (user id)";
    if (field_lower == fn1) return true;

    return false;
  }

  IsAuxField2(fieldName) {
    var field_lower = fieldName.toLowerCase();
    if (field_lower == "auxsuperid2") return true;

    var fn1 = "dual line manager (user id)";
    if (field_lower == fn1) return true;

    return false;
  }

  AddNewFieldValue(obj, fieldDef, fieldValue) {
    //  if (typeof fieldValue == 'string')
    //     fieldValue = fieldValue.replace(/[\u00A0-\ue007]/g, '');
    // fieldValue = fieldValue.replace(/[\u0250-\ue007]/g, '');

    obj.AddNewFieldValue(fieldDef, fieldValue);
  }

  PPopDBFromXML(
    objTree //
  ) {
    this.ConfigureXMLImportForSource();

    //  var docMgr = this.GetDocMgr();
    if (Globals._reportPerf2) {
      console.log("Import Data");
      var d1 = new Date();
      Globals._startImport = d1.getTime();
    }
    //BoxCount
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var headerinfo = this.ProcessHeader(objTree);
    var childList = headerinfo.fFieldList;
    if (headerinfo.fErrorMsg !== "") return headerinfo.fErrorMsg;
    this.fHeadersList = childList.map(function (x) {
      return x.fValue;
    });
    this.fDocMgr.fHeadersList = childList.map(function (x) {
      return x.fValue;
    });
    var auxFieldDef = null;
    var auxFieldDef2 = null;
    var auxFieldIdx = -1;
    var auxFieldIdx2 = -1;

    var nFields = childList.length;
    var fieldList = [];
    for (var i in childList) {
      var f = childList[i];
      var theName = f.GetValue();
      theName = this.CleanFieldName(theName);
      if (theName.length == 0) {
        var probCol = toColumnName(i + 1);
        var eStr = appstr("loaderr2b") + " " + appstr("loaderr2c") + " (" + probCol + ").";
        return eStr;
      }
      var existingField = aClass.GetFieldDefByID(theName);
      if (existingField != null) {
        var part2 = null;
        if (
          theName.toLowerCase() == "id" ||
          theName.toLowerCase() == "isaux" ||
          theName.toLowerCase() == "_wbaux"
        )
          return appstr("loaderr3");
        else if (existingField.FieldReadOnly()) part2 = "loaderr4c";
        else part2 = "loaderr4b";

        return appstr("loaderr4a") + " '" + theName + "' " + appstr(part2);
      }
      if (theName == "isAux" || theName == "_wbaux") {
        return appstr("loaderr10");
      }
      //TODO: Not dynamic.
      var aField = new QObjDBFieldDef();
      aField.InitField(theName, theName, aClass);
      if (auxFieldDef == null && this.IsAuxField(theName)) {
        aField.SetFieldID("AuxSuperID");
        auxFieldDef = aField;
        auxFieldIdx = i;
      }

      if (auxFieldDef2 == null && this.IsAuxField2(theName)) {
        aField.SetFieldID("AuxSuperID2");
        auxFieldDef2 = aField;
        auxFieldIdx2 = i;
      }

      aClass.AddField(aField);
      fieldList.push(aField);
    }

    var isAuxFieldDef = null;
    if (auxFieldDef) {
      isAuxFieldDef = new QObjDBFieldDef();
      isAuxFieldDef.InitField("isAux", "isAux", aClass);
      aClass.AddField(isAuxFieldDef);
    }

    this.SetupNineBox(aClass);
    if (this.GetDataSourceFlag() == enumDataSourceFlag.ultipro) {
      objTree = objTree.GetChildByName("dataset");
      objTree = objTree.GetChildByName("data");
    }
    var childList = objTree.GetChildOfTypeList(this.fRowTag);

    if (childList.length == 0) return appstr("loaderr5");

    //var m = this.fMaxRecs;
    //if (childList.length > m)
    //	return appstr("loaderr6a") + " " + m + " " + appstr("loaderr6b");

    for (var i in childList) {
      var r = childList[i];
      objTree.ClearChild(i);
      childList[i] = null;

      var valueTag = this.fValueTag;
      var childList2 = r.GetChildOfTypeList(valueTag);
      var dl = childList2.length;
      if (dl !== nFields) {
        var errRow = i + 2;
        return appstr("loaderr7") + errRow + ").";
      }

      var newObj = aClass.AddNewObject();

      for (var j in childList2) {
        var data = childList2[j];
        r.ClearChild(j);
        childList2[j] = null;
        var fieldDef = fieldList[j];
        var fieldID = fieldDef.GetFieldID();
        var isPhoto = fieldID == "Photo";
        var fieldValue = data.GetValue();
        if (fieldValue != null) fieldValue = fieldValue.trim();

        if (isPhoto) fieldValue = String(fieldValue);

        this.AddNewFieldValue(newObj, fieldDef, fieldValue);
      }
    }

    if (Globals._reportPerf2) {
      var d2 = new Date();
      var e2 = d2.getTime();
      var elapsed = e2 - Globals._startImport;
      console.log("Exit Import Data:", elapsed);
    }

    return "success";
  }

  CreateAuxObjects(aClass, auxSuperDef, auxLabel) {
    var auxes = [];
    var objCount = aClass.GetObjectCount();
    for (var idx = 0; idx < objCount; idx++) {
      var isAuxDef = aClass.GetFieldDefByID("isAux");
      var supervisorIDDef = aClass.GetFieldDefByID("SupervisorID");
      var personIDDef = aClass.GetFieldDefByID("PersonID");
      var curObj = aClass.GetIndexObject(idx);
      var auxFromField = curObj.GetFieldValue(auxSuperDef);
      if (auxFromField != null) {
        var auxVal1 = auxFromField.toString();
        this.AddAuxObjs(
          auxes,
          auxVal1,
          aClass,
          idx,
          auxLabel,
          isAuxDef,
          supervisorIDDef,
          personIDDef
        );
      }
    }
    return auxes;
  }

  //==================================================================================
  // Append Data Source
  FindEmailFieldDef(aClass) {
    var n = aClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = aClass.GetIndexFieldDef(i);
      var fieldID = fieldDef.GetFieldID();
      if (fieldID.indexOf("email") != -1 || fieldID.indexOf("Email") != -1) return fieldDef;
    }

    return null;
  }

  Email_Lookup(aClass, dataElements, email_lookup_col) {
    var emailFieldDef = this.FindEmailFieldDef(aClass);
    if (emailFieldDef == null) return null;

    var Email_Element = dataElements[email_lookup_col];

    var IDValue = Email_Element.GetValue();
    var matchObj = aClass.FindObject(emailFieldDef, IDValue, true);
    return matchObj;
  }

  FindAppendObj(aClass, dataElements, idDef, idIndex, email_lookup_col) {
    if (idIndex == -1) return;
    var IDElement = dataElements[idIndex];
    var matchObj = null;
    if (IDElement) {
      var IDValue = IDElement.GetValue();
      if (IDValue == "email_lookup" && email_lookup_col != -1) {
        matchObj = this.Email_Lookup(aClass, dataElements, email_lookup_col);
        return matchObj;
      }

      if (IDValue && IDValue.length > 0) matchObj = aClass.FindObject(idDef, IDValue, true);
    }

    return matchObj;
  }

  ProcessValue(fieldDef, dataElement) {
    var isPhoto = fieldDef.GetFieldID() == "Photo";
    var fieldValue = dataElement.GetValue();
    if (!isPhoto) {
      var intValue = parseInt(fieldValue);
      if (intValue.toString() == fieldValue) fieldValue = intValue;
    } else fieldValue = String(fieldValue);

    return fieldValue;
  }

  DoAddObj(aClass, rowObj, dataElements, dataElementsLength, fieldList, idDef, idIndex) {
    var newObj = aClass.AddNewObject();
    for (var j = 0; j < dataElementsLength; j++) {
      var data = dataElements[j];
      rowObj.ClearChild(j);
      dataElements[j] = null;
      var fieldDef = fieldList[j];
      var idField = j == idIndex;
      if (fieldDef || idField) {
        if (idField) fieldDef = idDef;
        var fieldValue = this.ProcessValue(fieldDef, data);
        newObj.AddNewFieldValue(fieldDef, fieldValue);
      }
    }
  }

  DoSuppressObj(aClass, dataElements, idDef, idIndex) {
    var matchObj = this.FindAppendObj(aClass, dataElements, idDef, idIndex, -1);
    if (matchObj) aClass.DeleteObject(matchObj);
  }

  DoUpdateObj(
    aClass,
    rowObj,
    dataElements,
    dataElementsLength,
    idDef,
    idIndex,
    fieldList,
    email_lookup_col
  ) {
    var matchObj = this.FindAppendObj(aClass, dataElements, idDef, idIndex, email_lookup_col);
    if (matchObj == null) return;

    for (var j = 0; j < dataElementsLength; j++) {
      var data = dataElements[j];
      rowObj.ClearChild(j);
      dataElements[j] = null;
      var fieldDef = fieldList[j];
      if (fieldDef) {
        var fieldValue = this.ProcessValue(fieldDef, data);
        if (fieldValue != "") {
          var compVal = fieldValue.toString().toLowerCase();
          if (compVal == "<blank>") fieldValue = "";
          matchObj.SetFieldValue(fieldDef, fieldValue);
        }
      }
    }
  }

  AppendDBFromXML(objTree) {
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");

    var headerData = objTree.GetChildByName("header");
    var nFields = 0;

    if (headerData != null) {
      var headerList = headerData.GetChildOfTypeList("field");
      if (headerList) nFields = headerList.length;
    }

    var personIDDef = aClass.GetFieldDefByID("PersonID");
    var personIDIndex = -1;

    var actionCol = -1;
    var email_lookup_col = -1;

    var fieldList = [];
    for (var i = 0; i < nFields; i++) {
      var f = headerList[i];
      var theName = f.GetValue();
      theName = this.CleanFieldName(theName);
      var existingField = null;
      if (theName == "Action") actionCol = i;
      else if (theName == "email_lookup") email_lookup_col = i;
      else if (theName != "Info") {
        existingField = aClass.GetFieldDefByName(theName);
        if (existingField == null) {
          var fieldDef = new QObjDBFieldDef();
          fieldDef.InitField(theName, theName, aClass);
          aClass.AddField(fieldDef);
          existingField = fieldDef;
        } else {
          if (personIDDef == existingField) {
            personIDIndex = i;
            existingField = null;
          }
        }
      }

      fieldList.push(existingField);
    }

    var childList = objTree.GetChildOfTypeList("r");
    var n2 = childList.length;
    for (var i = 0; i < n2; i++) {
      var r = childList[i];
      objTree.ClearChild(i);
      childList[i] = null;

      var dataElements = r.GetChildOfTypeList("d");
      var dl = dataElements.length;
      if (dl > nFields) dl = nFields;

      var action = "";
      if (actionCol != -1) {
        var dataElement = dataElements[actionCol];
        var data = dataElement.GetValue();
        if (data && data.length > 0) action = data.toLowerCase();
      } else action = "add";

      switch (action) {
        case "add":
          this.DoAddObj(aClass, r, dataElements, dl, fieldList, personIDDef, personIDIndex);
          break;
        case "suppress":
          this.DoSuppressObj(aClass, dataElements, personIDDef, personIDIndex);
          break;
        case "update":
          this.DoUpdateObj(
            aClass,
            r,
            dataElements,
            dl,
            personIDDef,
            personIDIndex,
            fieldList,
            email_lookup_col
          );
          break;
      }
    }
  }

  PopDBFromXML(objTree) {
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var n = aClass.GetFieldCount();
    this.fPredefFieldCount = n;
    var result = this.PPopDBFromXML(objTree);
    if (result != "success") this.UnwindImport();
    return result;
  }

  // Version 2.0.2
  UnwindImport() {
    // console.log("UnwindImport");

    var origCount = this.fPredefFieldCount;
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var n = aClass.GetFieldCount();
    for (var i = n - 1; i > origCount - 1; i--) {
      var f = aClass.GetIndexFieldDef(i);
      aClass.RemoveField(f);
    }

    // Delete any objects
    this.ClearParents();
    var n = aClass.GetObjectCount();
    for (var i = n - 1; i >= 0; i--) {
      var obj = aClass.GetIndexObject(i);
      aClass.DeleteObject(obj);
    }

    var profileMgr = this.GetProfileMgr();
    profileMgr.ResetProfileMgr();
  }

  ClearParents() {
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var n = aClass.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var obj = aClass.GetIndexObject(i);
      obj.SetParent(null, -1);
    }
  }

  ParentMatch(
    aClass,
    currentObj,
    personIndex,
    parentIDDef,
    supervisorIDDef,
    theFilter,
    checkChain,
    topList
  ) {
    var visited = new Set();
    var obj = currentObj;

    while (obj != null) {
      if (visited.has(obj)) return null; // cycle detected
      visited.add(obj);

      var v = obj.GetFieldValue(supervisorIDDef);
      var v2 = obj.GetFieldValue(parentIDDef);
      if (v == v2) return null;

      var parentObj = personIndex.FindObject(v);
      if (parentObj == null) return null;

      if (theFilter.MatchRule(parentObj, this)) return parentObj; // found match

      if (!checkChain) return null;
      if (topList != null && topList.indexOf(v.toString()) != -1) return null;

      obj = parentObj;
    }

    return null;
  }

  ChainOK(aClass, obj, personIDDef, supervisorIDDef, personIDIndex) {
    var v = obj.GetFieldValue(supervisorIDDef);
    if (v == null) return null;

    var objArr = [obj];

    while (obj != null && obj.IsPersistent()) {
      if (obj == this.fForceTopObj) {
        return null;
      }
      v = obj.GetFieldValue(supervisorIDDef);
      if (personIDIndex) obj = personIDIndex.FindObject(v);
      else obj = aClass.FindObject(personIDDef, v, true);

      if (objArr.indexOf(obj) > -1 || objArr.length > 100) {
        objArr.push(obj);
        var personIDs = objArr.map(function (a) {
          return a.GetObjectRecordID();
        });
        var s = personIDs.join("->");
        return s;
      }
      objArr.push(obj);
    }

    return null;
  }

  DocGetFieldDefByID(fieldID) {
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var curDef = aClass.GetFieldDefByID(fieldID);
    return curDef;
  }

  ForceFieldID(fieldDef, forceID) {
    if (fieldDef == null) return;

    var o = this.fObjDB;
    var aClass = fieldDef.GetParentClass();
    var curDef = aClass.GetFieldDefByID(forceID);
    if (curDef != null && fieldDef.GetFieldID() === forceID)
      //TODO: I think this statement may be missing something
      return;

    if (curDef != null && curDef !== fieldDef) aClass.ForceUniqueID(curDef);
    fieldDef.SetFieldID(forceID);
  }

  WirePhotoField(photoField) {
    if (photoField == null || photoField == "-") return;

    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var photoIDDef = aClass.GetFieldDefByName(photoField);
    if (photoIDDef) {
      photoIDDef.SetFieldType(enumFieldType.image);
      this.ForceFieldID(photoIDDef, "Photo");
    }
  }

  FieldNameInUse(newFieldName, includeDynamicFormulas) {
    var objDB = this.GetObjDB();
    var aClass = objDB.GetClassByName("Box");
    var existingField = aClass.GetFieldDefByName(newFieldName);
    if (existingField == null) existingField = aClass.GetFieldDefByID(newFieldName);
    if (existingField != null && !includeDynamicFormulas) {
      if (existingField.GetCalcCode() != null && existingField.GetCalcCode() != -1) return false;
    }
    return existingField != null;
  }

  // New separate method for field ID assignment
  AssignFieldID(fieldID, fieldMap, objectClass) {
    // these fields are calculated on backend - the mapped values act as a look up, and the other is a system placeholder
    const ignoreMappedName = [...SYSTEM_GENERATED_CAPABLE_FIELDS].includes(fieldID);

    let fieldName = fieldMap[fieldID];
    if (
      fieldName === SYSTEM_CALCULATE ||
      (ignoreMappedName && (fieldName === "" || fieldName == null))
    ) {
      // Special case: Desired Holders system-generated field uses "Staffing Target" as field name
      fieldName = fieldID === kDesiredHolders ? kStaffingTarget : fieldID;
    }

    if (!fieldName) return null;

    let fieldDef = objectClass.GetFieldDefByName(fieldName);
    if (!fieldDef) fieldDef = objectClass.GetFieldDefByID(fieldName);

    if (fieldDef) this.ForceFieldID(fieldDef, fieldID);

    return fieldDef;
  }

  // Main method with reduced duplication
  AssignFieldIDs(fieldMap) {
    const o = this.fObjDB;
    const boxClass = o.GetClassByName("Box");
    const positionClass = o.GetClassByName("Position");
    const isPositions = this.IsPositionDocument();
    const requiredKeys = [
      "PersonID",
      "SupervisorID",
      "Name",
      "Title",
      "Position Title",
      "PositionID",
      "Parent PositionID",
      "Employee PositionID",
      "EmployeeID",
      "Employee SupervisorID",
      "Employee Cost Center",
      "Position Location",
      "Position Cost Center",
      "Employee Pay",
      "Employee Salary",
      "Employee Location",
      "Position Division"
    ];

    // Optional fields run first to ensure they don't overwrite core fields
    const optionalKeys = Object.keys(fieldMap).filter(item => !requiredKeys.includes(item));

    for (const fieldID of optionalKeys) {
      const isPositionFieldId = isPositions && kPositionFieldIDs.includes(fieldID);
      const objectClass = isPositionFieldId ? positionClass : boxClass;

      const fieldDef = this.AssignFieldID(fieldID, fieldMap, objectClass);
      if (!fieldDef) {
        this.CreateFieldDef(fieldID, objectClass, [], false);
      }
    }

    for (const fieldID of requiredKeys) {
      const isPositionFieldId = isPositions && kPositionFieldIDs.includes(fieldID);
      const objectClass = isPositionFieldId ? positionClass : boxClass;
      this.AssignFieldID(fieldID, fieldMap, objectClass);
    }
  }

  SetAuxDivision(aClass, auxList, personIDIdx) {
    var filterDefID = this.fCompoundFilterDefID;
    var altFilterDefID = this.fAltFilterDefID;
    if (filterDefID == null) return;

    if (auxList == null || personIDIdx == null) return;

    var filterFieldIDDef = aClass.GetFieldDefByID(filterDefID);
    if (filterFieldIDDef == null) return;

    var altFieldIDDef = null;
    if (altFilterDefID != null) altFieldIDDef = aClass.GetFieldDefByID(altFilterDefID);

    var supervisorIDDef = aClass.GetFieldDefByID("SupervisorID");
    //   var isAuxDef = aClass.GetFieldDefByID("isAux");

    var n = auxList.length;
    for (var i = 0; i < n; i++) {
      var obj = auxList[i];
      var v = obj.GetFieldValue(supervisorIDDef);
      var parentObj = personIDIdx.FindObject(v);
      if (parentObj != null) {
        var f = null;

        if (altFieldIDDef) {
          // && isAuxDef && obj.GetFieldValue(isAuxDef) != "1")
          f = obj.GetFieldValue(altFieldIDDef);
          if (f == "") f = null;
        }

        if (f == null) f = parentObj.GetFieldValue(filterFieldIDDef);
        obj.SetFieldValue(filterFieldIDDef, f);
      }
    }
  }

  SetCompoundFilter(filterDefID, altFilterDefID) {
    if (filterDefID == "") filterDefID = null;
    this.fCompoundFilterDefID = filterDefID;

    if (altFilterDefID == "") altFilterDefID = null;
    this.fAltFilterDefID = altFilterDefID;
  }

  SetSubFilter(filterDef) {
    this.fComplexSubFilterDef = filterDef;
  }

  ClearForceTop() {
    this.fForceTopObj = null;
  }

  GetForceTop() {
    return this.fForceTopObj;
  }

  HandleTopBoxAndSelfRef(aClass, recordSupervisorDef, recordIdDef, topList) {
    this.ClearForceTop();
    if (recordSupervisorDef == null || recordIdDef == null) return;
    //var forceID = null;
    var n = aClass.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var obj = aClass.GetIndexObject(i);
      var v1 = obj.GetFieldValue(recordSupervisorDef);
      var v2 = obj.GetFieldValue(recordIdDef);

      if (v1 == v2) {
        obj.SetFieldValue(recordSupervisorDef, "selfref");
        obj.SetParent(null, -1);
      }

      if (topList && topList.indexOf(v2.toString()) != -1) {
        if (!obj.IsPersistent()) continue;
        if (this.GetForceTop() == null) {
          this.fForceTopObj = obj;
          //forceID = v2;
          obj.SetParent(null, -1);
        } else {
          //obj.SetFieldValue(supervisorIDDef, forceID);
          obj.SetParent(this.fForceTopObj, -1);
        }
      }
    }
  }

  HandleLevelRule(levelRule) {
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var n = aClass.GetObjectCount();
    var count = 0;
    var filterOut = [];

    for (var i = 0; i < n; i++) {
      var obj = aClass.GetIndexObject(i);
      var keepIt = levelRule.MatchRule(obj, this);

      if (!keepIt) filterOut.push(obj);
      else count++;
    }

    var m = filterOut.length;
    for (var i = 0; i < m; i++) {
      var obj = filterOut[i];
      obj.SetPersistent(false);
      obj.SetParent(null, -1);
    }

    return count;
  }

  WireHiringManager(personIDDef, supervisorIDDef) {
    if (personIDDef == null || supervisorIDDef == null) return;

    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var HIRING_MGR_EMAIL_Def = aClass.GetFieldDefByID("HIRING_MGR_EMAIL");
    var potentialEmailFields = ["Email", "email", "WorkEmail", "Email Address"];
    if (HIRING_MGR_EMAIL_Def == null) {
      HIRING_MGR_EMAIL_Def = aClass.GetFieldDefByID("Reports To Worker ID");
      potentialEmailFields = ["WorkerID"];
      if (HIRING_MGR_EMAIL_Def == null) return;
    }
    for (var aFieldIdx in potentialEmailFields) {
      var emailIDDef = aClass.GetFieldDefByID(potentialEmailFields[aFieldIdx]);
      if (emailIDDef != null) break;
    }
    /*
    var emailIDDef = aClass.GetFieldDefByID("Email"); // Bamboo
    if (emailIDDef == null)
    {
        emailIDDef = aClass.GetFieldDefByID("email"); // Namely
        if (emailIDDef == null)
            emailIDDef = aClass.GetFieldDefByID("WorkEmail"); // ADP
            if (emailIDDef == null)
                emailIDDef = aClass.GetFieldDefByID("Email Address"); // Ulti Pro
    }
    */
    if (emailIDDef == null) return;

    var emailIndex = new QIndex();
    emailIndex.SetForceLower(true);
    emailIndex.InitIndex(aClass, emailIDDef, true, false);

    var n = aClass.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var obj = aClass.GetIndexObject(i);
      var hiring_mgr_email = obj.GetFieldValue(HIRING_MGR_EMAIL_Def);
      if (hiring_mgr_email && hiring_mgr_email.length > 0) {
        hiring_mgr_email = hiring_mgr_email.toLowerCase();
        var matchObj = emailIndex.FindObject(hiring_mgr_email);
        if (matchObj) {
          var superVal = matchObj.GetFieldValue(personIDDef);
          if (superVal != null) obj.SetFieldValue(supervisorIDDef, superVal);
        }
      }
    }
  }

  CheckFilterChain(
    anObj,
    personIndex,
    personIDDef,
    supervisorIDDef,
    unfilterList,
    filterDef,
    topBoxList
  ) {
    var done = false;
    var encounteredIds = [];
    while (!done) {
      var v = anObj.GetFieldValue(supervisorIDDef);
      var v2 = anObj.GetFieldValue(personIDDef);
      if ((topBoxList != null && topBoxList.indexOf(v2) > -1) || encounteredIds.indexOf(v2) > -1)
        break;
      encounteredIds.push(v2);
      var parentObj = null;
      if (v == v2) parentObj = null;
      else parentObj = personIndex.FindObject(v);

      done = parentObj == null;
      if (!done) {
        if (!parentObj.IsPersistent()) {
          parentObj.SetPersistent(true);
          parentObj.SetFieldValue(filterDef, "1");
          unfilterList.push(parentObj);
        }
        anObj = parentObj;
      }
    }
  }

  CheckFilteredObjs(includeList, personIDIndex, personIDDef, supervisorIDDef, aClass, topBoxList) {
    var filterDef = new QObjDBFieldDef();
    filterDef.InitField("isFiltered", "isFiltered", aClass);
    aClass.AddField(filterDef);

    var unfilterList = [];
    var n = includeList.length;
    for (var i = 0; i < n; i++) {
      var anObj = includeList[i];
      anObj.SetFieldValue(filterDef, "0");
      this.CheckFilterChain(
        anObj,
        personIDIndex,
        personIDDef,
        supervisorIDDef,
        unfilterList,
        filterDef,
        topBoxList
      );
    }

    return unfilterList;
  }

  AddShortcuts(shortVal, anObj) {
    var shortCutMgr = this.GetShortcutMgr();
    shortCutMgr.AddShortcutByVal(shortVal, anObj);
  }

  HandleShortcut(shortcutFieldDef, anObj) {
    if (shortcutFieldDef == null) return;

    var shortVal = anObj.GetFieldValue(shortcutFieldDef);
    if (shortVal != null && shortVal.length > 0) this.AddShortcuts(shortVal, anObj);
  }

  SortShortcuts() {
    var shortCutMgr = this.GetShortcutMgr();
    shortCutMgr.SortShortcuts();
  }

  GetConnector(ds, dataObject) {
    var keys = Object.keys(dataObject);
    for (var i in keys) {
      if (dataObject[keys[i]] == ds) return keys[i];
    }
  }

  HandleAuxReporting(reqFields, aClass) {
    var isAuxDef = aClass.GetFieldDefByID("isAux");
    var auxList = null;
    for (var auxFieldIdx in reqFields) {
      if (auxFieldIdx.includes("AuxSuperID")) {
        var auxLabel = "0";
        var splitIdx = auxFieldIdx.split("-");
        if (splitIdx.length > 1) auxLabel = splitIdx[splitIdx.length - 1];
        if (!isAuxDef) {
          isAuxDef = new QObjDBFieldDef();
          isAuxDef.InitField("isAux", "isAux", aClass);
          aClass.AddField(isAuxDef);
        }
        var auxSuperDef = aClass.GetFieldDefByID(reqFields[auxFieldIdx]);
        if (!auxSuperDef) auxSuperDef = aClass.GetFieldDefByID(auxFieldIdx);
        if (auxSuperDef && !this.IsServerChart()) {
          // TODO: This check should exclude builder charts as well. A lot of unnecessary looping is being done.
          auxList = this.CreateAuxObjects(aClass, auxSuperDef, auxLabel);
        }
      }
    }
    return auxList;
  }

  CountActualRecords(recordList, isAuxDef, objTypeDef) {
    var boxCount = recordList.length;

    for (var recordIdx in recordList) {
      var aRecord = recordList[recordIdx];
      if (isAuxDef !== null) {
        var isAuxVal = aRecord.GetFieldValue(isAuxDef);
        if (isAuxVal !== 0 && isAuxVal !== null) boxCount -= 1;
      } else if (objTypeDef != null) {
        var objTypeVal = aRecord.GetFieldValue(objTypeDef);
        if (objTypeVal === "Function") boxCount -= 1;
      }
    }
    return boxCount;
  }

  ValidateReqFields(reqFields) {
    for (var aKey in reqFields) {
      var mappedField = reqFields[aKey];
      if (IsFieldNameReserved(mappedField)) {
        return appstr("configerrormsg");
      }
    }
    return "success";
  }

  HandleOverAccountLimit(numRecs) {
    // Skip box limit enforcement during export preview — the preview needs all
    // data to render accurately. The export modal's checkBuildLimit() separately
    // compares the real account limit against actual box count to gate the button.
    if (Globals._publishRef) return "success";

    var docMgr = this.GetDocMgr();
    var maxRecs = docMgr.fWSMgr.GetBoxCount();
    var maxAllowed = docMgr.fWSMgr.GetAllowedBoxCount();
    var tracker = docMgr.fWSMgr.GetUsageTracker();

    if (numRecs > maxAllowed) {
      var errorMsg =
        appstr("This chart exceeds your total box limit") + " (" + numRecs.toString() + "). ";
      errorMsg += appstr("loaderr6a") + " " + maxRecs + " " + appstr("loaderr6b");
      tracker.ReportEvent("global.box_limit_warning.viewed", {
        limit_type: "hard",
        count: numRecs
      });
      ReportToWebDriver("Fail - Over Box Limit");
      //if (doOverBoxLimit)
      return errorMsg;
    }

    //if not over the hard limit, but still over the soft limit, report it but let it go through
    if (numRecs > maxRecs + 10) {
      tracker.ReportEvent("global.box_limit_warning.viewed", {
        limit_type: "soft",
        count: numRecs
      });
    }

    //say nothing and let the chart load with the max number of boxes - users can then decide to trim down or upgrade
    return "success";
  }

  GetRecordsCount() {}

  WireHierarchy(reqFields, importFilter, hasHierarchy, singleSubchart, topBoxList) {
    var docMgr = this.GetDocMgr();
    const wsMgr = docMgr.fWSMgr;

    var iwizData = this.GetIWizData(enumIWizDataSet.refresh);
    var ds = iwizData.GetDataSource();
    var dataSource = this.GetConnector(ds, enumDataSource);
    var isPositions = this.IsPositionDocument();

    var msg = "success";
    var o = this.fObjDB;
    var boxClass = o.GetClassByName(enumObjClassNames.box);
    var hierarchyClass = this.GetHierarchyClass();
    var matchList = [];

    // User is selecting by name here - so name is correct as opposed to ID

    msg = this.ValidateReqFields(reqFields);
    this.AssignFieldIDs(reqFields);

    if (isPositions) {
      var recordIDKey = kPositionID;
      var recordSupervisorKey = kParentPositionID;
    } else {
      recordIDKey = kPersonID;
      recordSupervisorKey = kSupervisorID;
    }
    var recordIDDef = hierarchyClass.GetFieldDefByID(recordIDKey);
    var recordSupervisorDef = hierarchyClass.GetFieldDefByID(recordSupervisorKey);
    var shortCutDef = hierarchyClass.GetFieldDefByID("ShortCut");

    // not touching legacy auxes w/ a 10 ft pole
    var defaultAuxDef = boxClass.GetFieldDefByID("AuxSuperID");
    var defaultAux2Def = boxClass.GetFieldDefByID("AuxSuperID2");
    if (
      defaultAuxDef &&
      ds !== enumDataSource.chartbuilder &&
      !(isInViewer() && isInStandardSubmode())
    )
      reqFields["AuxSuperID"] = defaultAuxDef.GetFieldName();
    if (
      defaultAux2Def &&
      ds !== enumDataSource.chartbuilder &&
      !(isInViewer() && isInStandardSubmode())
    )
      reqFields["AuxSuperID2"] = defaultAux2Def.GetFieldName();

    if (this.IsServerChart() === false)
      //Backend wires the hierarchy here.
      this.WireHiringManager(recordIDDef, recordSupervisorDef);

    var auxList = null;
    if (dataSource !== "chartbuilder" && !this.IsServerChart() === false)
      // legacy (wb) aux
      auxList = this.HandleAuxReporting(reqFields, boxClass);

    var n = hierarchyClass.GetObjectCount();
    var recordIDIndex = null;
    if (recordIDDef) {
      recordIDIndex = new QIndex();
      recordIDIndex.InitIndex(hierarchyClass, recordIDDef, true, true);
    }

    this.SetAuxDivision(boxClass, auxList, recordIDIndex);

    var matchSubs = null;
    var chainLevels = 0;
    if (importFilter != null) {
      matchSubs = importFilter.GetMatchSubs();
      chainLevels = importFilter.GetChainLevels();
    }

    var doParentMatch = matchSubs !== enumMatchSubs.matchonly;
    var circRefErr = false;
    var isLevelRule = false;

    if (importFilter) isLevelRule = importFilter.IsLevelRule();
    // TODO: positions frontend import filters
    for (var i = 0; i < n; i++) {
      var pushIt = true;
      var obj = hierarchyClass.GetIndexObject(i);

      if (importFilter != null) {
        importFilter.fWSMgr = this.fDocMgr.fWSMgr;
        let matchesFilter;
        if (isPositions) {
          const [positionMatches, holderMatches] = importFilter
            .PositionsMatchRule(obj, this, undefined, true)
            .getConditions();

          matchesFilter = positionMatches;
          obj.SetHolderList(
            obj.GetHolderList().filter(holder => holderMatches[holder.GetObjectID()])
          );
        } else {
          matchesFilter = importFilter.MatchRule(obj, this, undefined, true);
        }
        pushIt = isLevelRule || matchesFilter;
        if (!pushIt && doParentMatch) {
          var parentObj = null;
          if (recordIDDef && recordSupervisorDef) {
            parentObj = this.ParentMatch(
              hierarchyClass,
              obj,
              recordIDIndex,
              recordIDDef,
              recordSupervisorDef,
              importFilter,
              matchSubs == enumMatchSubs.allsubs,
              topBoxList
            );
          }
          if (parentObj != null) {
            var subFilter = this.fComplexSubFilterDef;
            if (subFilter != null) pushIt = isLevelRule || subFilter.MatchRule(obj, this);
            else pushIt = true;
          }
          //pushIt = (parentObj != null);
        }
      }

      if (pushIt) {
        obj.SetPersistent(true);
        matchList.push(obj);
      } else obj.SetPersistent(false);
    }

    if (importFilter && importFilter.GetIncludeFiltered()) {
      var unfilterList = this.CheckFilteredObjs(
        matchList,
        recordIDIndex,
        recordIDDef,
        recordSupervisorDef,
        boxClass,
        topBoxList
      );
      matchList = matchList.concat(unfilterList);
    }

    if (this.IsServerChart() && false) {
      //TODO: Grab from the metadata - on load
      var numRecs = 100;
    } else {
      var numRecs = matchList.length;
    }

    if (auxList !== null) numRecs = numRecs - auxList.length;
    else {
      var objTypeDef = boxClass.GetFieldDefByID("objType");
      var isAuxDef = boxClass.GetFieldDefByID("isAux");
      if (isAuxDef || objTypeDef)
        numRecs = this.CountActualRecords(matchList, isAuxDef, objTypeDef);
    }

    msg = this.HandleOverAccountLimit(numRecs);
    if (msg !== "success") {
      wsMgr.SwitchToMasterChart(this.GetChartID());
      return msg;
    }

    if (hasHierarchy)
      this.HandleTopBoxAndSelfRef(hierarchyClass, recordSupervisorDef, recordIDDef, topBoxList);

    if (circRefErr) {
      this.ClearParents();
      msg = appstr("iwizerr2");
      return msg;
    }

    n = matchList.length;
    if (importFilter != null && singleSubchart) {
      if (n > kMaxRecSinglePage) {
        this.ClearParents();
        msg = appstr("iwizerr4");
        return msg;
      }
    }

    if (n == 0) {
      wsMgr.BroadcastTopic("noMatchingRecords", this);
      this.ClearParents();
      // Filter did not match any records.";
      msg = "noMatchingRecords";
      return msg;
    }

    if (hasHierarchy) {
      if (recordIDDef == null || recordSupervisorDef == null) {
        msg = appstr("iwizerr3");
        return msg;
      }
      var circResult = this.WireObjRefs(
        hierarchyClass,
        matchList,
        shortCutDef,
        recordSupervisorDef,
        recordIDIndex,
        recordIDDef
      );
      if (circResult != true) return circResult;
      this.SortShortcuts();

      // Chain of command
      if (chainLevels > 0) {
        var theRoot = this.GetLargestTree(aClass, false, false, null);
        var v = theRoot.GetFieldValue(recordSupervisorDef);
        var chainUp = recordIDIndex.FindObject(v);
        if (chainUp != null) {
          theRoot.SetParent(chainUp, -1);
          chainUp.SetPersistent(true);
        }
      }

      if (isLevelRule) {
        var recs = this.HandleLevelRule(importFilter);
        if (recs == 0) {
          this.ClearParents();
          msg = appstr("iwizerr1");
          return msg;
        }
      }
    }

    return msg;
  }

  WireObjRefs(aClass, records, shortCutDef, recordSupervisorDef, recordIdIndex, recordIDDef) {
    var rootCount = 0;
    var circRef = false;
    for (var i = 0; i < records.length; i++) {
      var obj = records[i];
      this.HandleShortcut(shortCutDef, obj);
      if (this.fForceTopObj == obj) {
        rootCount++;
        continue;
      }
      var v = obj.GetFieldValue(recordSupervisorDef);
      var parentObj = recordIdIndex.FindObject(v);
      if (parentObj != null && parentObj.IsPersistent()) {
        var detail = this.ChainOK(aClass, obj, recordIDDef, recordSupervisorDef, recordIdIndex);
        if (detail == null && parentObj.SmartGetFieldValue("Orphan") !== true) {
          obj.SetParent(parentObj, -1);
        } else circRef = true;

        if (circRef) {
          this.ClearParents();
          var msg = "keyerror^" + appstr("iwizerr2") + " " + detail;
          return msg;
        }
      } else {
        rootCount++;
      }
    }
    return true;
  }

  CountOrphans() {
    var orphanCount = 0;
    if (this.IsServerChart()) {
      var docmgr = this.GetDocMgr();
      var iWiz = docmgr.GetIWizDataRefresh();
      orphanCount = iWiz.GetOrphans();
    } else {
      var objDB = this.GetObjDB();
      var boxClass = objDB.GetClassByName("Box");

      var roots = boxClass.GetRootObjs();
      var n = roots.length;
      for (var i = 0; i < n; i++) {
        var curRoot = roots[i];
        var fieldDef = curRoot.GetFieldDefByID("Orphan");
        var v = curRoot.GetFieldValue(fieldDef);
        if (v == 1) orphanCount += 1;
      }
    }
    return orphanCount;
  }

  GetCurrentSubChartName() {
    var scm = this.GetSubChartMgr();
    return scm.GetCurrentSubChartName();
  }

  GetRootObj() {
    //TODO: Doesn't this make a bit of a weird assumption?
    var scm = this.GetSubChartMgr();
    var rootSubChart = scm.GetRootSubChart();
    if (rootSubChart == null) return null;
    var topObj = rootSubChart.GetTopDBObj();
    return topObj;
  }

  GetBoxObjByID(theID) {
    var objDB = this.GetObjDB();
    var aClass = objDB.GetClassByName("Box");
    var refObj = aClass.RetrieveObject(theID);

    return refObj;
  }

  GetLargestTree(objClass, tagOrphans, orphanRoot, forceTop) {
    var roots = objClass.GetRootObjs();
    var n = roots.length;
    var maxRoot;
    var maxCount = 0;
    if (forceTop == null) {
      if (n > 0) {
        maxRoot = roots[0];
        maxCount = maxRoot.CountSubs();
      }

      for (var i = 1; i < n; i++) {
        var r = roots[i];
        var c = r.CountSubs();
        if (c > maxCount) {
          maxCount = c;
          maxRoot = r;
        }
      }
    } else maxRoot = forceTop;

    if (tagOrphans) {
      var mRoot = maxRoot;
      if (orphanRoot) mRoot = null;
      for (var i = 0; i < n; i++) {
        var r = roots[i];
        if (r != mRoot) r.SetAsOrphan(true);
      }
    }

    return maxRoot;
  }

  //=================================================================================================
  // AutoText
  GetAutoText(autoTextType) {
    return this.fAutoTextMgr.GetAutoTextValue(autoTextType);
  }

  //=================================================================================================
  // Import Wizard + Data Refresh Support
  GetIWizData(whichset) {
    var docMgr = this.GetDocMgr();
    return docMgr.GetIWizDataInteractive();
    /*
	switch (whichset)
	{
		case enumIWizDataSet.refresh:
			return docMgr.GetIWizDataRefresh();
			break;
		case enumIWizDataSet.interactive:

			break;
        case enumIWizDataSet.workingrefresh:
			return this.fIWizDataWorkingRefresh;
			break;
        case enumIWizDataSet.workinginteractive:
            return this.fIWizDataWorkingInteractive;
            break;
	}

     */
  }

  SetIWizData(iWizData, iWizSet) {
    var docMgr = this.GetDocMgr();

    if (iWizData) iWizData = iWizData.Clone();

    switch (iWizSet) {
      case enumIWizDataSet.refresh:
        docMgr.SetIWizDataRefresh(iWizData);
        break;
      case enumIWizDataSet.interactive:
        docMgr.SetIWizDataInteractive(iWizData);
        break;
      case enumIWizDataSet.workingrefresh:
        this.fIWizDataWorkingRefresh = iWizData;
        break;
      case enumIWizDataSet.workinginteractive:
        this.fIWizDataWorkingInteractive = iWizData;
        break;
    }

    if (iWizData) iWizData.SetUpdateMode(iWizSet);
  }

  SetDataSourceParams(fileName, directory) {
    var iWizData = this.GetIWizData(enumIWizDataSet.refresh);
    iWizData.SetDataSourceParams(fileName, directory);
  }

  //=================================================================================================
  // Smart Refresh
  GetSCBreakTops() {
    var scm = this.GetSubChartMgr();
    return scm.GetSCBreakTops();
  }

  //=================================================================================================
  // View Mgr
  GetViewMgr() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetViewMgr();
  }

  //=================================================================================================
  // Filter Mgr
  GetFilterMgr() {
    var objDB = this.GetObjDB();
    var aClass = objDB.GetClassByName("Box");
    var filterMgr = aClass.GetFilterMgr();
    return filterMgr;
  }

  IsChartFiltered() {
    var filterMgr = this.GetFilterMgr();
    var iWizData = this.GetIWizData();
    var activeFilters = filterMgr?.HasActiveFilter(); // Interactive filters
    var appliedSavedFilters = filterMgr?.HasSavedFiltersApplied(); // Saved chart filters
    var hasCustomFilterApplied = iWizData.GetFilterData() && iWizData.GetFilterRule() != null; // Custom chart filters
    return (
      activeFilters === true || appliedSavedFilters === true || hasCustomFilterApplied === true
    );
  }

  IsChartHighlighted() {
    var highlightMgr = this.GetHighlightMgr();
    var hasAppliedSavedHighlights = highlightMgr?.HasSavedHighlightsApplied(); // Saved chart highlights
    var activeHighlights = highlightMgr?.HasActiveIH(); // Interactive highlights
    return activeHighlights === true || hasAppliedSavedHighlights === true;
  }

  //=================================================================================================
  // Filter Mgr
  GetHighlightMgr() {
    var objDB = this.GetObjDB();
    var aClass = objDB.GetClassByName("Box");
    var highlightMgr = aClass.GetHighlightMgr();
    return highlightMgr;
  }

  //=================================================================================================
  // Action Mgr
  GetMgrActionMgr() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetMgrActionsMgr();
  }

  //=================================================================================================
  // Profile Mgr
  GetProfileMgr() {
    var docMgr = this.GetDocMgr();
    return docMgr.GetProfileMgr();
  }

  SetUpDefaultProfile(result) {
    if (result == "success") {
      var profileMgr = this.GetProfileMgr();
      profileMgr.SetUpDefaultProfile(this);
    }
  }

  SetActiveTemplate(theTemplate) {
    var viewMgr = this.GetViewMgr();
    viewMgr.fInteractiveView = null;
    this.fActiveChartTemplate = theTemplate;
    this.GetIWizData().SetChartTemplateName(theTemplate.GetTemplateID());
  }

  GetActiveTemplate() {
    var viewMgr = this.GetViewMgr();
    if (viewMgr.fInteractiveView) return viewMgr.fInteractiveView;
    return this.fActiveChartTemplate;
  }

  GetDBObjTypeInfo() {
    var typeInfo = [];
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var n = boxClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var f = boxClass.GetIndexFieldDef(i);
      var fI = new QDBTypeInfo(f);
      typeInfo.push(fI);
    }

    return typeInfo;
  }

  GetRecordIDField() {
    var cobjDB = this.GetObjDB();
    var cboxClass = cobjDB.GetClassByName("Box");
    var scm = this.GetSubChartMgr();
    var cIDfieldDef = scm.GetRecordIDField(cboxClass);

    return cIDfieldDef;
  }

  GetHierarchyObjectType() {
    if (this.IsPositionDocument()) return enumObjectTypes.position;
    return enumObjectTypes.holder;
  }

  GetTitleFieldPosition() {
    var objDB = this.GetObjDB();
    var posClass = objDB.GetClassByName("Position");
    return posClass.GetTitleFieldDef();
  }

  GetTitleFieldEmployee() {
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    return boxClass.GetTitleFieldDef();
  }

  GetLockedIDField() {
    var cobjDB = this.GetObjDB();
    var cboxClass = cobjDB.GetClassByName("Box");
    var lockedFieldDef = cboxClass.GetFieldDefByID("Locked");

    return lockedFieldDef;
  }

  GetPersistField() {
    var cobjDB = this.GetObjDB();
    var cboxClass = cobjDB.GetClassByName("Box");
    var persistFieldDef = cboxClass.GetFieldDefByID("Persist");

    return persistFieldDef;
  }

  DoValueCopy(oldObj, newObj, oldDef, newDef) {
    var v = oldObj.GetFieldValue(oldDef);
    if (v) newObj.SetFieldValue(newDef, v);
  }

  OKtoCopyValue(
    fieldID // lockStatus)
  ) {
    var oktocopy;
    switch (fieldID) {
      case "PersonID":
        oktocopy = false;
        break;
      case "SupervisorID":
        oktocopy = false;
        break;
      //if (lockStatus == enumLockStatus.positionLocked || lockStatus == enumLockStatus.contentPosLocked)
      //    oktocopy = true;
      //else
      //    oktocopy = false;
      //break;
      default:
        oktocopy = true;
        break;
    }

    return oktocopy;
  }

  MoveLockedPosition(priorObj, curObj, curObjList, personIDDef) {
    // Get the NewSuperID for the priorObj
    var lockedSuperID = priorObj.NewSupervisorID();
    if (lockedSuperID == null) return;

    // Find the parentObj
    var n = curObjList.length;
    var found = false;
    var i = 0;
    var newParent = null;

    while (!found) {
      var o = curObjList[i];
      var curObjID = o.GetFieldValue(personIDDef);
      if (lockedSuperID == curObjID) {
        found = true;
        newParent = o;
      }

      i++;
      if (i >= n) found = true;
    }

    // If parentObj found set parent for curObj to parentObj
    if (newParent) curObj.SetParent(newParent, -1);
  }

  //var enumLockStatus = { notLocked : 0, contentLocked: 1, positionLocked: 2, contentPosLocked: 3};
  CopyObjFieldValues(priorObj, curObj, fieldDefPairs, lockStatus) {
    var numFields = fieldDefPairs.length;
    var contentLocked = ContentLocked(lockStatus);

    for (var j = 0; j < numFields; j++) {
      var pair = fieldDefPairs[j];
      var copyValue = pair.fUserAdded;
      if (copyValue) this.DoValueCopy(priorObj, curObj, pair.fPriorDef, pair.fNewDef);
      else {
        if (contentLocked) {
          var priorDef = pair.fPriorDef;
          var pFieldID = priorDef.GetFieldID();
          if (this.OKtoCopyValue(pFieldID))
            // lockStatus))
            this.DoValueCopy(priorObj, curObj, priorDef, pair.fNewDef);
        }
      }
    }
  }

  CopyFieldValuesFromDoc(oldDoc, objList, fieldDefPairs) {
    var cIDfieldDef = this.GetRecordIDField();
    var priorObjDB = oldDoc.GetObjDB();
    var pboxClass = priorObjDB.GetClassByName("Box");
    var ppersonIDIndex = new QIndex();
    var pscm = oldDoc.GetSubChartMgr();
    var priorIDfieldDef = pscm.GetRecordIDField(pboxClass);
    if (priorIDfieldDef == null) return;

    ppersonIDIndex.InitIndex(pboxClass, priorIDfieldDef, true, false);
    var priorLockDef = pboxClass.GetFieldDefByID("Locked");

    var cobjDB = this.GetObjDB();
    var cboxClass = cobjDB.GetClassByName("Box");
    var cPersonIDDef = cboxClass.GetFieldDefByID("PersonID");

    var missingObj = [];
    var n = objList.length;
    for (var i = 0; i < n; i++) {
      var o = objList[i];
      var curID = o.GetFieldValue(cIDfieldDef);
      var priormatchObj = ppersonIDIndex.FindObject(curID);
      if (priormatchObj) {
        var lockStatus = priormatchObj.GetLockStatus(priorLockDef);
        this.CopyObjFieldValues(priormatchObj, o, fieldDefPairs, lockStatus);

        if (PositionLocked(lockStatus))
          this.MoveLockedPosition(priormatchObj, o, objList, cPersonIDDef);
      } else missingObj.push(o);
    }

    return missingObj;
  }

  CopyFieldValues(fieldDefPairs, priorDoc) {
    var numFields = fieldDefPairs.length;
    if (numFields == 0) return null;

    var cIDfieldDef = this.GetRecordIDField();
    if (cIDfieldDef == null) return null;

    var cobjDB = this.GetObjDB();
    var cboxClass = cobjDB.GetClassByName("Box");

    // Make a list of all objects
    var objList = [];
    var n = cboxClass.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var o = cboxClass.GetIndexObject(i);
      objList.push(o);
    }

    var missingObjs = this.CopyFieldValuesFromDoc(priorDoc, objList, fieldDefPairs); //true);
    return missingObjs;
  }

  CopyCustomCalcs(priorDoc) {
    var typeInfoList = priorDoc.GetDBObjTypeInfo();
    var iWizData = priorDoc.GetDocMgr().GetIWizDataRefresh();
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var dataSource = iWizData.GetDataSource();
    var isBuilderChart = dataSource === enumDataSource.chartbuilder;
    var m = typeInfoList.length;
    for (var j = 0; j < m; j++) {
      var tI = typeInfoList[j];
      var calcCode = tI.fCalcCode;
      var fieldID = tI.fFieldID.toLowerCase();
      var isNotReserved = kReservedFieldNames.indexOf(fieldID) < 0;
      if (tI.fFormulaRule && calcCode === enumCalcCode.customcalc && isNotReserved) {
        var fieldDef = boxClass.GetFieldDefByID(fieldID);
        if (!isBuilderChart || (isBuilderChart && tI.fUserAdded)) {
          if (fieldDef == null) {
            var fieldDef = new QObjDBFieldDef();
            boxClass.AddField(fieldDef);
          }
          fieldDef.InitField(tI.fFieldID, tI.fFieldName, boxClass);
          fieldDef.SetCalcCode(enumCalcCode.customcalc);
          fieldDef.SetFormulaRule(tI.fFormulaRule);
          fieldDef.SetManualAdd(true);
        }
      }
    }
  }

  SetDBObjTypeInfo(typeInfoList, isMasterChartSource) {
    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var allFields = [];

    // Add Custom Calcs
    var m = typeInfoList.length;
    for (var j = 0; j < m; j++) {
      var tI = typeInfoList[j];
      if (tI.fFormulaRule) {
        /*
             var fieldDef1 = new QObjDBFieldDef();
             fieldDef1.InitField(tI.fFieldID,tI.fFieldName,boxClass);
             fieldDef1.SetCalcCode(enumCalcCode.customcalc);
             fieldDef1.SetFormulaRule(tI.fFormulaRule);
             boxClass.AddField(fieldDef1);
            */
      } else {
        var fieldID = tI.fFieldID;
        var fieldDef = boxClass.GetFieldDefByID(fieldID);
        var fieldDef2 = null;
        var contentLocked = false;

        if (fieldDef == null) {
          if (tI.fUserAdded) {
            fieldDef2 = new QObjDBFieldDef();
            fieldDef2.InitField(fieldID, tI.fFieldName, boxClass);
            fieldDef2.SetManualAdd(true);
            boxClass.AddField(fieldDef2);
          }
        } else {
          if (!fieldDef.FieldReadOnly()) fieldDef2 = fieldDef;
        }

        if (fieldDef2) {
          var pair = new QFieldDefPair(fieldDef2, tI.fPriorFieldDef, tI.fUserAdded);
          allFields.push(pair);
        }
      }
    }

    for (var i = 0; i < boxClass.GetFieldCount(); i++) {
      var fDef = boxClass.GetIndexFieldDef(i);
      var fID = fDef.GetFieldID();
      var typeInfo = FindTypeInfoObj(fID, typeInfoList);
      if (typeInfo) {
        var userAddedField = typeInfo.fUserAdded;
        // Set Field Types and formatting for Legacy WB Refreshes && user added fields with Master Sources
        if (!isMasterChartSource || (isMasterChartSource && userAddedField))
          typeInfo.CopyInfoToFieldDef(fDef);
      }
    }

    return allFields;
  }

  SetImportSortCriteria(
    sortConfig,
    iWizData //, importSort)
  ) {
    var docPrefs = this.GetDocPrefs();
    docPrefs.SetLastSort(sortConfig);
    iWizData.SetSortCriteria(sortConfig);
  }

  PerformSort(
    sortConfig,
    parentList //, importSort)
  ) {
    var docPrefs = this.GetDocPrefs();
    docPrefs.SetLastSort(sortConfig);

    var n = parentList.length;
    for (var i = 0; i < n; i++) {
      var aParent = parentList[i];
      aParent.SortChildrenOnKeys(sortConfig);
    }
    var scm = this.GetSubChartMgr();
    scm.HandleGroupedSort(parentList);
  }

  GetLastSort() {
    var docPrefs = this.GetDocPrefs();
    return docPrefs.GetLastSort();
  }

  HandleToday(variableName, includeTime) {
    var d = new Date();
    if (!includeTime) {
      d.setHours(0);
      d.setMinutes(0);
      d.setSeconds(0);
      d.setMilliseconds(0);
    }

    var modDate = 0;
    var parts = variableName.split("-");
    if (parts.length > 1) modDate = -1;
    else {
      parts = variableName.split("+");
      if (parts.length > 1) modDate = 1;
    }

    if (modDate != 0) {
      var days = parseInt(parts[1]) * modDate;
      d = addDays(d, days);
    }

    return d;
  }

  IsDateVariable(variableName) {
    var isdv = false;
    if (this.IsVariable(variableName)) isdv = variableName.indexOf("today") == 1;

    return isdv;
  }

  DoEvaluateVariable(variableName) {
    var resolved = false;

    switch (variableName) {
      case "division":
        var v1 = this.GetDivisionName();
        if (v1 != null) variableName = v1.toLowerCase();
        resolved = true;
        break;
      default:
        resolved = false;
    }

    if (!resolved) {
      if (variableName.indexOf("todayf") == 0) variableName = this.HandleToday(variableName, true);
      else if (variableName.indexOf("today") == 0)
        variableName = this.HandleToday(variableName, false);
    }

    return variableName;
  }

  GetSCNodeList() {
    var scm = this.GetSubChartMgr();
    return scm.GetSCNodeList();
  }

  UpdateLayoutForSCNodes(aLayout, applyToList, boxProps) {
    var scm = this.GetSubChartMgr();
    scm.UpdateLayoutForSCNodes(aLayout, applyToList, boxProps);
  }

  ResolveReference(reference, dbObj) {
    var docMgr = this.GetDocMgr();
    return docMgr.ResolveReference(reference, dbObj);
  }

  PausePicQueue() {
    this.fPictureMgr.PausePicQueue();
  }

  GetMissingPicList(missingImage64, w, h) {
    var aList = this.CheckPhotos(false, missingImage64, w, h);
    //var aList = this.fPictureMgr.MissingList(missingImage64,w,h);
    return aList;
  }

  ImageLoaded(pRef, redraw) {
    if (redraw) {
      var wsMgr = pRef.fWSMgr;
      wsMgr.PrepRenderWS();
      wsMgr.RenderWS();
    }
  }

  SubscriberImageLoaded() {
    // Nothing to do because we always ask the picturemgr for the image
  }

  CheckPhotos(loadPhotos, missingImage64, w, h) {
    var missingList = [];

    var objDB = this.GetObjDB(); //fObjDB;
    var aClass = objDB.GetClassByName("Box");
    var photoDef = aClass.GetFieldDefByID("Photo");
    if (photoDef == null) return missingList;

    var n = aClass.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var obj = aClass.GetIndexObject(i);
      var pDesc = GetPicSource(obj, photoDef);
      var pDir = pDesc.fDirectory;
      var pFile = pDesc.fFileName;
      var theImage = this.FindPicture(pDir, pFile);
      if (loadPhotos) {
        if (theImage == null) {
          var picLoading = this.PictureLoading(pDir, pFile, this);
          if (!picLoading) this.LoadPicture(pDir, pFile, this, null, null, false);
        }
      } else {
        if (theImage == null) missingList.push(obj);
        else {
          var missingImage = this.fPictureMgr.IsMissingImage(theImage, missingImage64, w, h);
          if (missingImage) missingList.push(obj);
        }
      }
    }

    return missingList;
  }

  LoadAllPhotos() {
    this.CheckPhotos(true, null, null, null);
  }

  AnyObjectLocked() {
    var objDB = this.GetObjDB();
    var aClass = objDB.GetClassByName("Box");

    return aClass.AnyObjectLocked();
  }

  AnyLayoutLocked() {
    var scm = this.GetSubChartMgr();
    return scm.AnyLayoutLocked();
  }

  SlimDocument() {
    var objDB = this.GetObjDB();
    objDB.PurgeData();

    var scm = this.GetSubChartMgr();
    scm.PurgeData();
  }

  ResetFilters() {
    var fm = this.GetFilterMgr();
    var activeSavedFilters = fm.DisableAppliedFilters();
    var activeInteractiveFilters = fm.DisableInteractiveFilters();
    this.ApplyChartFilters(false);

    return {
      activeInteractiveFilters,
      activeSavedFilters
    };
  }

  RestoreFilters({ activeSavedFilters, activeInteractiveFilters }) {
    var fm = this.GetFilterMgr();

    for (var i = 0; i < activeSavedFilters.length; i++) {
      var activeFilterName = activeSavedFilters[i];
      var aFilter = fm.GetNameFilter(activeFilterName);
      aFilter.fFilterOff = false;
    }

    for (const filterBlock of activeInteractiveFilters) {
      filterBlock.SetEnabled(true);
    }

    this.ApplyChartFilters(false);
  }

  DivMatch(doc, divSearch) {
    var divName = this.GetDivisionName();
    if (divName == null) return true; //Not a multi-chart
    var proceed = false;
    if (divSearch.length > 0) {
      divSearch = divSearch.toLowerCase();
      proceed = divName != null && divName.length > 0;
      if (proceed) {
        divName = divName.toLowerCase();
        proceed = divName.indexOf(divSearch) == 0;
      }
    }

    return proceed;
  }

  //======================================================================================================================
  // standard chart doc search
  MatchStdObj(searchStr, obj) {
    if (searchStr == null) return false;

    var objDB = this.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var nameField = GetNameDefForClass(boxClass);
    var titleField = boxClass.GetTitleFieldDef();
    var firstNameField = boxClass.GetFieldDefByID("First Name");
    var lastNameField = boxClass.GetFieldDefByID("Last Name");
    var preferredFirstNameField = boxClass.GetFieldDefByID("Preferred First");
    var preferredLastNameField = boxClass.GetFieldDefByID("Preferred Last");

    var gotMatch = false;

    if (lastNameField) {
      gotMatch = obj.MatchFieldValue(lastNameField, searchStr, true);
    }

    if (!gotMatch && firstNameField) {
      gotMatch = obj.MatchFieldValue(firstNameField, searchStr, true);
    }

    if (!gotMatch && preferredFirstNameField) {
      gotMatch = obj.MatchFieldValue(preferredFirstNameField, searchStr, true, null);
    }

    if (!gotMatch && preferredLastNameField) {
      gotMatch = obj.MatchFieldValue(preferredLastNameField, searchStr, true, null);
    }

    if (!gotMatch && nameField) {
      gotMatch = obj.MatchFieldValue(nameField, searchStr, true);
    }

    if (titleField && !gotMatch) {
      gotMatch = obj.MatchFieldValue(titleField, searchStr, false);
    }

    return gotMatch;
  }

  DoStdStaticChartSearch(searchStr, results, launchFull) {
    var objDB = this.GetObjDB();
    if (objDB == null) return;

    var boxClass = objDB.GetClassByName("Box");
    var objectList = boxClass.GetObjectList();
    var searchableObjects = objectList.filter(obj => obj.IsPersistent() && !obj.IsDeleted());

    // Check all non-filtered dbObjects
    for (var obj of searchableObjects) {
      // push obj matches to result array
      if (this.MatchStdObj(searchStr, obj) || (launchFull && searchStr == null)) {
        results.push(obj);
      }
    }
  }

  MatchPositionObj(searchStr, obj) {
    if (searchStr == null) return false;

    const objDB = this.GetObjDB();
    const posClass = objDB.GetClassByName("Position");
    const boxClass = objDB.GetClassByName("Box");

    const posIDField = posClass.GetRecordIDFieldDef();
    const posTitleField = posClass.GetTitleFieldDef();
    const nameField = GetNameDefForClass(boxClass);

    let gotMatch = false;

    if (posIDField) {
      gotMatch = obj.MatchFieldValue(posIDField, searchStr);
    }

    if (!gotMatch && posTitleField) {
      gotMatch = obj.MatchFieldValue(posTitleField, searchStr);
    }

    if (!gotMatch && nameField) {
      gotMatch = obj.GetHolderList().some(holder => holder.MatchFieldValue(nameField, searchStr));
    }

    return gotMatch;
  }

  DoStdStaticPositionSearch(searchStr, results) {
    var objDB = this.GetObjDB();
    if (objDB == null) return;

    var posClass = objDB.GetClassByName("Position");
    var objectList = posClass.GetObjectList();
    var searchableObjects = objectList.filter(obj => obj.IsPersistent() && !obj.IsDeleted());

    // Check all non-filtered dbObjects
    for (var obj of searchableObjects) {
      // push obj matches to result array
      if (this.MatchPositionObj(searchStr, obj)) {
        results.push(obj);
      }
    }
  }

  //======================================================================================================================
  // advanced chart doc search
  DoAdvStaticChartSearch(criteria, results) {
    var objDB = this.GetObjDB();
    if (objDB == null) return;

    var divBlank = !criteria["Division"] || criteria["Division"] === "";
    var divMatch = false;
    if (!divBlank) {
      divMatch = this.DivMatch(criteria["Division"]);
      if (!divMatch) return;
    }

    var boxClass = objDB.GetClassByName("Box");
    var nameFD = boxClass.GetFieldDefByID("Name");
    var lastNameFD = boxClass.GetFieldDefByID("Last Name");

    var objectList = boxClass.GetObjectList();
    var searchableObjects = objectList.filter(obj => obj.IsPersistent() && !obj.IsFiltered());

    // Check all non-filtered dbObjects
    for (var obj of searchableObjects) {
      // Checks for matches in all field criteria before pushing to results list
      var gotMatch = true;
      if (Object.keys(criteria).length > 0) {
        for (var aFieldIdx in criteria) {
          //Treated as an AND search.
          var theSearch = criteria[aFieldIdx];
          var doStartsWith = aFieldIdx.indexOf("Name") > -1;
          var splitchar = aFieldIdx === "Name" && lastNameFD == null ? " " : null;
          var theFieldDef = boxClass.GetFieldDefByID(aFieldIdx);
          if (!theFieldDef) {
            theFieldDef = boxClass.GetFieldDefByName(aFieldIdx);
          }

          if (!theFieldDef && (criteria["First Name"] || criteria["Last Name"])) {
            theFieldDef = nameFD;
          }

          gotMatch = obj.MatchFieldValue(theFieldDef, theSearch, doStartsWith, {
            splitchar,
            strictEmpty: true
          });

          if (!gotMatch) {
            break;
          }
        }
      }

      if (gotMatch) {
        results.push(obj);
      }
    }
  }

  // Excel Export Directory
  DoExportDir(colNames, searchResults, filename) {
    let wsMgr = this.GetDocMgr().fWSMgr;
    if (searchResults.length < 1 || !colNames || wsMgr.fPendingExcelExport) return;
    let boxClass = this.GetObjDB().GetClassByName("Box");

    let fieldDefList = colNames.map(function (colName) {
      var f = boxClass.GetFieldDefByName(colName);
      if (!f) f = boxClass.GetFieldDefByID(colName);
      return f;
    });

    let validFieldDefs = fieldDefList?.filter(f => f != null);
    if (!validFieldDefs || validFieldDefs.length < 1) return;

    var headers = validFieldDefs.map(function (fieldDef) {
      return fieldDef.GetFieldName();
    });

    var labels = validFieldDefs.map(function (fieldDef) {
      return fieldDef.GetFieldLabel();
    });

    var jsonData = {
      headers: headers,
      headerLabels: labels,
      filename: filename
    };
    if (this.IsServerChart() || isInReporting() || isInDirectory()) {
      jsonData["rows"] = searchResults;
    } else {
      jsonData["rows"] = searchResults.map(function (aRow) {
        return aRow.JSON(headers);
      });
    }
    var fieldData = [];
    for (var i = 0; i < jsonData["headers"].length; i++) {
      var theFieldData = jsonData["headers"][i];
      fieldData.push({ name: theFieldData, type: 0 });
    }
    jsonData["datatypes"] = fieldData;
    jsonData["sheetname"] = this.GetSubChartMgr().GetActiveSubChartName(); //TOOD: Shouldn't this be changed to what you searched on? Or the filename?
    return wsMgr.UploadExcelDataJSON(jsonData);
  }

  CanCurrentUserEdit() {
    const wsMgr = this.GetDocMgr().fWSMgr;

    if (this.IsPlanChart()) {
      const planMgr = wsMgr.GetPlanMgr();
      return planMgr.CanUpdatePlan();
    }

    const userInfo = wsMgr.GetUserInfo();
    const userRole = userInfo?.GetUserRole();

    return userRole === kAdminUser || userRole === kRWUser;
  }

  IsWritable() {
    if (this.IsPlanChart()) {
      const wsMgr = this.GetDocMgr().fWSMgr;
      const planMgr = wsMgr.GetPlanMgr();
      const planStatus = planMgr?.GetPlanStatus();
      return planStatus === "Active";
    }

    if (this.IsServerChart()) {
      return false;
    }

    return !isInViewer();
  }

  IsIndexPageActiveSC() {
    var scm = this.GetSubChartMgr();
    var curSC = scm.GetCurrentSubChart();
    return curSC.GetSubChartParentID() === kIndexSCID;
  }

  GetPlaceHolderNames(objDB, fieldDef, aClass, fieldDef2) {
    var objClass = aClass;
    var objMgr = objClass.GetObjMgr();
    var objList = objMgr.GetObjectList();
    if (fieldDef == null) return;
    var n = objList.length;
    this.fPlaceHolders = [];
    var placeHolderValue = null;

    var topIDList = [];

    var iWizData = this.GetDocMgr().fWSMgr.fDataRefreshInfo;
    if (iWizData) {
      var coheads = iWizData.GetTopBoxIDs();

      if (coheads) topIDList = coheads.split(",");
    }
    var theRoot = this.GetLargestTree(objClass, false, false, null);
    for (var i = 0; i < n; i++) {
      var isTeamParent = true;
      placeHolderValue = objList[i].GetFieldValue(fieldDef);
      if (placeHolderValue == "") continue;

      var anObj = objList[i];
      var parentObj = anObj.GetParent();
      if (parentObj != null || anObj == theRoot) {
        isTeamParent = FindMatchingParent(parentObj, [fieldDef], placeHolderValue);
      }
      if (!isTeamParent && topIDList.indexOf(anObj.GetObjectRecordID()) == -1) {
        var thisPlaceHolder = new QPlaceHolder();
        thisPlaceHolder.initPlaceHolder(this, placeHolderValue, anObj, parentObj);
      }
    }

    if (fieldDef2 != null) this.HandlePlaceHolderOrphans(objList, objClass, objMgr, fieldDef2);
    this.CreatePlaceHolders(objMgr, objClass);

    return;
  }

  HandlePlaceHolderOrphans(objList, objClass, objMgr, fieldDef2) {
    var n = objList.length;
    var jobFunction = null;

    var theRoot = this.GetLargestTree(objClass, false, false, null);

    for (var i = 0; i < n; i++) {
      var anObj = objList[i];
      if (anObj.IsPersistent()) {
        var parentObj = anObj.GetParent();
        if (parentObj == null && anObj != theRoot) {
          jobFunction = anObj.GetFieldValue(fieldDef2);
          //var parentObj = this.GetRootObj();
          //isTeamParent = FindMatchingParent(parentObj, fieldDef2, jobFunction);
          //if (!isTeamParent) {
          var thisPlaceHolder = new QPlaceHolder();
          thisPlaceHolder.initPlaceHolder(this, jobFunction, anObj, null);
          thisPlaceHolder.SetReportToRoot(true);
        }
      }
    }
  }

  FindFieldDef(fieldList, fieldID) {
    var n = fieldList.length;
    for (var i = 0; i < n; i++) {
      var fieldDef = fieldList[i];
      var fieldID2 = fieldDef.GetFieldID();
      if (fieldID == fieldID2) return fieldDef;
    }

    return null;
  }

  // DS11262018 - Rework to pass in 3 parameters instead of nastiness below.
  HandlePlaceHolderChart(pHoldField1, pHoldField2) {
    var o = this.fObjDB;
    var aClass = o.GetClassByName("Box");
    var pHolderField = aClass.GetFieldDefByName(pHoldField1);
    if (pHoldField2 != null) var pHolderField2 = aClass.GetFieldDefByName(pHoldField2);
    else var pHolderField2 = null;
    this.GetPlaceHolderNames(o, pHolderField, aClass, pHolderField2);
    return;
  }

  CreatePlaceHolders(objMgr, objClass) {
    var isPlaceHolder = new QObjDBFieldDef();
    isPlaceHolder.InitField("isPlaceHolder", "isPlaceHolder", objClass);
    objClass.AddField(isPlaceHolder);
    var namefdef = objClass.GetFieldDefByID("Name");
    var idFDef = objClass.GetRecordIDFieldDef();
    var titleDef = objClass.GetTitleFieldDef();
    var supportingGroups = [];
    var rootBox = null;
    var m = this.fPlaceHolders.length;
    for (var i = 0; i < m; i++) {
      var newPHolder = this.fPlaceHolders[i];
      var newObj = newPHolder.fChildren[0].Clone();

      var childPID = newObj.GetFieldValue(idFDef);
      var reportToRoot = newPHolder.GetReportToRoot();
      var newParent = newPHolder.fChildren[0].GetParent();
      if (newParent == null && !reportToRoot) rootBox = newObj;
      if (reportToRoot) supportingGroups.push(newObj);
      newObj.SetParent(newParent);
      var childLen = newPHolder.fChildren.length;
      for (var j = 0; j < childLen; j++) newPHolder.fChildren[j].SetParent(newObj);
      newObj.SetFieldValue(namefdef, newPHolder.fPlaceHolderName);
      newObj.SetFieldValue(idFDef, childPID + newPHolder.fPlaceHolderName);
      newObj.SetFieldValue(titleDef, "");
      newObj.AddNewFieldValue(isPlaceHolder, "1");
    }
    //return;
    var n = supportingGroups.length;
    for (var i = 0; i < n; i++) {
      var sg = supportingGroups[i];
      sg.SetParent(rootBox, -1);
    }
  }

  HandleOptions(chartData, wsMgr) {
    var chartType = chartData["chartType"];
    if (chartType != null) {
      var plm = this.GetPageLayoutMgr();
      plm.SetChartType(chartType);
      if (chartType == enumChartTypes.level && chartData["levelField"]) {
        plm.SetLevelField(chartData["levelField"]);
      }
      plm.fSaveChartType = chartType;
    }

    this.LoadCommonChartMetadata(chartData);

    wsMgr.UseURLScaleFactor();
  }

  /**
   *
   * @returns {string | null}
   */
  GetChartID() {
    var chartID = null;
    var docMgr = this.fDocMgr;
    var iWiz = docMgr.GetIWizDataInteractive();
    if (iWiz) chartID = iWiz.GetBaseChartID();
    return chartID;
  }

  GetLoadedLevels() {
    var scm = this.GetSubChartMgr();
    if (!scm) return null;
    return scm.GetLoadedLevels();
  }

  GetLoadedChainLevels() {
    var scm = this.GetSubChartMgr();
    if (!scm) return null;
    return scm.GetLoadedChainLevels();
  }

  GetRequestedChainLevels() {
    return this.fRequestedChainLevels || 0;
  }

  SetRequestedChainLevels(requestedLevels) {
    this.fRequestedChainLevels = requestedLevels;
  }

  GetRequestedLevels() {
    if (!this.IsServerChart() && this.GetIWizData()) {
      return this.GetIWizData().fChartLevels;
    }

    return this.fRequestedLevels || 2; //TODO: Should be global. Should this also be at the docmgr level?
  }

  SetRequestedLevels(requestedLevels) {
    this.fRequestedLevels = requestedLevels;
  }
}
